import { pricingTranslations } from "./pricing-i18n";
import { validationTranslations } from "./validation-i18n";
import { completionTranslations } from "./completion-i18n";
import { naturalnessTranslations } from "./naturalness-i18n";
import { experienceModeTranslations } from "./experience-mode-i18n";
import { handoffTranslations } from "./handoff-i18n";
import { privacyOverlayTranslations } from "./privacy-i18n";
import { marketDataErrorTranslations } from "./market-data-error-i18n";
import { locales, seoLocaleContent, type Locale } from "./homepage-seo";

export { locales, seoLocaleContent };
export type { Locale };

export type TranslationKey = keyof typeof en;
type LocaleStrings = Partial<Record<TranslationKey, string>>;

const en = {
  ...marketDataErrorTranslations.en,
  ...experienceModeTranslations.en,
  ...handoffTranslations.en,
  ...privacyOverlayTranslations.en,
  account: "Account",
  accountSyncHint: "Sign in to load quota, cloud history, and AI credits.",
  accountSignInUnavailable: "Account sign-in unavailable",
  accountSignInUnavailableHint: "Backtests can still run. Account sync is temporarily unavailable.",
  aiCredits: "AI credits",
  creditsAvailable: "available",
  api: "API",
  appSubtitle: "Strategy tester",
  downloadOnAppStore: "Download on the App Store",
  backtest: "Backtest",
  backtestComplete: "Backtest complete",
  backtestFailed: "Backtest failed",
  backtests: "Backtests",
  basicBacktestHint: "Backtests can still run. Account sync is temporarily unavailable.",
  applyResult: "Apply result",
  capital: "Capital",
  cloudHistory: "Cloud history",
  commission: "Commission",
  botLoginHint: "Sign in to create and monitor realtime paper bots.",
  bots: "Bots",
  createBot: "Create bot",
  cryptoLab: "Trading strategy platform",
  delete: "Delete",
  drawdown: "Drawdown",
  emptyBots: "No realtime bots yet.",
  emptyHistory: "Login to sync saved setups from the iOS app.",
  emptyResultsTitle: "No backtest result yet",
  emptyResults: "Run a backtest to review net profit, win rate, drawdown, equity curve, and every closed trade.",
  endDate: "End date",
  entry: "Entry",
  exit: "Exit",
  firebaseNotSet: "Account service unavailable",
  guestMode: "Guest mode",
  home: "Home",
  history: "History",
  language: "Language",
  learn: "Learn",
  market: "Market",
  manualPrompt: "Manual prompt",
  maxRange: "Max range",
  methodology: "Methodology",
  mode: "Mode",
  name: "Name",
  email: "Email",
  password: "Password",
  netProfit: "Net profit",
  noBacktest: "No backtest yet",
  noClosedTrades: "No closed trades returned for this setup.",
  noRealTrades: "No real-money trades",
  period: "Period",
  periodAdjusted: "Period adjusted for selected candle timeframe",
  periodClampedByPlan: "Range adjusted to fit your plan",
  planValueBody: "Paid plans offer longer test ranges, more saved history, and more paper signal bots.",
  planValueTitle: "Plan value",
  loadingBacktestTitle: "Running market simulation",
  loadingBacktestData: "Loading candles",
  loadingBacktestEngine: "Testing strategy rules",
  loadingBacktestReport: "Building chart and trade report",
  loadingAnalysisTitle: "Reviewing trade behavior",
  loadingAnalysisData: "Reading closed trades",
  loadingAnalysisEngine: "Finding weak spots",
  loadingAnalysisReport: "Preparing improved prompt",
  estimatedWait: "Estimated wait",
  position: "Position",
  executionModel: "Trading model",
  spotCash: "Spot / Cash",
  futuresPerpetual: "Futures / Perpetual",
  spotPositionHint: "Allocation is the position notional as a percentage of account equity.",
  futuresPositionHint: "Allocation is margin; position value equals margin multiplied by leverage.",
  capitalAllocation: "Capital allocation",
  marginAllocation: "Margin allocation",
  stopLossPrice: "Stop loss (price)",
  takeProfitPrice: "Take profit (price)",
  positionPreview: "Estimated position",
  capitalUsed: "Capital used",
  marginUsed: "Initial margin",
  positionValue: "Position value",
  estimatedLossAtStop: "Loss at stop before costs",
  estimatedProfitAtTarget: "Profit at target before costs",
  leverage: "Leverage",
  marginMode: "Margin mode",
  isolatedMargin: "Isolated",
  crossMargin: "Cross",
  isolatedMarginHelp: "Only the margin assigned to this position supports it; the remaining simulated balance is not used to delay liquidation.",
  crossMarginHelp: "The available simulated account balance supports the position, which can move liquidation farther away but exposes more of the balance.",
  maintenanceMargin: "Maintenance margin",
  fundingRate: "Funding / interval",
  fundingInterval: "Funding interval",
  liquidationFee: "Liquidation fee",
  liquidation: "Liquidation",
  quantity: "Quantity",
  roe: "Return on margin",
  fundingPaid: "Funding paid",
  privacy: "Privacy",
  privacyClose: "Close",
  privacyIntro: "Backtest helps users backtest strategies and monitor paper trading signals. The app is for education and research, not financial advice.",
  privacyDataTitle: "Data we process",
  privacyDataBody: "We may process account identifiers, email address, subscription status, quota usage, backtest setup, saved history, bot configuration, device tokens for push notifications, and technical logs needed to operate the service.",
  privacyUseTitle: "How we use data",
  privacyUseBody: "We use this data to authenticate accounts, enforce quotas, sync history, run backtests, send requested signal notifications, improve reliability, and prevent abuse.",
  privacyThirdTitle: "Third-party services",
  privacyThirdBody: "The service can use Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance market data, and OpenAI or Gemini when AI features are enabled.",
  privacyRetentionTitle: "Retention and control",
  privacyRetentionBody: "Backtest setups and account usage are kept while the account is active or as needed for service operations. Users can contact support to request deletion of account-linked data.",
  privacyTermsTitle: "Terms of Use",
  privacyTermsBody: "Subscriptions are purchased in the iOS app and governed by Apple's standard Terms of Use. Apple confirms the final localized price and renewal terms before purchase.",
  privacyTermsLink: "Apple Terms of Use (EULA)",
  webSubscriptionTerms: "Web Subscription Terms",
  privacyRiskTitle: "Trading risk",
  privacyRiskBody: "Backtests are historical simulations and do not guarantee future performance. Backtest does not place real trades and does not provide investment advice.",
  privacyUpdated: "Last updated: July 16, 2026",
  riskNoticeTitle: "Risk Notice",
  riskNoticeSubtitle: "Backtest is a strategy research tool, not investment advice.",
  riskNoticeCapitalTitle: "You can lose capital",
  riskNoticeCapitalBody: "Trading crypto and financial assets is high risk. You may lose part or all of your capital.",
  riskNoticeBacktestTitle: "Backtests do not guarantee future results",
  riskNoticeBacktestBody: "Historical results, including profitable ones, do not guarantee performance in live market conditions.",
  riskNoticeAiTitle: "AI can be wrong",
  riskNoticeAiBody: "AI can turn a text description into strategy rules. Check the parsed rules before running a backtest; they may be wrong.",
  riskNoticeBotTitle: "Bots are paper/signal tools",
  riskNoticeBotBody: "Realtime bots currently track signals and simulated history; they do not guarantee accurate entries or exits.",
  riskNoticeConfirmation: "I understand these risks and agree to use Backtest only as a research tool.",
  riskNoticeContinue: "I Understand and Continue",
  promptPreview: "Rule parser prompt",
  quotaHistory: "History",
  ready: "Ready",
  results: "Results",
  returnMetric: "Return",
  riskDetails: "SL / TP",
  runBacktest: "Run backtest",
  running: "Running...",
  savedSetups: "saved setups",
  settings: "Settings",
  setup: "Setup",
  signIn: "Sign in",
  signUp: "Sign up",
  createAccount: "Create account",
  signOut: "Sign out",
  signedIn: "Signed in",
  slippage: "Slippage",
  sample: "Sample",
  startDate: "Start date",
  stopLoss: "Stop loss",
  strategy: "Strategy",
  strategyConfidence: "In-sample backtest quality",
  qualityNotForecast: "Descriptive evidence, not a forecast",
  noLockedHoldout: "No locked holdout",
  noLockedHoldoutBody: "This score describes the sample shown. The strategy has not been verified on a locked out-of-sample period.",
  researchEvidence: "Research evidence",
  executionAssumptions: "Execution assumptions",
  executionNextOpen: "Signals are read at bar close; market entries fill at the next bar open.",
  executionStopFirst: "If stop and target touch in one bar, the stop wins. Entries without buying power are rejected.",
  dataProvenance: "Data provenance",
  closedCandlesOnly: "Closed candles only",
  candleCloseUnknown: "Candle close status unavailable",
  dataGaps: "Candle gaps",
  missingBars: "estimated missing bars",
  noUnexpectedGaps: "No unexpected continuous-market gaps",
  robustness: "Robustness",
  strategyReturn: "Strategy return",
  benchmarkReturn: "Buy & hold return",
  drawdownDuration: "Drawdown duration",
  costStress: "Execution-cost stress",
  rejectedOrders: "Rejected entries",
  rejectedOrdersHint: "Entry attempts rejected by buying-power checks are excluded from completed trades.",
  attemptedNotional: "Attempted notional",
  buyingPower: "Buying power",
  insufficientBuyingPower: "Insufficient buying power",
  insufficientRiskSample: "Too few return observations for reliable annualized risk ratios.",
  dataSnapshot: "Data snapshot",
  bars: "bars",
  notAvailable: "Not available",
  costDoubledFragile: "Profit disappears when execution costs are doubled.",
  continuousMarketGaps: "Continuous-market candle gaps were detected.",
  confidenceHigh: "High",
  confidenceMedium: "Medium",
  confidenceLow: "Low",
  confidenceLargeTradeSample: "Large trade sample",
  confidenceModerateTradeSample: "Moderate trade sample",
  confidenceSmallTradeSample: "Small but usable trade sample",
  confidenceVerySmallTradeSample: "Very small trade sample",
  confidenceWideCandleSample: "Wide candle sample",
  confidenceShortCandleHistory: "Short candle history",
  confidenceNoGrossLoss: "No gross losing trades in this sample",
  confidenceNoClosedTrades: "No closed trades",
  confidenceStrongProfitFactor: "Strong profit factor",
  confidencePositiveProfitFactor: "Positive profit factor",
  confidenceWeakProfitFactor: "Profit factor below 1.0",
  confidencePositiveNetProfit: "Positive net profit after costs",
  confidenceNegativeNetProfit: "Negative net profit after costs",
  confidenceHighWinRate: "Win rate above 60%",
  confidenceLowWinRate: "Win rate below 40%",
  confidenceControlledDrawdown: "Controlled drawdown",
  confidenceHighDrawdown: "High drawdown",
  confidenceModerateDrawdown: "Moderate drawdown",
  confidenceConcentratedProfit: "Profit depends heavily on one large winner",
  strategyBollinger: "Bollinger",
  strategyBreakout: "Breakout",
  strategyMACD: "MACD",
  strategyMACross: "MA Cross",
  strategyPullback: "Pullback",
  strategyRSI: "RSI",
  strategyVisualBuilder: "Visual Strategy Builder",
  strategyCustomDSL: "Custom DSL Strategy",
  takeProfit: "Take profit",
  timeframe: "Timeframe",
  tick: "Tick",
  refresh: "Refresh",
  trades: "Trades",
  tradeTablePnL: "P&L",
  improve: "Improve",
  improveLoginHint: "Sign in to analyze wins/losses and generate an improved strategy.",
  improvedPrompt: "Improved prompt",
  applyImprove: "Apply improve",
  aiAnalysisReady: "AI analysis ready.",
  aiAnalysisFailed: "AI analysis failed.",
  improvedPromptApplied: "Improved prompt applied. Run backtest again to compare.",
  runLockedHoldout: "Run final holdout check",
  runningLockedHoldout: "Checking unseen data...",
  lockedHoldoutHint: "An improved candidate can only be applied after it passes the one-time unseen-data check.",
  validationApplyBlocked: "Apply locked until validated",
  validationPending: "The candidate is waiting for its final holdout check.",
  validationPassed: "Candidate validated. You can apply it and run a new backtest.",
  validationRejected: "The candidate was not validated. Keep the original strategy.",
  lockedHoldoutFailed: "The final holdout check could not be completed. Please try again.",
  loadedResultFromHistory: "Loaded result from history.",
  loadedPresetFromGuide: "Loaded setup from guide",
  signInCreateBots: "Sign in to create realtime bots.",
  loadedLatestBotResult: "Loaded latest bot result.",
  symbol: "Symbol",
  entryToExit: "Entry to exit",
  selectedTrade: "Selected trade",
  side: "Side",
  aiReview: "AI rule parsing",
  learnStrategies: "Learn strategies",
  openWebApp: "Open Web App",
  paperAlerts: "Paper alerts",
  readLearningCenter: "Read Learning Center",
  riskFirst: "Risk first",
  runSampleBacktest: "Run a backtest",
  runFreeBacktest: "Run a free backtest",
  resultEmptyStep1: "Choose a market, timeframe, and strategy.",
  resultEmptyStep2: "Run the backtest with fees, slippage, stop loss, and take profit.",
  resultEmptyStep3: "Review the chart, costs, drawdown, and trades. Save the result to compare your next test.",
  aiImproveCta: "Analyze and improve this strategy",
  aiImproveHint: "AI reviews win rate, stop-loss behavior, exits, and trade clusters, then suggests a safer prompt to retest.",
  trialPromptBody: "Paid plans offer longer test ranges, more saved history, and more paper signal bots.",
  currentPlanCta: "Your current plan controls range, AI credits, saved history, and paper bot limits.",
  shareCopied: "Share summary copied.",
  shareFailed: "Could not share this result.",
  shareReady: "Share sheet opened.",
  shareResult: "Share result",
  shareDialogTitle: "Choose what to share",
  shareEvidenceOnly: "Evidence summary only",
  shareEvidenceOnlyHint: "Shares metrics and a normalized curve. Strategy rules stay private.",
  shareReusableStrategy: "Reusable strategy",
  shareReusableStrategyHint: "Explicitly publishes the canonical rules and assumptions so others can edit and rerun them.",
  shareReusableUnavailable: "This result has no canonical rules that can be safely reproduced.",
  cancel: "Cancel",
  strategyProof: "Proof before risk",
  heroProofTitle: "See the chart, trades, and risk in one place.",
  heroProofBody: "Test a rule on historical candles, inspect entries and exits, then save a baseline and change one condition to compare.",
  buy: "Buy",
  sell: "Sell",
  long: "Long",
  short: "Short",
  outcome: "Outcome",
  winner: "Win",
  loser: "Loss",
  duration: "Duration",
  fees: "Fees",
  direction: "Direction",
  both: "Both",
  exitMode: "Exit mode",
  tpSlOnly: "TP/SL only",
  signalAndRisk: "Signal + risk",
  signalOnly: "Signal only",
  aiParser: "AI Parser",
  aiParserHint: "Manual mode with login uses AI credits.",
  chartEma20: "EMA 20",
  chartEma50: "EMA 50",
  chartBollinger: "Bollinger",
  chartVolume: "Volume",
  chartTrades: "Trades",
  winRate: "Win rate",
  strategyEngulfing: "Engulfing",
  strategyPinBar: "Pin Bar",
  strategyInsideBar: "Inside Bar",
  strategySweep: "Sweep",
  strategyTrendPlus: "Trend+",
  strategyVolBreak: "Vol Break",
  strategyMeanPlus: "Mean+",
  strategyMTF: "MTF",
  setupLoadedFromHistory: "Setup loaded from history.",
  viewHistory: "View history",
  realtimeBotCreated: "Realtime bot created.",
  createBotFailed: "Create bot failed.",
  botTickComplete: "Bot tick complete.",
  tickBotFailed: "Tick bot failed.",
  realtimeBotDeleted: "Realtime bot deleted.",
  deleteBotFailed: "Delete bot failed.",
  accountCreated: "Account created.",
  emailAuthFailed: "Email authentication failed.",
  emailAlreadyInUse: "An account with this email already exists.",
  invalidCredentials: "Email or password is incorrect.",
  passwordTooWeak: "Password must be at least 8 characters.",
  signInUnavailable: "Sign-in is temporarily unavailable. Please try again later.",
  chartIndicators: "Chart indicators",
  selectTrade: "Select trade",
  heroTitle: "Backtest before you trade.",
  pricing: "Pricing",
  pricingSeoTitle: "Backtest Plans & Pricing | Strategy Backtesting",
  pricingSeoDescription: "Compare Guest, Free, Pro, and Power limits for backtests, saved history, and paper bots.",
  pricingEyebrow: "App Store plans",
  pricingTitle: "Choose the research capacity you need.",
  pricingBody: "Compare plan prices here, then continue to the iOS app to purchase securely through Apple.",
  guestAccessTitle: "Trying it without an account?",
  guestAccessBody: "A free account adds cloud history, saved strategies, and paper bots. Limits follow the current service configuration.",
  freePlan: "Free",
  freePrice: "0",
  freeForever: "No subscription required",
  freeWelcomeBonusTitle: "Welcome AI credits",
  freeWelcomeBonusBody: "Eligible new free accounts may receive one-time welcome AI credits. The app shows the current amount and expiry.",
  welcomeCreditsRemaining: "welcome AI credits remaining",
  welcomeCreditsExpire: "Expires",
  perMonth: "per month",
  perYear: "per year",
  mostPopular: "Most popular",
  saveComparedMonthly: "Save",
  currentPlan: "Current plan",
  appStoreBillingSource: "Purchased through the App Store",
  planActive: "Plan already active",
  continueInIOSApp: "Continue in the iOS app",
  appStorePricingFinePrint: "Displayed USD prices are US App Store reference prices. Apple confirms the final price and currency for your storefront, trial eligibility, tax, and renewal terms before purchase in the app.",
  quotaEyebrow: "Plan limits",
  quotaTitle: "Full quota comparison",
  quotaBody: "Limits reset by the period shown. Re-running an item from saved history does not consume another backtest.",
  feature: "Feature",
  guestPlan: "Guest",
  quotaBacktests: "Backtests",
  quotaAIReviews: "AI rule parsing",
  quotaMaxRange: "Maximum test range",
  quotaMarketCandles: "Market candles per run",
  quotaSavedStrategies: "Saved strategies",
  quotaCloudHistory: "Cloud history items",
  quotaPaperBots: "Paper signal bots",
  perDayQuota: "/ day",
  perMonthQuota: "/ month",
  quotaDay: "day",
  quotaDays: "days",
  quotaYear: "year",
  quotaYears: "years",
  localOnly: "local only",
  trialQuotaTitle: "How introductory trial quotas work",
  trialQuotaBody: "Trial limits may differ from paid plans and can change. After activation, the app shows your active limits. Apple confirms eligibility and renewal details before purchase.",
};

const translations: Record<Locale, LocaleStrings> = {
  en,
  vi: {
    account: "Tài khoản",
    accountSyncHint: "Đăng nhập để tải quota, lịch sử cloud và lượt AI.",
    accountSignInUnavailable: "Chưa thể đăng nhập",
    accountSignInUnavailableHint: "Bạn vẫn có thể chạy backtest cơ bản. Đồng bộ tài khoản hiện tạm thời chưa khả dụng.",
    aiCredits: "Lượt AI",
    creditsAvailable: "còn lại",
    api: "API",
    appSubtitle: "Strategy tester",
    backtest: "Kiểm thử",
    backtestComplete: "Backtest hoàn tất",
    backtestFailed: "Backtest thất bại",
    backtests: "Lượt backtest",
    basicBacktestHint: "Backtest cơ bản vẫn chạy được. Đồng bộ tài khoản hiện tạm thời chưa khả dụng.",
    applyResult: "Mở kết quả",
    capital: "Vốn",
    cloudHistory: "Lịch sử cloud",
    commission: "Phí giao dịch",
    botLoginHint: "Đăng nhập để tạo và theo dõi bot paper realtime.",
    bots: "Bot",
    createBot: "Tạo bot",
    cryptoLab: "Phòng thử chiến lược crypto",
    delete: "Xoá",
    drawdown: "Sụt giảm",
    emptyBots: "Chưa có bot realtime.",
    emptyHistory: "Đăng nhập để đồng bộ thiết lập đã lưu từ app iOS.",
    emptyResultsTitle: "Chưa có kết quả backtest",
    emptyResults: "Chạy một backtest để xem lãi ròng, tỉ lệ thắng, sụt giảm, đường vốn và từng lệnh đã đóng.",
    endDate: "Ngày kết thúc",
    entry: "Vào lệnh",
    exit: "Thoát lệnh",
    firebaseNotSet: "Dịch vụ tài khoản tạm thời chưa khả dụng",
    guestMode: "Chế độ khách",
    home: "Trang chủ",
    history: "Lịch sử",
    language: "Ngôn ngữ",
    learn: "Kiến thức",
    manualPrompt: "Prompt thủ công",
    maxRange: "Khoảng tối đa",
    methodology: "Phương pháp tính",
    mode: "Chế độ",
    name: "Tên",
    email: "Email",
    password: "Mật khẩu",
    netProfit: "Lãi ròng",
    noBacktest: "Chưa có backtest",
    noClosedTrades: "Thiết lập này chưa trả về lệnh đã đóng.",
    noRealTrades: "Không đặt lệnh tiền thật",
    period: "Khoảng thời gian",
    periodAdjusted: "Đã điều chỉnh khoảng kiểm thử theo khung nến đã chọn",
    periodClampedByPlan: "Đã tự rút khoảng kiểm thử theo giới hạn gói",
    planValueBody: "Gói trả phí mở khoảng kiểm thử dài hơn, nhiều lịch sử đã lưu và bot tín hiệu mô phỏng hơn.",
    planValueTitle: "Giá trị gói",
    loadingBacktestTitle: "Đang mô phỏng thị trường",
    loadingBacktestData: "Tải dữ liệu nến",
    loadingBacktestEngine: "Kiểm thử luật chiến lược",
    loadingBacktestReport: "Dựng biểu đồ và báo cáo lệnh",
    loadingAnalysisTitle: "Đang đọc hành vi giao dịch",
    loadingAnalysisData: "Đọc các lệnh đã đóng",
    loadingAnalysisEngine: "Tìm điểm yếu chiến lược",
    loadingAnalysisReport: "Chuẩn bị prompt cải thiện",
    estimatedWait: "Thời gian dự kiến",
    position: "Vị thế",
    executionModel: "Mô hình giao dịch",
    spotCash: "Spot / Tiền mặt",
    futuresPerpetual: "Futures / Perpetual",
    spotPositionHint: "Phần phân bổ là giá trị lệnh theo phần trăm tổng vốn tài khoản.",
    futuresPositionHint: "Phần phân bổ là ký quỹ; giá trị vị thế bằng ký quỹ nhân đòn bẩy.",
    capitalAllocation: "Phân bổ vốn",
    marginAllocation: "Phân bổ ký quỹ",
    stopLossPrice: "Cắt lỗ (% giá)",
    takeProfitPrice: "Chốt lời (% giá)",
    positionPreview: "Ước tính vị thế",
    capitalUsed: "Vốn sử dụng",
    marginUsed: "Ký quỹ ban đầu",
    positionValue: "Giá trị vị thế",
    estimatedLossAtStop: "Lỗ tại SL trước chi phí",
    estimatedProfitAtTarget: "Lãi tại TP trước chi phí",
    leverage: "Đòn bẩy",
    marginMode: "Chế độ ký quỹ",
    isolatedMargin: "Isolated",
    crossMargin: "Cross",
    maintenanceMargin: "Ký quỹ duy trì",
    fundingRate: "Funding mỗi kỳ",
    fundingInterval: "Chu kỳ funding",
    liquidationFee: "Phí thanh lý",
    liquidation: "Thanh lý",
    quantity: "Khối lượng",
    roe: "Lợi nhuận trên ký quỹ",
    fundingPaid: "Funding đã trả",
    privacy: "Quyền riêng tư",
    privacyClose: "Đóng",
    privacyIntro: "Backtest giúp người dùng kiểm thử chiến lược và theo dõi tín hiệu giao dịch mô phỏng. Ứng dụng dùng cho giáo dục và nghiên cứu, không phải lời khuyên tài chính.",
    privacyDataTitle: "Dữ liệu được xử lý",
    privacyDataBody: "Chúng tôi có thể xử lý định danh tài khoản, email, trạng thái gói đăng ký, quota, thiết lập backtest, lịch sử, cấu hình bot, token thiết bị và log kỹ thuật cần thiết để vận hành dịch vụ.",
    privacyUseTitle: "Cách sử dụng dữ liệu",
    privacyUseBody: "Dữ liệu được dùng để xác thực, giới hạn quota, đồng bộ lịch sử, chạy backtest, gửi thông báo tín hiệu theo yêu cầu, cải thiện độ ổn định và chống lạm dụng.",
    privacyThirdTitle: "Dịch vụ bên thứ ba",
    privacyThirdBody: "Dịch vụ có thể dùng Google Cloud, Firebase, Cloudflare, dịch vụ Apple, dữ liệu Binance, yfinance và OpenAI hoặc Gemini khi bật AI.",
    privacyRetentionTitle: "Lưu trữ và kiểm soát",
    privacyRetentionBody: "Thiết lập backtest và quota được giữ khi tài khoản còn hoạt động hoặc khi cần vận hành dịch vụ. Người dùng có thể liên hệ hỗ trợ để yêu cầu xoá dữ liệu gắn với tài khoản.",
    privacyTermsTitle: "Điều khoản sử dụng",
    privacyTermsBody: "Gói được mua trong ứng dụng iOS và tuân theo điều khoản Apple. Apple xác nhận giá theo khu vực và điều kiện gia hạn trước khi mua.",
    privacyTermsLink: "Điều khoản sử dụng Apple (EULA)",
    webSubscriptionTerms: "Điều khoản gói mua trên web",
    privacyRiskTitle: "Rủi ro giao dịch",
    privacyRiskBody: "Backtest là mô phỏng lịch sử và không đảm bảo hiệu quả tương lai. Backtest không đặt lệnh thật và không cung cấp lời khuyên đầu tư.",
    privacyUpdated: "Cập nhật lần cuối: 16/07/2026",
    promptPreview: "Prompt bộ đọc quy tắc",
    quotaHistory: "Lịch sử",
    ready: "Sẵn sàng",
    results: "Kết quả",
    returnMetric: "Tỷ suất",
    riskDetails: "SL / TP",
    runBacktest: "Chạy backtest",
    running: "Đang chạy...",
    savedSetups: "thiết lập đã lưu",
    settings: "Cài đặt",
    setup: "Thiết lập",
    signIn: "Đăng nhập",
    signOut: "Đăng xuất",
    signedIn: "Đã đăng nhập",
    slippage: "Trượt giá",
    sample: "Mẫu",
    startDate: "Ngày bắt đầu",
    stopLoss: "Cắt lỗ",
    strategy: "Chiến lược",
    strategyConfidence: "Độ tin cậy chiến lược",
    confidenceHigh: "Cao",
    confidenceMedium: "Trung bình",
    confidenceLow: "Thấp",
    confidenceLargeTradeSample: "Mẫu lệnh lớn",
    confidenceModerateTradeSample: "Mẫu lệnh vừa phải",
    confidenceSmallTradeSample: "Mẫu lệnh nhỏ nhưng còn dùng được",
    confidenceVerySmallTradeSample: "Mẫu lệnh quá ít",
    confidenceWideCandleSample: "Mẫu nến đủ rộng",
    confidenceShortCandleHistory: "Lịch sử nến quá ngắn",
    confidenceNoGrossLoss: "Không có lệnh lỗ gộp trong mẫu này",
    confidenceNoClosedTrades: "Không có lệnh đã đóng",
    confidenceStrongProfitFactor: "Profit factor mạnh",
    confidencePositiveProfitFactor: "Profit factor dương",
    confidenceWeakProfitFactor: "Profit factor dưới 1.0",
    confidencePositiveNetProfit: "Lãi ròng dương sau chi phí",
    confidenceNegativeNetProfit: "Lãi ròng âm sau chi phí",
    confidenceHighWinRate: "Tỉ lệ thắng trên 60%",
    confidenceLowWinRate: "Tỉ lệ thắng dưới 40%",
    confidenceControlledDrawdown: "Drawdown được kiểm soát",
    confidenceHighDrawdown: "Drawdown cao",
    confidenceModerateDrawdown: "Drawdown trung bình",
    confidenceConcentratedProfit: "Lợi nhuận phụ thuộc nhiều vào một lệnh thắng lớn",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "Giao cắt MA",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    takeProfit: "Chốt lời",
    timeframe: "Khung thời gian",
    tick: "Chạy tick",
    refresh: "Tải lại",
    trades: "Lệnh",
    tradeTablePnL: "Lãi/lỗ",
    selectedTrade: "Lệnh đang chọn",
    side: "Hướng",
    aiReview: "AI đọc quy tắc",
    learnStrategies: "Học chiến lược",
    openWebApp: "Mở Web App",
    paperAlerts: "Cảnh báo paper",
    readLearningCenter: "Đọc trung tâm kiến thức",
    riskFirst: "Ưu tiên rủi ro",
    runSampleBacktest: "Chạy backtest",
    runFreeBacktest: "Chạy backtest miễn phí",
    loadedPresetFromGuide: "Đã tải setup từ hướng dẫn",
    resultEmptyStep1: "Chọn thị trường, khung thời gian và chiến lược.",
    resultEmptyStep2: "Chạy backtest kèm phí, trượt giá, cắt lỗ và chốt lời.",
    resultEmptyStep3: "Xem biểu đồ, chi phí, mức sụt giảm vốn và từng giao dịch. Lưu kết quả để so sánh lần kiểm thử tiếp theo.",
    aiImproveCta: "Phân tích và cải thiện chiến lược",
    aiImproveHint: "AI đọc win rate, hành vi stop-loss, điểm thoát và cụm lệnh rồi đề xuất prompt an toàn hơn để test lại.",
    trialPromptBody: "Gói trả phí mở khoảng kiểm thử dài hơn, nhiều lịch sử đã lưu và bot tín hiệu mô phỏng hơn.",
    currentPlanCta: "Gói hiện tại quyết định khoảng test, lượt AI, lịch sử đã lưu và giới hạn bot paper.",
    shareCopied: "Đã copy tóm tắt chia sẻ.",
    shareFailed: "Không thể chia sẻ kết quả này.",
    shareReady: "Đã mở bảng chia sẻ.",
    shareResult: "Chia sẻ kết quả",
    shareDialogTitle: "Chọn nội dung muốn chia sẻ",
    shareEvidenceOnly: "Chỉ chia sẻ bằng chứng tóm tắt",
    shareEvidenceOnlyHint: "Chia sẻ chỉ số và đường cong chuẩn hóa. Quy tắc chiến lược vẫn riêng tư.",
    shareReusableStrategy: "Chiến lược có thể dùng lại",
    shareReusableStrategyHint: "Chủ động công bố quy tắc chuẩn hóa và giả định để người khác chỉnh sửa rồi tự chạy lại.",
    shareReusableUnavailable: "Kết quả này chưa có bộ quy tắc chuẩn hóa để tái lập an toàn.",
    cancel: "Hủy",
    strategyProof: "Kiểm chứng trước rủi ro",
    heroProofTitle: "Xem biểu đồ, giao dịch và rủi ro tại một nơi.",
    heroProofBody: "Kiểm thử quy tắc trên nến lịch sử, xem điểm vào và thoát, rồi lưu kết quả gốc và đổi một điều kiện để so sánh.",
    buy: "Mua",
    sell: "Bán",
    long: "Long",
    short: "Short",
    outcome: "Kết quả",
    winner: "Thắng",
    loser: "Thua",
    duration: "Thời lượng",
    fees: "Phí",
    direction: "Hướng lệnh",
    both: "Cả hai",
    exitMode: "Kiểu thoát lệnh",
    tpSlOnly: "Chỉ TP/SL",
    signalAndRisk: "Tín hiệu + rủi ro",
    signalOnly: "Chỉ tín hiệu",
    aiParser: "AI Parser",
    aiParserHint: "Manual + đăng nhập sẽ dùng AI credits.",
    winRate: "Tỷ lệ thắng",
    heroTitle: "Backtest trước khi giao dịch.",
    pricing: "Bảng giá",
    pricingSeoTitle: "Gói và bảng giá Backtest | Kiểm thử chiến lược",
    pricingSeoDescription: "So sánh giới hạn Guest, Free, Pro và Power cho backtest, lịch sử đã lưu và bot mô phỏng.",
    pricingEyebrow: "Gói trên App Store",
    pricingTitle: "Chọn năng lực nghiên cứu phù hợp.",
    pricingBody: "So sánh giá tại đây, sau đó mở ứng dụng iOS để mua an toàn qua Apple.",
    guestAccessTitle: "Muốn thử mà chưa cần tài khoản?", guestAccessBody: "Tài khoản miễn phí có lịch sử đám mây, chiến lược đã lưu và bot mô phỏng. Giới hạn theo cấu hình dịch vụ hiện tại.",
    freePlan: "Miễn phí", freePrice: "0", freeForever: "Không cần đăng ký thuê bao", freeWelcomeBonusTitle: "Lượt AI chào mừng", freeWelcomeBonusBody: "Tài khoản miễn phí mới đủ điều kiện có thể nhận lượt AI chào mừng một lần. Ứng dụng hiển thị số lượt và hạn dùng hiện tại.", welcomeCreditsRemaining: "lượt AI chào mừng còn lại", welcomeCreditsExpire: "Hết hạn",
    perMonth: "mỗi tháng", perYear: "mỗi năm", mostPopular: "Phổ biến nhất", saveComparedMonthly: "Tiết kiệm",
    currentPlan: "Gói hiện tại", appStoreBillingSource: "Mua qua App Store", planActive: "Gói đang hoạt động",
    continueInIOSApp: "Tiếp tục trong ứng dụng iOS", appStorePricingFinePrint: "Giá USD hiển thị là giá tham khảo trên App Store Hoa Kỳ. Apple xác nhận giá và tiền tệ cuối cùng theo vùng App Store của bạn, điều kiện dùng thử, thuế và gia hạn trước khi mua trong ứng dụng.",
    quotaEyebrow: "Giới hạn gói", quotaTitle: "So sánh quota đầy đủ", quotaBody: "Quota được đặt lại theo chu kỳ hiển thị. Chạy lại một mục trong lịch sử đã lưu không tốn thêm lượt backtest.", feature: "Tính năng", guestPlan: "Khách", quotaBacktests: "Backtest", quotaAIReviews: "AI đọc quy tắc", quotaMaxRange: "Khoảng kiểm thử tối đa", quotaMarketCandles: "Số nến mỗi lần chạy", quotaSavedStrategies: "Chiến lược đã lưu", quotaCloudHistory: "Mục lịch sử", quotaPaperBots: "Bot tín hiệu paper", perDayQuota: "/ ngày", perMonthQuota: "/ tháng", quotaDay: "ngày", quotaDays: "ngày", quotaYear: "năm", quotaYears: "năm", localOnly: "chỉ lưu cục bộ",
    trialQuotaTitle: "Quota dùng thử mở đầu hoạt động thế nào", trialQuotaBody: "Giới hạn dùng thử có thể khác gói trả phí và có thể thay đổi. Sau khi kích hoạt, ứng dụng hiển thị giới hạn đang áp dụng. Apple xác nhận điều kiện và thông tin gia hạn trước khi mua.",
  },
  hi: {
    account: "खाता", backtest: "बैकटेस्ट", results: "परिणाम", history: "इतिहास", language: "भाषा", setup: "सेटअप", runBacktest: "बैकटेस्ट चलाएं", running: "चल रहा है...", capital: "पूंजी", position: "पोज़िशन", stopLoss: "स्टॉप लॉस", takeProfit: "टेक प्रॉफिट", commission: "कमीशन", slippage: "स्लिपेज", timeframe: "टाइमफ्रेम", period: "अवधि", strategy: "रणनीति", signIn: "साइन इन", signOut: "साइन आउट", guestMode: "गेस्ट मोड", home: "होम", learn: "सीखें", aiCredits: "AI क्रेडिट", backtests: "बैकटेस्ट", drawdown: "ड्रॉडाउन", trades: "ट्रेड", winRate: "विन रेट", privacy: "गोपनीयता", privacyClose: "बंद करें", heroTitle: "ट्रेड करने से पहले बैकटेस्ट करें.", privacyIntro: "Backtest रणनीति बैकटेस्ट और पेपर ट्रेडिंग सिग्नल मॉनिटरिंग में मदद करता है। यह शिक्षा और शोध के लिए है, वित्तीय सलाह नहीं।", privacyUpdated: "अंतिम अपडेट: 30 जून 2026",
    aiReview: "AI से नियम पढ़ना",
    heroProofBody: "ऐतिहासिक कैंडल पर नियम जाँचें, प्रवेश और निकास देखें, फिर मूल परिणाम सहेजकर तुलना के लिए एक शर्त बदलें।",
    heroProofTitle: "चार्ट, ट्रेड और जोखिम एक जगह देखें।",
    learnStrategies: "रणनीतियां सीखें",
    noRealTrades: "वास्तविक पैसे के ट्रेड नहीं",
    openWebApp: "वेब ऐप खोलें",
    paperAlerts: "सिम्युलेटेड अलर्ट",
    planValueBody: "सशुल्क प्लान लंबी टेस्ट अवधि, अधिक सहेजा हुआ इतिहास और अधिक पेपर सिग्नल बॉट देते हैं।",
    planValueTitle: "योजना का मूल्य",
    readLearningCenter: "सीखने का केंद्र पढ़ें",
    riskFirst: "पहले जोखिम",
    runSampleBacktest: "Backtest चलाएं",
    shareCopied: "Share summary copy हो गया।",
    shareFailed: "यह result share नहीं हो सका।",
    shareReady: "Share sheet खुल गई।",
    shareResult: "Result share करें",
    shareDialogTitle: "साझा करने के लिए सामग्री चुनें",
    shareEvidenceOnly: "केवल प्रमाण सारांश",
    shareEvidenceOnlyHint: "मेट्रिक और सामान्यीकृत कर्व साझा होते हैं। रणनीति के नियम निजी रहते हैं।",
    shareReusableStrategy: "दोबारा उपयोग योग्य रणनीति",
    shareReusableStrategyHint: "नियम और मान्यताएँ साझा करता है, ताकि दूसरे उन्हें बदलकर फिर चला सकें।",
    shareReusableUnavailable: "इस परिणाम में सुरक्षित रूप से दोहराने योग्य मानक नियम नहीं हैं।",
    cancel: "रद्द करें",
    strategyProof: "जोखिम से पहले प्रमाण",
  },
  ur: {
    account: "اکاؤنٹ", backtest: "بیک ٹیسٹ", results: "نتائج", history: "ہسٹری", language: "زبان", setup: "سیٹ اپ", runBacktest: "بیک ٹیسٹ چلائیں", running: "چل رہا ہے...", capital: "سرمایہ", position: "پوزیشن", stopLoss: "اسٹاپ لاس", takeProfit: "ٹیک پرافٹ", commission: "کمیشن", slippage: "سلپیج", timeframe: "ٹائم فریم", period: "مدت", strategy: "حکمت عملی", signIn: "سائن ان", signOut: "سائن آؤٹ", guestMode: "مہمان موڈ", home: "ہوم", learn: "سیکھیں", aiCredits: "AI کریڈٹس", backtests: "بیک ٹیسٹس", drawdown: "ڈرا ڈاؤن", trades: "ٹریڈز", winRate: "جیت کی شرح", privacy: "پرائیویسی", privacyClose: "بند کریں", heroTitle: "ٹریڈ سے پہلے بیک ٹیسٹ کریں۔", privacyIntro: "Backtest حکمت عملیوں کے بیک ٹیسٹ اور پیپر ٹریڈنگ سگنلز کی نگرانی میں مدد کرتا ہے۔ یہ تعلیم اور تحقیق کے لیے ہے، مالی مشورہ نہیں۔", privacyUpdated: "آخری اپ ڈیٹ: 30 جون 2026",
    aiReview: "AI سے قواعد کی تشریح",
    heroProofBody: "تاریخی کینڈلز پر قواعد آزمائیں، داخلہ اور اخراج دیکھیں، پھر اصل نتیجہ محفوظ کرکے موازنے کے لیے ایک شرط بدلیں۔",
    heroProofTitle: "چارٹ، سودے اور خطرہ ایک جگہ دیکھیں۔",
    learnStrategies: "Strategies سیکھیں",
    noRealTrades: "حقیقی پیسے کی trades نہیں",
    openWebApp: "ویب ایپ کھولیں",
    paperAlerts: "سگنل الرٹس",
    planValueBody: "بامعاوضہ پلان زیادہ طویل ٹیسٹ، زیادہ محفوظ ہسٹری اور زیادہ پیپر سگنل بوٹس دیتے ہیں۔",
    planValueTitle: "پلان کی قدر",
    readLearningCenter: "لرننگ سینٹر پڑھیں",
    riskFirst: "پہلے رسک",
    runSampleBacktest: "Backtest چلائیں",
    shareCopied: "Share summary copy ہو گئی۔",
    shareFailed: "یہ result share نہیں ہو سکا۔",
    shareReady: "Share sheet کھل گئی۔",
    shareResult: "Result share کریں",
    shareDialogTitle: "شیئر کرنے کے لیے مواد منتخب کریں",
    shareEvidenceOnly: "صرف ثبوت کا خلاصہ",
    shareEvidenceOnlyHint: "میٹرکس اور معمول کی منحنی لکیر شیئر ہوتی ہے۔ حکمت عملی کے قواعد نجی رہتے ہیں۔",
    shareReusableStrategy: "دوبارہ قابلِ استعمال حکمت عملی",
    shareReusableStrategyHint: "قواعد اور مفروضے شیئر کرتا ہے تاکہ دوسرے انہیں بدل کر دوبارہ چلا سکیں۔",
    shareReusableUnavailable: "اس نتیجے میں محفوظ طور پر دہرائے جانے والے معیاری قواعد نہیں ہیں۔",
    cancel: "منسوخ کریں",
    strategyProof: "رسک سے پہلے ثبوت",
  },
  th: {
    account: "บัญชี", backtest: "แบ็กเทสต์", results: "ผลลัพธ์", history: "ประวัติ", language: "ภาษา", setup: "ตั้งค่า", runBacktest: "รัน backtest", running: "กำลังรัน...", capital: "เงินทุน", position: "สถานะ", stopLoss: "Stop Loss", takeProfit: "Take Profit", commission: "ค่าคอมมิชชั่น", slippage: "Slippage", timeframe: "กรอบเวลา", period: "ช่วงเวลา", strategy: "กลยุทธ์", signIn: "เข้าสู่ระบบ", signOut: "ออกจากระบบ", guestMode: "โหมดผู้ใช้ทั่วไป", home: "หน้าแรก", learn: "เรียนรู้", aiCredits: "เครดิต AI", backtests: "แบ็กเทสต์", drawdown: "Drawdown", trades: "คำสั่ง", winRate: "อัตราชนะ", privacy: "ความเป็นส่วนตัว", privacyClose: "ปิด", heroTitle: "Backtest ก่อนเทรดจริง", privacyIntro: "Backtest ช่วย backtest กลยุทธ์และติดตามสัญญาณ paper trading ใช้เพื่อการศึกษาและวิจัย ไม่ใช่คำแนะนำทางการเงิน", privacyUpdated: "อัปเดตล่าสุด: 30 มิถุนายน 2026",
    aiReview: "AI แปลงข้อความเป็นกฎ",
    heroProofBody: "ทดสอบกฎกับแท่งราคาในอดีต ตรวจจุดเข้าและออก แล้วบันทึกผลตั้งต้นและเปลี่ยนหนึ่งเงื่อนไขเพื่อเปรียบเทียบ",
    heroProofTitle: "ดูกราฟ การเทรด และความเสี่ยงในที่เดียว",
    learnStrategies: "เรียนรู้กลยุทธ์",
    noRealTrades: "ไม่ส่งคำสั่งเงินจริง",
    openWebApp: "เปิด Web App",
    paperAlerts: "การแจ้งเตือนจำลอง",
    planValueBody: "แพ็กเกจแบบชำระเงินให้ช่วงทดสอบยาวขึ้น ประวัติที่บันทึกมากขึ้น และบอตสัญญาณจำลองมากขึ้น",
    planValueTitle: "คุณค่าของแพ็กเกจ",
    readLearningCenter: "อ่านศูนย์การเรียนรู้",
    riskFirst: "ให้ความสำคัญกับความเสี่ยง",
    runSampleBacktest: "รัน backtest",
    shareCopied: "คัดลอกสรุปสำหรับแชร์แล้ว",
    shareFailed: "แชร์ผลลัพธ์นี้ไม่ได้",
    shareReady: "เปิดหน้าต่างแชร์แล้ว",
    shareResult: "แชร์ผลลัพธ์",
    shareDialogTitle: "เลือกเนื้อหาที่ต้องการแชร์",
    shareEvidenceOnly: "สรุปหลักฐานเท่านั้น",
    shareEvidenceOnlyHint: "แชร์ตัวชี้วัดและเส้นโค้งแบบปรับมาตรฐาน โดยกฎกลยุทธ์ยังเป็นส่วนตัว",
    shareReusableStrategy: "กลยุทธ์ที่นำกลับมาใช้ได้",
    shareReusableStrategyHint: "เผยแพร่กฎและสมมติฐานเพื่อให้ผู้อื่นแก้ไขและรันใหม่ได้",
    shareReusableUnavailable: "ผลลัพธ์นี้ไม่มีกฎมาตรฐานที่ทำซ้ำได้อย่างปลอดภัย",
    cancel: "ยกเลิก",
    strategyProof: "พิสูจน์ก่อนเสี่ยง",
  },
  bn: {
    account: "অ্যাকাউন্ট", backtest: "ব্যাকটেস্ট", results: "ফলাফল", history: "ইতিহাস", language: "ভাষা", setup: "সেটআপ", runBacktest: "ব্যাকটেস্ট চালান", running: "চলছে...", capital: "মূলধন", position: "পজিশন", stopLoss: "স্টপ লস", takeProfit: "টেক প্রফিট", commission: "কমিশন", slippage: "স্লিপেজ", timeframe: "টাইমফ্রেম", period: "সময়কাল", strategy: "কৌশল", signIn: "সাইন ইন", signOut: "সাইন আউট", guestMode: "অতিথি মোড", home: "হোম", learn: "শিখুন", aiCredits: "AI ক্রেডিট", backtests: "ব্যাকটেস্ট", drawdown: "ড্রডাউন", trades: "ট্রেড", winRate: "জয়ের হার", privacy: "গোপনীয়তা", privacyClose: "বন্ধ করুন", heroTitle: "ট্রেড করার আগে ব্যাকটেস্ট করুন।", privacyIntro: "Backtest কৌশল ব্যাকটেস্ট এবং পেপার ট্রেডিং সিগন্যাল মনিটর করতে সাহায্য করে। এটি শিক্ষা ও গবেষণার জন্য, আর্থিক পরামর্শ নয়।", privacyUpdated: "শেষ আপডেট: ৩০ জুন ২০২৬",
    aiReview: "AI দিয়ে নিয়ম বোঝা",
    heroProofBody: "ঐতিহাসিক ক্যান্ডলে নিয়ম পরীক্ষা করুন, প্রবেশ ও প্রস্থান দেখুন, তারপর মূল ফল সংরক্ষণ করে তুলনার জন্য একটি শর্ত বদলান।",
    heroProofTitle: "চার্ট, ট্রেড ও ঝুঁকি এক জায়গায় দেখুন।",
    learnStrategies: "কৌশল শিখুন",
    noRealTrades: "বাস্তব টাকার trade নয়",
    openWebApp: "ওয়েব অ্যাপ খুলুন",
    paperAlerts: "সিমুলেটেড alert",
    planValueBody: "পেইড প্ল্যানে দীর্ঘ সময়ের পরীক্ষা, বেশি সংরক্ষিত ইতিহাস ও বেশি পেপার সিগন্যাল বট পাওয়া যায়।",
    planValueTitle: "প্ল্যানের মূল্য",
    readLearningCenter: "শেখার কেন্দ্র পড়ুন",
    riskFirst: "ঝুঁকি আগে",
    runSampleBacktest: "Backtest চালান",
    shareCopied: "Share summary copy হয়েছে।",
    shareFailed: "এই result share করা যায়নি।",
    shareReady: "Share sheet খোলা হয়েছে।",
    shareResult: "Result share করুন",
    shareDialogTitle: "কী শেয়ার করবেন তা বেছে নিন",
    shareEvidenceOnly: "শুধু প্রমাণের সারাংশ",
    shareEvidenceOnlyHint: "মেট্রিক ও স্বাভাবিকীকৃত কার্ভ শেয়ার হয়। কৌশলের নিয়ম ব্যক্তিগত থাকে।",
    shareReusableStrategy: "পুনর্ব্যবহারযোগ্য কৌশল",
    shareReusableStrategyHint: "নিয়ম ও অনুমান প্রকাশ করে, যাতে অন্যরা সম্পাদনা করে আবার চালাতে পারে।",
    shareReusableUnavailable: "এই ফলাফলে নিরাপদে পুনরুত্পাদনযোগ্য মানসম্মত নিয়ম নেই।",
    cancel: "বাতিল",
    strategyProof: "ঝুঁকির আগে প্রমাণ",
  },
  id: {
    account: "Akun", backtest: "Backtest", results: "Hasil", history: "Riwayat", language: "Bahasa", setup: "Pengaturan", runBacktest: "Jalankan backtest", running: "Berjalan...", capital: "Modal", position: "Posisi", stopLoss: "Stop loss", takeProfit: "Take profit", commission: "Komisi", slippage: "Slippage", timeframe: "Timeframe", period: "Periode", strategy: "Strategi", signIn: "Masuk", signOut: "Keluar", guestMode: "Mode tamu", home: "Beranda", learn: "Belajar", aiCredits: "Kredit AI", backtests: "Backtest", drawdown: "Drawdown", trades: "Trade", winRate: "Win rate", privacy: "Privasi", privacyClose: "Tutup", heroTitle: "Backtest sebelum trading.", privacyIntro: "Backtest membantu pengguna melakukan backtest strategi dan memantau sinyal paper trading. Aplikasi ini untuk edukasi dan riset, bukan nasihat finansial.", privacyUpdated: "Terakhir diperbarui: 30 Juni 2026",
    aiReview: "Penguraian aturan AI",
    heroProofBody: "Uji aturan pada candle historis, periksa masuk dan keluar, lalu simpan hasil awal dan ubah satu kondisi untuk dibandingkan.",
    heroProofTitle: "Lihat grafik, transaksi, dan risiko di satu tempat.",
    learnStrategies: "Pelajari strategi",
    noRealTrades: "Tidak ada trade uang sungguhan",
    openWebApp: "Buka Web App",
    paperAlerts: "Alert simulasi",
    planValueBody: "Paket berbayar menyediakan periode uji lebih panjang, lebih banyak riwayat tersimpan, dan bot sinyal simulasi.",
    planValueTitle: "Nilai paket",
    readLearningCenter: "Baca pusat belajar",
    riskFirst: "Utamakan risiko",
    runSampleBacktest: "Jalankan backtest",
    shareCopied: "Ringkasan share disalin.",
    shareFailed: "Tidak dapat membagikan hasil ini.",
    shareReady: "Share sheet dibuka.",
    shareResult: "Bagikan hasil",
    shareDialogTitle: "Pilih yang ingin dibagikan",
    shareEvidenceOnly: "Ringkasan bukti saja",
    shareEvidenceOnlyHint: "Membagikan metrik dan kurva ternormalisasi. Aturan strategi tetap privat.",
    shareReusableStrategy: "Strategi yang dapat digunakan ulang",
    shareReusableStrategyHint: "Menerbitkan aturan dan asumsi agar orang lain dapat mengubah lalu menjalankannya kembali.",
    shareReusableUnavailable: "Hasil ini tidak memiliki aturan baku yang dapat direproduksi dengan aman.",
    cancel: "Batal",
    strategyProof: "Bukti sebelum risiko",
  },
  fil: {
    account: "Account", backtest: "Backtest", results: "Resulta", history: "Kasaysayan", language: "Wika", setup: "Setup", runBacktest: "Patakbuhin ang backtest", running: "Tumatakbo...", capital: "Capital", position: "Position", stopLoss: "Stop loss", takeProfit: "Take profit", commission: "Komisyon", slippage: "Slippage", timeframe: "Timeframe", period: "Panahon", strategy: "Strategy", signIn: "Mag-sign in", signOut: "Mag-sign out", guestMode: "Guest mode", home: "Home", learn: "Matuto", aiCredits: "AI credits", backtests: "Backtests", drawdown: "Drawdown", trades: "Trades", winRate: "Win rate", privacy: "Privacy", privacyClose: "Isara", heroTitle: "Mag-backtest bago mag-trade.", privacyIntro: "Tumutulong ang Backtest sa backtest ng strategy at pag-monitor ng paper trading signals. Para ito sa edukasyon at research, hindi financial advice.", privacyUpdated: "Huling update: Hunyo 30, 2026",
    aiReview: "Pagbasa ng mga tuntunin gamit ang AI",
    heroProofBody: "Subukan ang tuntunin sa mga dating candle, suriin ang pagpasok at paglabas, saka i-save ang unang resulta at baguhin ang isang kondisyon para ihambing.",
    heroProofTitle: "Tingnan ang chart, mga trade, at panganib sa isang lugar.",
    learnStrategies: "Matuto ng strategies",
    noRealTrades: "Walang totoong pera na trades",
    openWebApp: "Buksan ang Web App",
    paperAlerts: "Simulated alerts",
    planValueBody: "May mas mahabang panahon ng pagsusuri, mas maraming naka-save na kasaysayan, at paper signal bot ang mga bayad na plan.",
    planValueTitle: "Halaga ng plan",
    readLearningCenter: "Basahin ang learning center",
    riskFirst: "Risk muna",
    runSampleBacktest: "Patakbuhin ang backtest",
    shareCopied: "Nakopya ang share summary.",
    shareFailed: "Hindi ma-share ang result na ito.",
    shareReady: "Nabuksan ang share sheet.",
    shareResult: "I-share ang result",
    shareDialogTitle: "Piliin kung ano ang ibabahagi",
    shareEvidenceOnly: "Buod ng ebidensya lamang",
    shareEvidenceOnlyHint: "Ibinabahagi ang metrics at normalized curve. Mananatiling pribado ang mga tuntunin ng strategy.",
    shareReusableStrategy: "Strategy na magagamit muli",
    shareReusableStrategyHint: "Inilalathala ang mga tuntunin at palagay para ma-edit at mapatakbo muli ng iba.",
    shareReusableUnavailable: "Walang pamantayang tuntunin ang resultang ito na ligtas na mauulit.",
    cancel: "Kanselahin",
    strategyProof: "Patunay bago risk",
  },
  "es-419": {
    account: "Cuenta", backtest: "Backtest", results: "Resultados", history: "Historial", language: "Idioma", setup: "Configuración", runBacktest: "Ejecutar backtest", running: "Ejecutando...", capital: "Capital", position: "Posición", stopLoss: "Stop loss", takeProfit: "Take profit", commission: "Comisión", slippage: "Deslizamiento", timeframe: "Temporalidad", period: "Periodo", strategy: "Estrategia", signIn: "Iniciar sesión", signOut: "Cerrar sesión", guestMode: "Modo invitado", home: "Inicio", learn: "Aprender", aiCredits: "Créditos AI", backtests: "Backtests", drawdown: "Drawdown", trades: "Operaciones", winRate: "Tasa de acierto", privacy: "Privacidad", privacyClose: "Cerrar", heroTitle: "Haz backtest antes de operar.", privacyIntro: "Backtest ayuda a hacer backtests de estrategias y monitorear señales de paper trading. Es para educación e investigación, no asesoría financiera.", privacyUpdated: "Última actualización: 30 de junio de 2026",
    aiReview: "Interpretación de reglas con IA",
    heroProofBody: "Prueba una regla con velas históricas, revisa entradas y salidas, guarda el resultado inicial y cambia una condición para comparar.",
    heroProofTitle: "Consulta el gráfico, las operaciones y el riesgo en un solo lugar.",
    learnStrategies: "Aprende estrategias",
    noRealTrades: "Sin operaciones con dinero real",
    openWebApp: "Abrir app web",
    paperAlerts: "Alertas simuladas",
    planValueBody: "Los planes de pago ofrecen periodos de prueba más largos, más historial guardado y más bots de señales simuladas.",
    planValueTitle: "Valor del plan",
    readLearningCenter: "Leer centro de aprendizaje",
    riskFirst: "Riesgo primero",
    runSampleBacktest: "Ejecutar backtest",
    shareCopied: "Resumen para compartir copiado.",
    shareFailed: "No se pudo compartir este resultado.",
    shareReady: "Hoja para compartir abierta.",
    shareResult: "Compartir resultado",
    shareDialogTitle: "Elige qué compartir",
    shareEvidenceOnly: "Solo resumen de evidencia",
    shareEvidenceOnlyHint: "Comparte métricas y una curva normalizada. Las reglas de la estrategia siguen privadas.",
    shareReusableStrategy: "Estrategia reutilizable",
    shareReusableStrategyHint: "Publica las reglas y los supuestos para que otras personas los editen y vuelvan a ejecutar.",
    shareReusableUnavailable: "Este resultado no tiene reglas estándar que puedan reproducirse de forma segura.",
    cancel: "Cancelar",
    strategyProof: "Prueba antes del riesgo",
  },
  "pt-BR": {
    account: "Conta", backtest: "Backtest", results: "Resultados", history: "Histórico", language: "Idioma", setup: "Configuração", runBacktest: "Rodar backtest", running: "Rodando...", capital: "Capital", position: "Posição", stopLoss: "Stop loss", takeProfit: "Take profit", commission: "Comissão", slippage: "Slippage", timeframe: "Tempo gráfico", period: "Período", strategy: "Estratégia", signIn: "Entrar", signOut: "Sair", guestMode: "Modo visitante", home: "Início", learn: "Aprender", aiCredits: "Créditos de IA", backtests: "Backtests", drawdown: "Drawdown", trades: "Trades", winRate: "Taxa de acerto", privacy: "Privacidade", privacyClose: "Fechar", heroTitle: "Faça backtest antes de operar.", privacyIntro: "Backtest ajuda usuários a testar estratégias e monitorar sinais de paper trading. É para educação e pesquisa, não aconselhamento financeiro.", privacyUpdated: "Última atualização: 30 de junho de 2026",
    aiReview: "Interpretação de regras por IA",
    heroProofBody: "Teste uma regra em candles históricos, confira entradas e saídas, salve o resultado inicial e altere uma condição para comparar.",
    heroProofTitle: "Veja o gráfico, as operações e o risco em um só lugar.",
    learnStrategies: "Aprender estratégias",
    noRealTrades: "Sem trades com dinheiro real",
    openWebApp: "Abrir app web",
    paperAlerts: "Alertas simulados",
    planValueBody: "Os planos pagos oferecem períodos de teste mais longos, mais histórico salvo e mais bots de sinais simulados.",
    planValueTitle: "Valor do plano",
    readLearningCenter: "Ler central de aprendizado",
    riskFirst: "Risco primeiro",
    runSampleBacktest: "Rodar backtest",
    shareCopied: "Resumo para compartilhar copiado.",
    shareFailed: "Não foi possível compartilhar este resultado.",
    shareReady: "Tela de compartilhamento aberta.",
    shareResult: "Compartilhar resultado",
    shareDialogTitle: "Escolha o que compartilhar",
    shareEvidenceOnly: "Somente resumo das evidências",
    shareEvidenceOnlyHint: "Compartilha métricas e uma curva normalizada. As regras da estratégia permanecem privadas.",
    shareReusableStrategy: "Estratégia reutilizável",
    shareReusableStrategyHint: "Publica regras e premissas para que outras pessoas editem e executem novamente.",
    shareReusableUnavailable: "Este resultado não tem regras padronizadas que possam ser reproduzidas com segurança.",
    cancel: "Cancelar",
    strategyProof: "Prova antes do risco",
  },
  ja: {
    account: "アカウント", backtest: "バックテスト", results: "結果", history: "履歴", language: "言語", setup: "設定", runBacktest: "バックテスト実行", running: "実行中...", capital: "資金", position: "ポジション", stopLoss: "損切り", takeProfit: "利確", commission: "手数料", slippage: "スリッページ", timeframe: "時間足", period: "期間", strategy: "戦略", signIn: "サインイン", signOut: "サインアウト", guestMode: "ゲストモード", home: "ホーム", learn: "学ぶ", aiCredits: "AIクレジット", backtests: "バックテスト", drawdown: "ドローダウン", trades: "取引", winRate: "勝率", privacy: "プライバシー", privacyClose: "閉じる", heroTitle: "取引前にバックテスト。", privacyIntro: "Backtestは戦略のバックテストとペーパートレードシグナル監視を支援します。教育・研究目的であり、金融助言ではありません。", privacyUpdated: "最終更新日: 2026年6月30日",
    aiReview: "AIによるルール変換",
    heroProofBody: "過去のローソク足でルールを検証し、エントリーと決済を確認。基準となる結果を保存して、条件を一つ変えて比較します。",
    heroProofTitle: "チャート、取引、リスクをまとめて確認。",
    learnStrategies: "戦略を学ぶ",
    noRealTrades: "実資金の取引なし",
    openWebApp: "Webアプリを開く",
    paperAlerts: "シミュレーション通知",
    planValueBody: "有料プランでは検証期間、保存履歴、ペーパーシグナルボットの上限が増えます。",
    planValueTitle: "プランの価値",
    readLearningCenter: "学習センターを読む",
    riskFirst: "リスク優先",
    runSampleBacktest: "バックテスト実行",
    shareCopied: "共有用サマリーをコピーしました。",
    shareFailed: "この結果を共有できませんでした。",
    shareReady: "共有シートを開きました。",
    shareResult: "結果を共有",
    shareDialogTitle: "共有する内容を選択",
    shareEvidenceOnly: "エビデンス概要のみ",
    shareEvidenceOnlyHint: "指標と正規化された曲線を共有します。戦略ルールは非公開のままです。",
    shareReusableStrategy: "再利用可能な戦略",
    shareReusableStrategyHint: "他の人が編集して再実行できるよう、ルールと前提を公開します。",
    shareReusableUnavailable: "この結果には安全に再現できる標準ルールがありません。",
    cancel: "キャンセル",
    strategyProof: "リスク前の検証",
  },
  ko: {
    account: "계정", backtest: "백테스트", results: "결과", history: "기록", language: "언어", setup: "설정", runBacktest: "백테스트 실행", running: "실행 중...", capital: "자본", position: "포지션", stopLoss: "손절", takeProfit: "익절", commission: "수수료", slippage: "슬리피지", timeframe: "타임프레임", period: "기간", strategy: "전략", signIn: "로그인", signOut: "로그아웃", guestMode: "게스트 모드", home: "홈", learn: "배우기", aiCredits: "AI 크레딧", backtests: "백테스트", drawdown: "드로다운", trades: "거래", winRate: "승률", privacy: "개인정보", privacyClose: "닫기", heroTitle: "거래 전 백테스트하세요.", privacyIntro: "Backtest은 전략 백테스트와 페이퍼 트레이딩 신호 모니터링을 돕습니다. 교육 및 연구용이며 투자 조언이 아닙니다.", privacyUpdated: "마지막 업데이트: 2026년 6월 30일",
    aiReview: "AI 규칙 변환",
    heroProofBody: "과거 캔들로 규칙을 테스트하고 진입과 청산을 확인하세요. 기준 결과를 저장한 뒤 조건 하나를 바꿔 비교하세요.",
    heroProofTitle: "차트, 거래, 위험을 한곳에서 확인하세요.",
    learnStrategies: "전략 배우기",
    noRealTrades: "실제 자금 거래 없음",
    openWebApp: "웹 앱 열기",
    paperAlerts: "모의 알림",
    planValueBody: "유료 플랜은 더 긴 테스트 기간, 더 많은 저장 기록과 모의 신호 봇을 제공합니다.",
    planValueTitle: "플랜 가치",
    readLearningCenter: "학습 센터 읽기",
    riskFirst: "리스크 우선",
    runSampleBacktest: "백테스트 실행",
    shareCopied: "공유 요약을 복사했습니다.",
    shareFailed: "이 결과를 공유할 수 없습니다.",
    shareReady: "공유 시트를 열었습니다.",
    shareResult: "결과 공유",
    shareDialogTitle: "공유할 내용 선택",
    shareEvidenceOnly: "근거 요약만 공유",
    shareEvidenceOnlyHint: "지표와 정규화된 곡선을 공유합니다. 전략 규칙은 비공개로 유지됩니다.",
    shareReusableStrategy: "재사용 가능한 전략",
    shareReusableStrategyHint: "다른 사람이 수정하고 다시 실행할 수 있도록 규칙과 가정을 공개합니다.",
    shareReusableUnavailable: "이 결과에는 안전하게 재현할 수 있는 표준 규칙이 없습니다.",
    cancel: "취소",
    strategyProof: "리스크 전 검증",
  },
  ru: {
    account: "Аккаунт", backtest: "Бэктест", results: "Результаты", history: "История", language: "Язык", setup: "Настройки", runBacktest: "Запустить бэктест", running: "Выполняется...", capital: "Капитал", position: "Позиция", stopLoss: "Стоп-лосс", takeProfit: "Тейк-профит", commission: "Комиссия", slippage: "Проскальзывание", timeframe: "Таймфрейм", period: "Период", strategy: "Стратегия", signIn: "Войти", signOut: "Выйти", guestMode: "Гостевой режим", home: "Главная", learn: "Обучение", aiCredits: "AI кредиты", backtests: "Бэктесты", drawdown: "Просадка", trades: "Сделки", winRate: "Win rate", privacy: "Конфиденциальность", privacyClose: "Закрыть", heroTitle: "Проверьте стратегию перед сделкой.", privacyIntro: "Backtest помогает тестировать стратегии и отслеживать сигналы paper trading. Это инструмент для обучения и исследований, не финансовый совет.", privacyUpdated: "Последнее обновление: 30 июня 2026 г.",
    aiReview: "Разбор правил с ИИ",
    heroProofBody: "Проверьте правило на исторических свечах, изучите входы и выходы, сохраните исходный результат и измените одно условие для сравнения.",
    heroProofTitle: "График, сделки и риск в одном месте.",
    learnStrategies: "Изучить стратегии",
    noRealTrades: "Без сделок на реальные деньги",
    openWebApp: "Открыть веб-приложение",
    paperAlerts: "Симулированные уведомления",
    planValueBody: "Платные планы дают более длинные периоды тестирования, больше сохранённой истории и ботов учебных сигналов.",
    planValueTitle: "Ценность плана",
    readLearningCenter: "Читать учебный центр",
    riskFirst: "Сначала риск",
    runSampleBacktest: "Запустить бэктест",
    shareCopied: "Сводка для публикации скопирована.",
    shareFailed: "Не удалось поделиться этим результатом.",
    shareReady: "Открыто окно публикации.",
    shareResult: "Поделиться результатом",
    shareDialogTitle: "Выберите, чем поделиться",
    shareEvidenceOnly: "Только сводка доказательств",
    shareEvidenceOnlyHint: "Публикуются метрики и нормализованная кривая. Правила стратегии остаются закрытыми.",
    shareReusableStrategy: "Повторно используемая стратегия",
    shareReusableStrategyHint: "Публикует правила и допущения, чтобы другие могли изменить и повторно запустить их.",
    shareReusableUnavailable: "В результате нет стандартных правил, которые можно безопасно воспроизвести.",
    cancel: "Отмена",
    strategyProof: "Проверка до риска",
  },
  tr: {
    account: "Hesap", backtest: "Backtest", results: "Sonuçlar", history: "Geçmiş", language: "Dil", setup: "Kurulum", runBacktest: "Backtest çalıştır", running: "Çalışıyor...", capital: "Sermaye", position: "Pozisyon", stopLoss: "Stop loss", takeProfit: "Take profit", commission: "Komisyon", slippage: "Slipaj", timeframe: "Zaman aralığı", period: "Dönem", strategy: "Strateji", signIn: "Giriş yap", signOut: "Çıkış yap", guestMode: "Misafir modu", home: "Ana sayfa", learn: "Öğren", aiCredits: "AI kredisi", backtests: "Backtestler", drawdown: "Drawdown", trades: "İşlemler", winRate: "Kazanma oranı", privacy: "Gizlilik", privacyClose: "Kapat", heroTitle: "İşlemden önce backtest yapın.", privacyIntro: "Backtest stratejileri backtest etmeye ve paper trading sinyallerini izlemeye yardımcı olur. Eğitim ve araştırma amaçlıdır, finansal tavsiye değildir.", privacyUpdated: "Son güncelleme: 30 Haziran 2026",
    aiReview: "AI ile kural yorumlama",
    heroProofBody: "Kuralı geçmiş mumlarda test edin, giriş ve çıkışları inceleyin, ilk sonucu kaydedip karşılaştırmak için tek bir koşulu değiştirin.",
    heroProofTitle: "Grafik, işlemler ve riski tek yerde görün.",
    learnStrategies: "Stratejileri öğren",
    noRealTrades: "Gerçek para işlemi yok",
    openWebApp: "Web uygulamasını aç",
    paperAlerts: "Simülasyon uyarıları",
    planValueBody: "Ücretli planlar daha uzun test aralıkları, daha fazla kayıtlı geçmiş ve simülasyon sinyal botu sunar.",
    planValueTitle: "Plan değeri",
    readLearningCenter: "Öğrenme merkezini oku",
    riskFirst: "Önce risk",
    runSampleBacktest: "Backtest çalıştır",
    shareCopied: "Paylaşım özeti kopyalandı.",
    shareFailed: "Bu sonuç paylaşılamadı.",
    shareReady: "Paylaşım ekranı açıldı.",
    shareResult: "Sonucu paylaş",
    shareDialogTitle: "Neyi paylaşacağınızı seçin",
    shareEvidenceOnly: "Yalnızca kanıt özeti",
    shareEvidenceOnlyHint: "Metrikleri ve normalize edilmiş eğriyi paylaşır. Strateji kuralları gizli kalır.",
    shareReusableStrategy: "Yeniden kullanılabilir strateji",
    shareReusableStrategyHint: "Başkalarının düzenleyip yeniden çalıştırabilmesi için kuralları ve varsayımları yayımlar.",
    shareReusableUnavailable: "Bu sonuçta güvenle yeniden üretilebilecek standart kurallar yok.",
    cancel: "İptal",
    strategyProof: "Riskten önce kanıt",
  },
  uk: {
    account: "Акаунт", backtest: "Бектест", results: "Результати", history: "Історія", language: "Мова", setup: "Налаштування", runBacktest: "Запустити бектест", running: "Виконується...", capital: "Капітал", position: "Позиція", stopLoss: "Стоп-лос", takeProfit: "Тейк-профіт", commission: "Комісія", slippage: "Прослизання", timeframe: "Таймфрейм", period: "Період", strategy: "Стратегія", signIn: "Увійти", signOut: "Вийти", guestMode: "Гостьовий режим", home: "Головна", learn: "Навчання", aiCredits: "AI кредити", backtests: "Бектести", drawdown: "Просадка", trades: "Угоди", winRate: "Win rate", privacy: "Приватність", privacyClose: "Закрити", heroTitle: "Перевіряйте стратегію до угоди.", privacyIntro: "Backtest допомагає тестувати стратегії та відстежувати сигнали paper trading. Це інструмент для навчання й досліджень, не фінансова порада.", privacyUpdated: "Останнє оновлення: 30 червня 2026",
    aiReview: "Розбір правил за допомогою ШІ",
    heroProofBody: "Перевірте правило на історичних свічках, перегляньте входи й виходи, збережіть початковий результат і змініть одну умову для порівняння.",
    heroProofTitle: "Графік, угоди та ризик в одному місці.",
    learnStrategies: "Вивчити стратегії",
    noRealTrades: "Без угод на реальні гроші",
    openWebApp: "Відкрити веб-застосунок",
    paperAlerts: "Симульовані сповіщення",
    planValueBody: "Платні плани дають довші періоди тестування, більше збереженої історії та ботів навчальних сигналів.",
    planValueTitle: "Цінність плану",
    readLearningCenter: "Читати навчальний центр",
    riskFirst: "Спочатку ризик",
    runSampleBacktest: "Запустити бектест",
    shareCopied: "Підсумок для поширення скопійовано.",
    shareFailed: "Не вдалося поділитися цим результатом.",
    shareReady: "Відкрито вікно поширення.",
    shareResult: "Поділитися результатом",
    shareDialogTitle: "Виберіть, чим поділитися",
    shareEvidenceOnly: "Лише підсумок доказів",
    shareEvidenceOnlyHint: "Публікуються метрики й нормалізована крива. Правила стратегії залишаються приватними.",
    shareReusableStrategy: "Стратегія для повторного використання",
    shareReusableStrategyHint: "Публікує правила й припущення, щоб інші могли змінити та запустити їх знову.",
    shareReusableUnavailable: "У результаті немає стандартних правил, які можна безпечно відтворити.",
    cancel: "Скасувати",
    strategyProof: "Перевірка до ризику",
  },
};

const derivativesTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {
    executionModel: "Mô hình giao dịch",
    spotCash: "Giao ngay / Tiền mặt",
    futuresPerpetual: "Hợp đồng tương lai / Vĩnh cửu",
    spotPositionHint: "Phần phân bổ là giá trị danh nghĩa của vị thế theo phần trăm tổng vốn tài khoản.",
    futuresPositionHint: "Phần phân bổ là ký quỹ; giá trị vị thế bằng ký quỹ nhân với đòn bẩy.",
    capitalAllocation: "Phân bổ vốn",
    marginAllocation: "Phân bổ ký quỹ",
    stopLossPrice: "Cắt lỗ (% giá)",
    takeProfitPrice: "Chốt lời (% giá)",
    positionPreview: "Vị thế ước tính",
    capitalUsed: "Vốn sử dụng",
    marginUsed: "Ký quỹ ban đầu",
    positionValue: "Giá trị vị thế",
    estimatedLossAtStop: "Lỗ tại điểm cắt lỗ trước chi phí",
    estimatedProfitAtTarget: "Lãi tại mục tiêu trước chi phí",
    leverage: "Đòn bẩy",
    marginMode: "Chế độ ký quỹ",
    isolatedMargin: "Ký quỹ tách biệt",
    crossMargin: "Ký quỹ chéo",
    isolatedMarginHelp: "Chỉ phần ký quỹ được phân bổ cho vị thế này hỗ trợ vị thế; số dư mô phỏng còn lại không được dùng để trì hoãn thanh lý.",
    crossMarginHelp: "Số dư khả dụng của tài khoản mô phỏng hỗ trợ vị thế, có thể đẩy ngưỡng thanh lý ra xa hơn nhưng khiến phần số dư chịu rủi ro lớn hơn.",
    maintenanceMargin: "Ký quỹ duy trì",
    fundingRate: "Phí tài trợ mỗi kỳ",
    fundingInterval: "Chu kỳ phí tài trợ",
    liquidationFee: "Phí thanh lý",
    liquidation: "Thanh lý",
    quantity: "Khối lượng",
    roe: "Lợi nhuận trên ký quỹ",
    fundingPaid: "Phí tài trợ đã trả",
  },
  hi: {
    executionModel: "ट्रेडिंग मॉडल",
    spotCash: "स्पॉट / नकद",
    futuresPerpetual: "फ्यूचर्स / स्थायी अनुबंध",
    spotPositionHint: "आवंटन, खाते की इक्विटी के प्रतिशत के रूप में पोज़िशन का नाममात्र मूल्य है।",
    futuresPositionHint: "आवंटन मार्जिन है; पोज़िशन मूल्य मार्जिन को लीवरेज से गुणा करने पर मिलता है।",
    capitalAllocation: "पूंजी आवंटन",
    marginAllocation: "मार्जिन आवंटन",
    stopLossPrice: "स्टॉप लॉस (% मूल्य)",
    takeProfitPrice: "टेक प्रॉफिट (% मूल्य)",
    positionPreview: "अनुमानित पोज़िशन",
    capitalUsed: "उपयोग की गई पूंजी",
    marginUsed: "प्रारंभिक मार्जिन",
    positionValue: "पोज़िशन मूल्य",
    estimatedLossAtStop: "लागत से पहले स्टॉप पर अनुमानित नुकसान",
    estimatedProfitAtTarget: "लागत से पहले लक्ष्य पर अनुमानित लाभ",
    leverage: "लीवरेज",
    marginMode: "मार्जिन मोड",
    isolatedMargin: "अलग मार्जिन",
    crossMargin: "साझा मार्जिन",
    isolatedMarginHelp: "केवल इस पोज़िशन को दिया गया मार्जिन इसे सहारा देता है; बाकी सिम्युलेटेड बैलेंस लिक्विडेशन टालने के लिए इस्तेमाल नहीं होता।",
    crossMarginHelp: "उपलब्ध सिम्युलेटेड खाता बैलेंस पोज़िशन को सहारा देता है, जिससे लिक्विडेशन दूर जा सकता है लेकिन बैलेंस का बड़ा हिस्सा जोखिम में आता है।",
    maintenanceMargin: "रखरखाव मार्जिन",
    fundingRate: "प्रति अवधि फंडिंग दर",
    fundingInterval: "फंडिंग अंतराल",
    liquidationFee: "लिक्विडेशन शुल्क",
    liquidation: "लिक्विडेशन",
    quantity: "मात्रा",
    roe: "मार्जिन पर रिटर्न",
    fundingPaid: "भुगतान की गई फंडिंग",
  },
  ur: {
    executionModel: "ٹریڈنگ ماڈل",
    spotCash: "اسپاٹ / نقد",
    futuresPerpetual: "فیوچرز / دائمی معاہدہ",
    spotPositionHint: "مختص رقم، اکاؤنٹ ایکویٹی کے فیصد کے طور پر پوزیشن کی برائے نام قدر ہے۔",
    futuresPositionHint: "مختص رقم مارجن ہے؛ پوزیشن کی قدر مارجن کو لیوریج سے ضرب دینے کے برابر ہے۔",
    capitalAllocation: "سرمائے کی تقسیم",
    marginAllocation: "مارجن کی تقسیم",
    stopLossPrice: "اسٹاپ لاس (% قیمت)",
    takeProfitPrice: "ٹیک پرافٹ (% قیمت)",
    positionPreview: "تخمینی پوزیشن",
    capitalUsed: "استعمال شدہ سرمایہ",
    marginUsed: "ابتدائی مارجن",
    positionValue: "پوزیشن کی قدر",
    estimatedLossAtStop: "اخراجات سے پہلے اسٹاپ پر تخمینی نقصان",
    estimatedProfitAtTarget: "اخراجات سے پہلے ہدف پر تخمینی منافع",
    leverage: "لیوریج",
    marginMode: "مارجن موڈ",
    isolatedMargin: "علیحدہ مارجن",
    crossMargin: "مشترکہ مارجن",
    isolatedMarginHelp: "صرف اس پوزیشن کو دیا گیا مارجن اسے سہارا دیتا ہے؛ باقی سیمولیٹڈ بیلنس لیکویڈیشن کو مؤخر کرنے کے لیے استعمال نہیں ہوتا۔",
    crossMarginHelp: "دستیاب سیمولیٹڈ اکاؤنٹ بیلنس پوزیشن کو سہارا دیتا ہے، جس سے لیکویڈیشن دور ہو سکتی ہے مگر بیلنس کا زیادہ حصہ خطرے میں آتا ہے۔",
    maintenanceMargin: "مینٹیننس مارجن",
    fundingRate: "فی وقفہ فنڈنگ شرح",
    fundingInterval: "فنڈنگ وقفہ",
    liquidationFee: "لیکویڈیشن فیس",
    liquidation: "لیکویڈیشن",
    quantity: "مقدار",
    roe: "مارجن پر منافع",
    fundingPaid: "ادا شدہ فنڈنگ",
  },
  th: {
    executionModel: "รูปแบบการซื้อขาย",
    spotCash: "สปอต / เงินสด",
    futuresPerpetual: "ฟิวเจอร์ส / สัญญาถาวร",
    spotPositionHint: "สัดส่วนคือมูลค่าตามสัญญาของสถานะ คิดเป็นเปอร์เซ็นต์ของเงินทุนในบัญชี",
    futuresPositionHint: "สัดส่วนคือมาร์จิน และมูลค่าสถานะเท่ากับมาร์จินคูณเลเวอเรจ",
    capitalAllocation: "สัดส่วนเงินทุน",
    marginAllocation: "สัดส่วนมาร์จิน",
    stopLossPrice: "จุดตัดขาดทุน (% ราคา)",
    takeProfitPrice: "จุดทำกำไร (% ราคา)",
    positionPreview: "ประมาณการสถานะ",
    capitalUsed: "เงินทุนที่ใช้",
    marginUsed: "มาร์จินเริ่มต้น",
    positionValue: "มูลค่าสถานะ",
    estimatedLossAtStop: "ขาดทุนที่จุดตัดขาดทุนก่อนค่าใช้จ่าย",
    estimatedProfitAtTarget: "กำไรที่เป้าหมายก่อนค่าใช้จ่าย",
    leverage: "เลเวอเรจ",
    marginMode: "โหมดมาร์จิน",
    isolatedMargin: "มาร์จินแยก",
    crossMargin: "มาร์จินร่วม",
    isolatedMarginHelp: "มีเพียงมาร์จินที่กำหนดให้สถานะนี้เท่านั้นที่ช่วยรักษาสถานะ ยอดจำลองที่เหลือจะไม่ถูกใช้เพื่อชะลอการบังคับปิด",
    crossMarginHelp: "ยอดคงเหลือที่ใช้ได้ในบัญชีจำลองจะช่วยรักษาสถานะ ทำให้จุดบังคับปิดอยู่ไกลขึ้นได้ แต่ทำให้ยอดคงเหลือส่วนใหญ่เสี่ยงมากขึ้น",
    maintenanceMargin: "มาร์จินรักษาสถานะ",
    fundingRate: "อัตราฟันดิงต่อรอบ",
    fundingInterval: "รอบฟันดิง",
    liquidationFee: "ค่าธรรมเนียมบังคับปิด",
    liquidation: "บังคับปิดสถานะ",
    quantity: "ปริมาณ",
    roe: "ผลตอบแทนต่อมาร์จิน",
    fundingPaid: "ฟันดิงที่จ่ายแล้ว",
  },
  bn: {
    executionModel: "ট্রেডিং মডেল",
    spotCash: "স্পট / নগদ",
    futuresPerpetual: "ফিউচারস / স্থায়ী চুক্তি",
    spotPositionHint: "বরাদ্দ হলো অ্যাকাউন্ট ইকুইটির শতাংশ হিসেবে পজিশনের নোশনাল মূল্য।",
    futuresPositionHint: "বরাদ্দ হলো মার্জিন; পজিশন মূল্য মার্জিন ও লিভারেজের গুণফল।",
    capitalAllocation: "মূলধন বরাদ্দ",
    marginAllocation: "মার্জিন বরাদ্দ",
    stopLossPrice: "স্টপ লস (% মূল্য)",
    takeProfitPrice: "টেক প্রফিট (% মূল্য)",
    positionPreview: "আনুমানিক পজিশন",
    capitalUsed: "ব্যবহৃত মূলধন",
    marginUsed: "প্রাথমিক মার্জিন",
    positionValue: "পজিশন মূল্য",
    estimatedLossAtStop: "খরচের আগে স্টপে আনুমানিক ক্ষতি",
    estimatedProfitAtTarget: "খরচের আগে লক্ষ্যে আনুমানিক লাভ",
    leverage: "লিভারেজ",
    marginMode: "মার্জিন মোড",
    isolatedMargin: "আলাদা মার্জিন",
    crossMargin: "সম্মিলিত মার্জিন",
    isolatedMarginHelp: "শুধু এই পজিশনে দেওয়া মার্জিন এটিকে সমর্থন করে; লিকুইডেশন পিছিয়ে দিতে বাকি সিমুলেটেড ব্যালেন্স ব্যবহার হয় না।",
    crossMarginHelp: "উপলভ্য সিমুলেটেড অ্যাকাউন্ট ব্যালেন্স পজিশনকে সমর্থন করে; এতে লিকুইডেশন দূরে যেতে পারে, তবে ব্যালেন্সের বেশি অংশ ঝুঁকিতে পড়ে।",
    maintenanceMargin: "রক্ষণাবেক্ষণ মার্জিন",
    fundingRate: "প্রতি পর্যায়ে ফান্ডিং হার",
    fundingInterval: "ফান্ডিং ব্যবধান",
    liquidationFee: "লিকুইডেশন ফি",
    liquidation: "লিকুইডেশন",
    quantity: "পরিমাণ",
    roe: "মার্জিনে রিটার্ন",
    fundingPaid: "পরিশোধিত ফান্ডিং",
  },
  id: {
    executionModel: "Model perdagangan",
    spotCash: "Spot / Tunai",
    futuresPerpetual: "Berjangka / Perpetual",
    spotPositionHint: "Alokasi adalah nilai nosional posisi sebagai persentase ekuitas akun.",
    futuresPositionHint: "Alokasi adalah margin; nilai posisi sama dengan margin dikali leverage.",
    capitalAllocation: "Alokasi modal",
    marginAllocation: "Alokasi margin",
    stopLossPrice: "Batas rugi (% harga)",
    takeProfitPrice: "Ambil untung (% harga)",
    positionPreview: "Perkiraan posisi",
    capitalUsed: "Modal terpakai",
    marginUsed: "Margin awal",
    positionValue: "Nilai posisi",
    estimatedLossAtStop: "Perkiraan rugi di batas rugi sebelum biaya",
    estimatedProfitAtTarget: "Perkiraan laba di target sebelum biaya",
    leverage: "Daya ungkit",
    marginMode: "Mode margin",
    isolatedMargin: "Margin terpisah",
    crossMargin: "Margin silang",
    isolatedMarginHelp: "Hanya margin yang dialokasikan ke posisi ini yang mendukungnya; sisa saldo simulasi tidak digunakan untuk menunda likuidasi.",
    crossMarginHelp: "Saldo akun simulasi yang tersedia mendukung posisi, sehingga likuidasi dapat menjadi lebih jauh tetapi lebih banyak saldo terekspos risiko.",
    maintenanceMargin: "Margin pemeliharaan",
    fundingRate: "Pendanaan per interval",
    fundingInterval: "Interval pendanaan",
    liquidationFee: "Biaya likuidasi",
    liquidation: "Likuidasi",
    quantity: "Jumlah",
    roe: "Imbal hasil atas margin",
    fundingPaid: "Pendanaan dibayar",
  },
  fil: {
    executionModel: "Modelo ng trading",
    spotCash: "Spot / Pera",
    futuresPerpetual: "Kontratang futures / Perpetual",
    spotPositionHint: "Ang allocation ay notional na halaga ng position bilang porsiyento ng equity ng account.",
    futuresPositionHint: "Margin ang allocation; ang halaga ng position ay margin na minultiply sa leverage.",
    capitalAllocation: "Alokasyon ng kapital",
    marginAllocation: "Alokasyon ng margin",
    stopLossPrice: "Stop loss (% ng presyo)",
    takeProfitPrice: "Take profit (% ng presyo)",
    positionPreview: "Tinatayang position",
    capitalUsed: "Ginamit na kapital",
    marginUsed: "Panimulang margin",
    positionValue: "Halaga ng position",
    estimatedLossAtStop: "Tinatayang lugi sa stop bago ang gastos",
    estimatedProfitAtTarget: "Tinatayang tubo sa target bago ang gastos",
    leverage: "Antas ng leverage",
    marginMode: "Paraan ng margin",
    isolatedMargin: "Hiwalay na margin",
    crossMargin: "Pinagsamang margin",
    isolatedMarginHelp: "Tanging margin na inilaan sa position na ito ang sumusuporta rito; hindi ginagamit ang natitirang simulated balance para maantala ang liquidation.",
    crossMarginHelp: "Sinusuportahan ng available na simulated account balance ang position, kaya maaaring lumayo ang liquidation ngunit mas malaking bahagi ng balance ang malalagay sa panganib.",
    maintenanceMargin: "Maintenance na margin",
    fundingRate: "Funding bawat interval",
    fundingInterval: "Agwat ng funding",
    liquidationFee: "Bayad sa liquidation",
    liquidation: "Sapilitang pagsara",
    quantity: "Dami",
    roe: "Kita sa margin",
    fundingPaid: "Nabayarang funding",
  },
  "es-419": {
    executionModel: "Modelo de operación",
    spotCash: "Contado / Efectivo",
    futuresPerpetual: "Futuros / Contrato perpetuo",
    spotPositionHint: "La asignación es el valor nocional de la posición como porcentaje del capital de la cuenta.",
    futuresPositionHint: "La asignación es margen; el valor de la posición equivale al margen multiplicado por el apalancamiento.",
    capitalAllocation: "Asignación de capital",
    marginAllocation: "Asignación de margen",
    stopLossPrice: "Stop loss (% del precio)",
    takeProfitPrice: "Take profit (% del precio)",
    positionPreview: "Posición estimada",
    capitalUsed: "Capital utilizado",
    marginUsed: "Margen inicial",
    positionValue: "Valor de la posición",
    estimatedLossAtStop: "Pérdida estimada en el stop antes de costos",
    estimatedProfitAtTarget: "Ganancia estimada en el objetivo antes de costos",
    leverage: "Apalancamiento",
    marginMode: "Modo de margen",
    isolatedMargin: "Margen aislado",
    crossMargin: "Margen cruzado",
    isolatedMarginHelp: "Solo el margen asignado a esta posición la respalda; el saldo simulado restante no se usa para retrasar la liquidación.",
    crossMarginHelp: "El saldo disponible de la cuenta simulada respalda la posición, lo que puede alejar la liquidación, pero expone una mayor parte del saldo.",
    maintenanceMargin: "Margen de mantenimiento",
    fundingRate: "Financiamiento por intervalo",
    fundingInterval: "Intervalo de financiamiento",
    liquidationFee: "Comisión de liquidación",
    liquidation: "Liquidación",
    quantity: "Cantidad",
    roe: "Rendimiento sobre margen",
    fundingPaid: "Financiamiento pagado",
  },
  "pt-BR": {
    executionModel: "Modelo de negociação",
    spotCash: "À vista / Dinheiro",
    futuresPerpetual: "Futuros / Contrato perpétuo",
    spotPositionHint: "A alocação é o valor nocional da posição como percentual do patrimônio da conta.",
    futuresPositionHint: "A alocação é margem; o valor da posição equivale à margem multiplicada pela alavancagem.",
    capitalAllocation: "Alocação de capital",
    marginAllocation: "Alocação de margem",
    stopLossPrice: "Stop loss (% do preço)",
    takeProfitPrice: "Take profit (% do preço)",
    positionPreview: "Posição estimada",
    capitalUsed: "Capital utilizado",
    marginUsed: "Margem inicial",
    positionValue: "Valor da posição",
    estimatedLossAtStop: "Perda estimada no stop antes dos custos",
    estimatedProfitAtTarget: "Lucro estimado no alvo antes dos custos",
    leverage: "Alavancagem",
    marginMode: "Modo de margem",
    isolatedMargin: "Margem isolada",
    crossMargin: "Margem cruzada",
    isolatedMarginHelp: "Somente a margem atribuída a esta posição a sustenta; o saldo simulado restante não é usado para adiar a liquidação.",
    crossMarginHelp: "O saldo disponível da conta simulada sustenta a posição, o que pode afastar a liquidação, mas expõe uma parte maior do saldo.",
    maintenanceMargin: "Margem de manutenção",
    fundingRate: "Financiamento por intervalo",
    fundingInterval: "Intervalo de financiamento",
    liquidationFee: "Taxa de liquidação",
    liquidation: "Liquidação",
    quantity: "Quantidade",
    roe: "Retorno sobre a margem",
    fundingPaid: "Financiamento pago",
  },
  ja: {
    executionModel: "取引モデル",
    spotCash: "現物 / 現金",
    futuresPerpetual: "先物 / 無期限契約",
    spotPositionHint: "配分は、口座資産に対するポジション想定元本の割合です。",
    futuresPositionHint: "配分は証拠金です。ポジション価値は証拠金にレバレッジを掛けた額です。",
    capitalAllocation: "資金配分",
    marginAllocation: "証拠金配分",
    stopLossPrice: "損切り（価格比）",
    takeProfitPrice: "利確（価格比）",
    positionPreview: "推定ポジション",
    capitalUsed: "使用資金",
    marginUsed: "当初証拠金",
    positionValue: "ポジション価値",
    estimatedLossAtStop: "費用前の損切り時推定損失",
    estimatedProfitAtTarget: "費用前の目標到達時推定利益",
    leverage: "レバレッジ",
    marginMode: "証拠金モード",
    isolatedMargin: "分離証拠金",
    crossMargin: "共有証拠金",
    isolatedMarginHelp: "このポジションに割り当てた証拠金だけで維持され、残りのシミュレーション残高は強制決済を遅らせるために使われません。",
    crossMarginHelp: "利用可能なシミュレーション口座残高がポジションを支えるため、強制決済を遠ざけられる一方で、より多くの残高がリスクにさらされます。",
    maintenanceMargin: "維持証拠金",
    fundingRate: "期間ごとの資金調達率",
    fundingInterval: "資金調達間隔",
    liquidationFee: "強制決済手数料",
    liquidation: "強制決済",
    quantity: "数量",
    roe: "証拠金利益率",
    fundingPaid: "支払済み資金調達料",
  },
  ko: {
    executionModel: "거래 모델",
    spotCash: "현물 / 현금",
    futuresPerpetual: "선물 / 무기한 계약",
    spotPositionHint: "배분은 계정 자산 대비 포지션 명목 가치의 비율입니다.",
    futuresPositionHint: "배분은 증거금이며 포지션 가치는 증거금에 레버리지를 곱한 값입니다.",
    capitalAllocation: "자본 배분",
    marginAllocation: "증거금 배분",
    stopLossPrice: "손절 (% 가격)",
    takeProfitPrice: "익절 (% 가격)",
    positionPreview: "예상 포지션",
    capitalUsed: "사용 자본",
    marginUsed: "개시 증거금",
    positionValue: "포지션 가치",
    estimatedLossAtStop: "비용 전 손절 시 예상 손실",
    estimatedProfitAtTarget: "비용 전 목표가 예상 이익",
    leverage: "레버리지",
    marginMode: "증거금 모드",
    isolatedMargin: "격리 증거금",
    crossMargin: "교차 증거금",
    isolatedMarginHelp: "이 포지션에 배정된 증거금만 포지션을 유지하며, 남은 모의 잔액은 청산을 늦추는 데 사용되지 않습니다.",
    crossMarginHelp: "사용 가능한 모의 계정 잔액이 포지션을 유지하므로 청산 시점이 멀어질 수 있지만 더 많은 잔액이 위험에 노출됩니다.",
    maintenanceMargin: "유지 증거금",
    fundingRate: "구간별 펀딩 비율",
    fundingInterval: "펀딩 간격",
    liquidationFee: "청산 수수료",
    liquidation: "강제 청산",
    quantity: "수량",
    roe: "증거금 수익률",
    fundingPaid: "지급한 펀딩",
  },
  ru: {
    executionModel: "Модель торговли",
    spotCash: "Спот / Наличные",
    futuresPerpetual: "Фьючерсы / Бессрочный контракт",
    spotPositionHint: "Распределение — это номинальная стоимость позиции в процентах от капитала счёта.",
    futuresPositionHint: "Распределение задаёт маржу; стоимость позиции равна марже, умноженной на плечо.",
    capitalAllocation: "Распределение капитала",
    marginAllocation: "Распределение маржи",
    stopLossPrice: "Стоп-лосс (% цены)",
    takeProfitPrice: "Тейк-профит (% цены)",
    positionPreview: "Расчётная позиция",
    capitalUsed: "Использованный капитал",
    marginUsed: "Начальная маржа",
    positionValue: "Стоимость позиции",
    estimatedLossAtStop: "Расчётный убыток на стопе до расходов",
    estimatedProfitAtTarget: "Расчётная прибыль на цели до расходов",
    leverage: "Кредитное плечо",
    marginMode: "Режим маржи",
    isolatedMargin: "Изолированная маржа",
    crossMargin: "Кросс-маржа",
    isolatedMarginHelp: "Позицию поддерживает только выделенная ей маржа; остаток учебного баланса не используется для отсрочки ликвидации.",
    crossMarginHelp: "Доступный учебный баланс поддерживает позицию, что может отдалить ликвидацию, но подвергает риску большую часть баланса.",
    maintenanceMargin: "Поддерживающая маржа",
    fundingRate: "Фандинг за период",
    fundingInterval: "Интервал фандинга",
    liquidationFee: "Комиссия за ликвидацию",
    liquidation: "Ликвидация",
    quantity: "Количество",
    roe: "Доходность маржи",
    fundingPaid: "Уплаченный фандинг",
  },
  tr: {
    executionModel: "İşlem modeli",
    spotCash: "Spot / Nakit",
    futuresPerpetual: "Vadeli / Sürekli sözleşme",
    spotPositionHint: "Tahsis, pozisyonun nominal değerinin hesap özkaynağına oranıdır.",
    futuresPositionHint: "Tahsis marjdır; pozisyon değeri marj ile kaldıracın çarpımına eşittir.",
    capitalAllocation: "Sermaye tahsisi",
    marginAllocation: "Marj tahsisi",
    stopLossPrice: "Zarar durdur (% fiyat)",
    takeProfitPrice: "Kâr al (% fiyat)",
    positionPreview: "Tahmini pozisyon",
    capitalUsed: "Kullanılan sermaye",
    marginUsed: "Başlangıç marjı",
    positionValue: "Pozisyon değeri",
    estimatedLossAtStop: "Maliyetlerden önce stop seviyesinde tahmini zarar",
    estimatedProfitAtTarget: "Maliyetlerden önce hedefte tahmini kâr",
    leverage: "Kaldıraç",
    marginMode: "Marj modu",
    isolatedMargin: "İzole marj",
    crossMargin: "Çapraz marj",
    isolatedMarginHelp: "Pozisyonu yalnızca ona ayrılan marj destekler; kalan simülasyon bakiyesi likidasyonu geciktirmek için kullanılmaz.",
    crossMarginHelp: "Kullanılabilir simülasyon hesap bakiyesi pozisyonu destekler; bu, likidasyonu uzaklaştırabilir ancak bakiyenin daha büyük bölümünü riske atar.",
    maintenanceMargin: "Sürdürme marjı",
    fundingRate: "Dönem başına fonlama",
    fundingInterval: "Fonlama aralığı",
    liquidationFee: "Likidasyon ücreti",
    liquidation: "Likidasyon",
    quantity: "Miktar",
    roe: "Marj getirisi",
    fundingPaid: "Ödenen fonlama",
  },
  uk: {
    executionModel: "Модель торгівлі",
    spotCash: "Спот / Готівка",
    futuresPerpetual: "Ф’ючерси / Безстроковий контракт",
    spotPositionHint: "Розподіл — це номінальна вартість позиції у відсотках від капіталу рахунку.",
    futuresPositionHint: "Розподіл задає маржу; вартість позиції дорівнює маржі, помноженій на плече.",
    capitalAllocation: "Розподіл капіталу",
    marginAllocation: "Розподіл маржі",
    stopLossPrice: "Стоп-лос (% ціни)",
    takeProfitPrice: "Тейк-профіт (% ціни)",
    positionPreview: "Розрахункова позиція",
    capitalUsed: "Використаний капітал",
    marginUsed: "Початкова маржа",
    positionValue: "Вартість позиції",
    estimatedLossAtStop: "Розрахунковий збиток на стопі до витрат",
    estimatedProfitAtTarget: "Розрахунковий прибуток на цілі до витрат",
    leverage: "Кредитне плече",
    marginMode: "Режим маржі",
    isolatedMargin: "Ізольована маржа",
    crossMargin: "Крос-маржа",
    isolatedMarginHelp: "Позицію підтримує лише виділена їй маржа; решта навчального балансу не використовується для відтермінування ліквідації.",
    crossMarginHelp: "Доступний навчальний баланс підтримує позицію, що може віддалити ліквідацію, але наражає на ризик більшу частину балансу.",
    maintenanceMargin: "Підтримувальна маржа",
    fundingRate: "Фандинг за період",
    fundingInterval: "Інтервал фандингу",
    liquidationFee: "Комісія за ліквідацію",
    liquidation: "Ліквідація",
    quantity: "Кількість",
    roe: "Дохідність маржі",
    fundingPaid: "Сплачений фандинг",
  },
};

export const localeNames: Record<Locale, string> = {
  en: "English",
  vi: "Tiếng Việt",
  hi: "हिन्दी",
  ur: "اردو",
  th: "ไทย",
  bn: "বাংলা",
  id: "Bahasa Indonesia",
  fil: "Filipino",
  "es-419": "Español (LatAm)",
  "pt-BR": "Português (Brasil)",
  ja: "日本語",
  ko: "한국어",
  ru: "Русский",
  tr: "Türkçe",
  uk: "Українська",
};

const researchQualityTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {
    strategyConfidence: "Chất lượng backtest trong mẫu",
    qualityNotForecast: "Bằng chứng mô tả, không phải dự báo",
    noLockedHoldout: "Chưa có tập holdout khóa",
    noLockedHoldoutBody: "Điểm này chỉ mô tả mẫu đang hiển thị. Chiến lược chưa được kiểm chứng trên một giai đoạn ngoài mẫu đã khóa.",
    researchEvidence: "Bằng chứng nghiên cứu",
    executionAssumptions: "Giả định khớp lệnh",
    executionNextOpen: "Tín hiệu được đọc khi nến đóng; lệnh thị trường khớp tại giá mở cửa của nến kế tiếp.",
    executionStopFirst: "Nếu stop và target cùng chạm trong một nến, stop được ưu tiên. Lệnh vào thiếu sức mua sẽ bị từ chối.",
    dataProvenance: "Nguồn gốc dữ liệu",
    closedCandlesOnly: "Chỉ dùng nến đã đóng",
    candleCloseUnknown: "Không có trạng thái đóng nến",
    dataGaps: "Khoảng trống dữ liệu nến",
    missingBars: "nến ước tính bị thiếu",
    noUnexpectedGaps: "Không có khoảng trống bất thường trên thị trường liên tục",
    robustness: "Độ bền kết quả",
    strategyReturn: "Lợi nhuận chiến lược",
    benchmarkReturn: "Lợi nhuận mua và giữ",
    drawdownDuration: "Thời lượng drawdown",
    costStress: "Kiểm tra tăng chi phí khớp lệnh",
    rejectedOrders: "Lệnh vào bị từ chối",
    rejectedOrdersHint: "Các lần vào lệnh bị kiểm tra sức mua từ chối không được tính là giao dịch hoàn tất.",
    attemptedNotional: "Giá trị lệnh dự kiến",
    buyingPower: "Sức mua",
    insufficientBuyingPower: "Không đủ sức mua",
    insufficientRiskSample: "Chưa đủ quan sát lợi nhuận để tính các tỷ lệ rủi ro thường niên đáng tin cậy.",
    dataSnapshot: "Ảnh chụp dữ liệu",
    bars: "nến",
    notAvailable: "Chưa có",
    costDoubledFragile: "Lợi nhuận biến mất khi chi phí khớp lệnh tăng gấp đôi.",
    continuousMarketGaps: "Đã phát hiện khoảng trống nến trên thị trường liên tục.",
  },
  hi: {
    strategyConfidence: "इन-सैंपल बैकटेस्ट गुणवत्ता", qualityNotForecast: "वर्णनात्मक प्रमाण, पूर्वानुमान नहीं", noLockedHoldout: "लॉक्ड होल्डआउट नहीं", noLockedHoldoutBody: "यह स्कोर केवल दिखाए गए सैंपल का वर्णन करता है। रणनीति को लॉक्ड आउट-ऑफ-सैंपल अवधि पर सत्यापित नहीं किया गया है।", researchEvidence: "रिसर्च प्रमाण", executionAssumptions: "एक्ज़ीक्यूशन मान्यताएं", executionNextOpen: "सिग्नल बार बंद होने पर पढ़े जाते हैं; मार्केट एंट्री अगली बार के ओपन पर भरती है।", executionStopFirst: "एक ही बार में स्टॉप और टार्गेट छूने पर स्टॉप पहले माना जाता है। अपर्याप्त खरीद शक्ति वाली एंट्री अस्वीकार होती है।", dataProvenance: "डेटा स्रोत", closedCandlesOnly: "केवल बंद कैंडल", candleCloseUnknown: "कैंडल बंद होने की स्थिति उपलब्ध नहीं", dataGaps: "कैंडल गैप", missingBars: "अनुमानित गायब बार", noUnexpectedGaps: "निरंतर बाजार में कोई अप्रत्याशित गैप नहीं", robustness: "मजबूती", strategyReturn: "रणनीति रिटर्न", benchmarkReturn: "खरीदकर रखने का रिटर्न", drawdownDuration: "ड्रॉडाउन अवधि", costStress: "एक्ज़ीक्यूशन लागत स्ट्रेस", rejectedOrders: "अस्वीकृत एंट्री", rejectedOrdersHint: "खरीद शक्ति जांच में अस्वीकृत प्रयास पूरे ट्रेड में शामिल नहीं होते।", attemptedNotional: "प्रयासित अनुमानित मूल्य", buyingPower: "खरीद शक्ति", insufficientBuyingPower: "अपर्याप्त खरीद शक्ति", insufficientRiskSample: "विश्वसनीय वार्षिक जोखिम अनुपात के लिए रिटर्न अवलोकन बहुत कम हैं।", dataSnapshot: "डेटा स्नैपशॉट", bars: "बार", notAvailable: "उपलब्ध नहीं", costDoubledFragile: "एक्ज़ीक्यूशन लागत दोगुनी होने पर लाभ समाप्त हो जाता है।", continuousMarketGaps: "निरंतर बाजार के कैंडल गैप मिले।",
  },
  ur: {
    strategyConfidence: "اِن سیمپل بیک ٹیسٹ معیار", qualityNotForecast: "وضاحتی ثبوت، پیش گوئی نہیں", noLockedHoldout: "لاک شدہ ہولڈ آؤٹ موجود نہیں", noLockedHoldoutBody: "یہ اسکور صرف دکھائے گئے نمونے کو بیان کرتا ہے۔ حکمت عملی کو لاک شدہ آؤٹ آف سیمپل مدت پر جانچا نہیں گیا۔", researchEvidence: "تحقیقی ثبوت", executionAssumptions: "عمل درآمد کے مفروضے", executionNextOpen: "سگنل بار بند ہونے پر پڑھا جاتا ہے؛ مارکیٹ انٹری اگلے بار کے اوپن پر فل ہوتی ہے۔", executionStopFirst: "ایک بار میں اسٹاپ اور ہدف دونوں لگیں تو اسٹاپ پہلے مانا جاتا ہے۔ ناکافی خرید قوت والی انٹری رد ہوتی ہے۔", dataProvenance: "ڈیٹا کا ماخذ", closedCandlesOnly: "صرف بند کینڈلز", candleCloseUnknown: "کینڈل بند ہونے کی حالت دستیاب نہیں", dataGaps: "کینڈل گیپس", missingBars: "تخمینی گم شدہ بارز", noUnexpectedGaps: "مسلسل مارکیٹ میں کوئی غیر متوقع گیپ نہیں", robustness: "مضبوطی", strategyReturn: "حکمت عملی کا ریٹرن", benchmarkReturn: "خرید کر رکھنے کا ریٹرن", drawdownDuration: "ڈرا ڈاؤن دورانیہ", costStress: "عمل درآمد لاگت کا اسٹریس", rejectedOrders: "رد شدہ انٹریز", rejectedOrdersHint: "خرید قوت کی جانچ میں رد ہونے والی انٹریز مکمل ٹریڈز میں شامل نہیں۔", attemptedNotional: "درخواست کردہ مالیت", buyingPower: "خرید قوت", insufficientBuyingPower: "خرید قوت ناکافی ہے", insufficientRiskSample: "قابل اعتماد سالانہ رسک تناسب کے لیے ریٹرن مشاہدات بہت کم ہیں۔", dataSnapshot: "ڈیٹا اسنیپ شاٹ", bars: "بارز", notAvailable: "دستیاب نہیں", costDoubledFragile: "عمل درآمد کی لاگت دگنی ہونے پر منافع ختم ہو جاتا ہے۔", continuousMarketGaps: "مسلسل مارکیٹ کے کینڈل گیپس ملے۔",
  },
  th: {
    strategyConfidence: "คุณภาพแบ็กเทสต์ในกลุ่มตัวอย่าง", qualityNotForecast: "หลักฐานเชิงพรรณนา ไม่ใช่การคาดการณ์", noLockedHoldout: "ยังไม่มีชุด holdout ที่ล็อกไว้", noLockedHoldoutBody: "คะแนนนี้อธิบายเฉพาะตัวอย่างที่แสดง กลยุทธ์ยังไม่ได้ตรวจสอบกับช่วงนอกตัวอย่างที่ล็อกไว้", researchEvidence: "หลักฐานการวิจัย", executionAssumptions: "สมมติฐานการส่งคำสั่ง", executionNextOpen: "อ่านสัญญาณเมื่อแท่งปิด และคำสั่งตลาดเข้าที่ราคาเปิดของแท่งถัดไป", executionStopFirst: "หาก stop และ target ถูกแตะในแท่งเดียวกัน จะถือว่า stop เกิดก่อน และปฏิเสธรายการที่กำลังซื้อไม่พอ", dataProvenance: "ที่มาของข้อมูล", closedCandlesOnly: "ใช้เฉพาะแท่งที่ปิดแล้ว", candleCloseUnknown: "ไม่มีสถานะการปิดแท่ง", dataGaps: "ช่องว่างของแท่งราคา", missingBars: "แท่งที่คาดว่าขาดหาย", noUnexpectedGaps: "ไม่พบช่องว่างผิดปกติในตลาดต่อเนื่อง", robustness: "ความทนทานของผลลัพธ์", strategyReturn: "ผลตอบแทนกลยุทธ์", benchmarkReturn: "ผลตอบแทนซื้อแล้วถือ", drawdownDuration: "ระยะเวลา Drawdown", costStress: "ทดสอบความเครียดด้านต้นทุน", rejectedOrders: "รายการเข้าที่ถูกปฏิเสธ", rejectedOrdersHint: "รายการที่ไม่ผ่านการตรวจสอบกำลังซื้อจะไม่ถูกนับเป็นเทรดที่เสร็จสิ้น", attemptedNotional: "มูลค่าคำสั่งที่พยายาม", buyingPower: "กำลังซื้อ", insufficientBuyingPower: "กำลังซื้อไม่เพียงพอ", insufficientRiskSample: "ข้อมูลผลตอบแทนน้อยเกินไปสำหรับอัตราส่วนความเสี่ยงรายปีที่น่าเชื่อถือ", dataSnapshot: "สแนปช็อตข้อมูล", bars: "แท่ง", notAvailable: "ไม่มีข้อมูล", costDoubledFragile: "กำไรหายไปเมื่อต้นทุนการส่งคำสั่งเพิ่มเป็นสองเท่า", continuousMarketGaps: "พบช่องว่างแท่งราคาในตลาดต่อเนื่อง",
  },
  bn: {
    strategyConfidence: "ইন-স্যাম্পল ব্যাকটেস্ট মান", qualityNotForecast: "বর্ণনামূলক প্রমাণ, পূর্বাভাস নয়", noLockedHoldout: "লক করা হোল্ডআউট নেই", noLockedHoldoutBody: "এই স্কোর শুধু দেখানো নমুনাটি বর্ণনা করে। কৌশলটি লক করা আউট-অফ-স্যাম্পল সময়ে যাচাই করা হয়নি।", researchEvidence: "গবেষণার প্রমাণ", executionAssumptions: "এক্সিকিউশন অনুমান", executionNextOpen: "বার বন্ধ হলে সিগন্যাল পড়া হয়; মার্কেট এন্ট্রি পরের বারের ওপেনে পূরণ হয়।", executionStopFirst: "একই বারে স্টপ ও টার্গেট ছুঁলে স্টপ আগে ধরা হয়। পর্যাপ্ত ক্রয়ক্ষমতা না থাকলে এন্ট্রি বাতিল হয়।", dataProvenance: "ডেটার উৎস", closedCandlesOnly: "শুধু বন্ধ ক্যান্ডেল", candleCloseUnknown: "ক্যান্ডেল বন্ধের অবস্থা নেই", dataGaps: "ক্যান্ডেল গ্যাপ", missingBars: "আনুমানিক অনুপস্থিত বার", noUnexpectedGaps: "ধারাবাহিক বাজারে অপ্রত্যাশিত গ্যাপ নেই", robustness: "দৃঢ়তা", strategyReturn: "কৌশলের রিটার্ন", benchmarkReturn: "বাই অ্যান্ড হোল্ড রিটার্ন", drawdownDuration: "ড্রডাউন সময়কাল", costStress: "এক্সিকিউশন খরচ স্ট্রেস", rejectedOrders: "বাতিল এন্ট্রি", rejectedOrdersHint: "ক্রয়ক্ষমতা পরীক্ষায় বাতিল এন্ট্রি সম্পন্ন ট্রেড হিসেবে গণনা হয় না।", attemptedNotional: "চেষ্টাকৃত অর্ডার মূল্য", buyingPower: "ক্রয়ক্ষমতা", insufficientBuyingPower: "ক্রয়ক্ষমতা অপর্যাপ্ত", insufficientRiskSample: "নির্ভরযোগ্য বার্ষিক ঝুঁকি অনুপাতের জন্য রিটার্ন পর্যবেক্ষণ খুব কম।", dataSnapshot: "ডেটা স্ন্যাপশট", bars: "বার", notAvailable: "পাওয়া যায়নি", costDoubledFragile: "এক্সিকিউশন খরচ দ্বিগুণ হলে লাভ হারিয়ে যায়।", continuousMarketGaps: "ধারাবাহিক বাজারে ক্যান্ডেল গ্যাপ পাওয়া গেছে।",
  },
  id: {
    strategyConfidence: "Kualitas backtest dalam sampel", qualityNotForecast: "Bukti deskriptif, bukan prediksi", noLockedHoldout: "Belum ada holdout terkunci", noLockedHoldoutBody: "Skor ini hanya menggambarkan sampel yang ditampilkan. Strategi belum diverifikasi pada periode di luar sampel yang dikunci.", researchEvidence: "Bukti riset", executionAssumptions: "Asumsi eksekusi", executionNextOpen: "Sinyal dibaca saat candle ditutup; entri pasar terisi pada harga pembukaan candle berikutnya.", executionStopFirst: "Jika stop dan target tersentuh dalam satu candle, stop didahulukan. Entri tanpa daya beli yang cukup ditolak.", dataProvenance: "Asal data", closedCandlesOnly: "Hanya candle yang sudah ditutup", candleCloseUnknown: "Status penutupan candle tidak tersedia", dataGaps: "Celah candle", missingBars: "bar yang diperkirakan hilang", noUnexpectedGaps: "Tidak ada celah tak terduga pada pasar kontinu", robustness: "Ketahanan hasil", strategyReturn: "Return strategi", benchmarkReturn: "Return beli dan tahan", drawdownDuration: "Durasi drawdown", costStress: "Uji tekanan biaya eksekusi", rejectedOrders: "Entri ditolak", rejectedOrdersHint: "Percobaan entri yang ditolak oleh pemeriksaan daya beli tidak dihitung sebagai trade selesai.", attemptedNotional: "Nilai pesanan yang dicoba", buyingPower: "Daya beli", insufficientBuyingPower: "Daya beli tidak mencukupi", insufficientRiskSample: "Observasi return terlalu sedikit untuk rasio risiko tahunan yang andal.", dataSnapshot: "Snapshot data", bars: "bar", notAvailable: "Tidak tersedia", costDoubledFragile: "Profit hilang ketika biaya eksekusi digandakan.", continuousMarketGaps: "Ditemukan celah candle pada pasar kontinu.",
  },
  fil: {
    strategyConfidence: "Kalidad ng in-sample backtest", qualityNotForecast: "Naglalarawang ebidensya, hindi forecast", noLockedHoldout: "Walang naka-lock na holdout", noLockedHoldoutBody: "Inilalarawan lamang ng score ang sample na ipinapakita. Hindi pa nasusuri ang strategy sa naka-lock na out-of-sample na panahon.", researchEvidence: "Ebidensya ng pananaliksik", executionAssumptions: "Mga palagay sa execution", executionNextOpen: "Binabasa ang signal sa pagsara ng bar; napupunan ang market entry sa bukas ng susunod na bar.", executionStopFirst: "Kapag naabot ang stop at target sa iisang bar, stop ang inuuna. Tinatanggihan ang entry kung kulang ang buying power.", dataProvenance: "Pinagmulan ng data", closedCandlesOnly: "Saradong candle lamang", candleCloseUnknown: "Hindi available ang candle-close status", dataGaps: "Mga puwang sa candle", missingBars: "tinatayang nawawalang bar", noUnexpectedGaps: "Walang di-inaasahang puwang sa tuloy-tuloy na market", robustness: "Katatagan", strategyReturn: "Return ng strategy", benchmarkReturn: "Return ng buy and hold", drawdownDuration: "Tagal ng drawdown", costStress: "Stress test ng execution cost", rejectedOrders: "Tinanggihang entry", rejectedOrdersHint: "Hindi kasama sa mga natapos na trade ang entry na tinanggihan ng buying-power check.", attemptedNotional: "Sinubukang halaga ng order", buyingPower: "Buying power", insufficientBuyingPower: "Kulang ang buying power", insufficientRiskSample: "Kulang ang return observations para sa maaasahang annualized risk ratios.", dataSnapshot: "Snapshot ng data", bars: "bar", notAvailable: "Hindi available", costDoubledFragile: "Nawawala ang profit kapag dinoble ang execution cost.", continuousMarketGaps: "May nakitang candle gaps sa tuloy-tuloy na market.",
  },
  "es-419": {
    strategyConfidence: "Calidad del backtest dentro de la muestra", qualityNotForecast: "Evidencia descriptiva, no un pronóstico", noLockedHoldout: "Sin período de reserva bloqueado", noLockedHoldoutBody: "Este puntaje solo describe la muestra mostrada. La estrategia no se ha verificado en un período fuera de muestra bloqueado.", researchEvidence: "Evidencia de investigación", executionAssumptions: "Supuestos de ejecución", executionNextOpen: "Las señales se leen al cierre de la vela; las entradas a mercado se ejecutan en la apertura de la vela siguiente.", executionStopFirst: "Si stop y objetivo se tocan en una vela, prevalece el stop. Se rechazan entradas sin poder de compra suficiente.", dataProvenance: "Procedencia de los datos", closedCandlesOnly: "Solo velas cerradas", candleCloseUnknown: "Estado de cierre de vela no disponible", dataGaps: "Huecos entre velas", missingBars: "velas faltantes estimadas", noUnexpectedGaps: "Sin huecos inesperados en el mercado continuo", robustness: "Robustez", strategyReturn: "Retorno de la estrategia", benchmarkReturn: "Retorno de comprar y mantener", drawdownDuration: "Duración del drawdown", costStress: "Prueba de estrés de costos", rejectedOrders: "Entradas rechazadas", rejectedOrdersHint: "Los intentos rechazados por poder de compra no cuentan como operaciones completadas.", attemptedNotional: "Valor nominal intentado", buyingPower: "Poder de compra", insufficientBuyingPower: "Poder de compra insuficiente", insufficientRiskSample: "Hay muy pocas observaciones de retorno para calcular ratios de riesgo anualizados confiables.", dataSnapshot: "Instantánea de datos", bars: "velas", notAvailable: "No disponible", costDoubledFragile: "La ganancia desaparece al duplicar los costos de ejecución.", continuousMarketGaps: "Se detectaron huecos de velas en un mercado continuo.",
  },
  "pt-BR": {
    strategyConfidence: "Qualidade do backtest na amostra", qualityNotForecast: "Evidência descritiva, não uma previsão", noLockedHoldout: "Sem holdout bloqueado", noLockedHoldoutBody: "Esta pontuação descreve apenas a amostra exibida. A estratégia não foi verificada em um período fora da amostra bloqueado.", researchEvidence: "Evidência de pesquisa", executionAssumptions: "Premissas de execução", executionNextOpen: "Os sinais são lidos no fechamento da vela; as entradas a mercado são executadas na abertura da vela seguinte.", executionStopFirst: "Se stop e alvo forem tocados na mesma vela, o stop prevalece. Entradas sem poder de compra suficiente são rejeitadas.", dataProvenance: "Origem dos dados", closedCandlesOnly: "Somente velas fechadas", candleCloseUnknown: "Status de fechamento da vela indisponível", dataGaps: "Lacunas entre velas", missingBars: "velas ausentes estimadas", noUnexpectedGaps: "Nenhuma lacuna inesperada no mercado contínuo", robustness: "Robustez", strategyReturn: "Retorno da estratégia", benchmarkReturn: "Retorno de comprar e manter", drawdownDuration: "Duração do drawdown", costStress: "Teste de estresse de custos", rejectedOrders: "Entradas rejeitadas", rejectedOrdersHint: "Tentativas rejeitadas pela verificação de poder de compra não contam como trades concluídos.", attemptedNotional: "Valor nocional tentado", buyingPower: "Poder de compra", insufficientBuyingPower: "Poder de compra insuficiente", insufficientRiskSample: "Há poucas observações de retorno para calcular índices de risco anualizados confiáveis.", dataSnapshot: "Snapshot dos dados", bars: "velas", notAvailable: "Indisponível", costDoubledFragile: "O lucro desaparece quando os custos de execução dobram.", continuousMarketGaps: "Foram detectadas lacunas de velas em mercado contínuo.",
  },
  ja: {
    strategyConfidence: "サンプル内バックテスト品質", qualityNotForecast: "予測ではなく、記述的な証拠", noLockedHoldout: "固定ホールドアウト未検証", noLockedHoldoutBody: "このスコアは表示中のサンプルのみを説明します。戦略は固定されたアウト・オブ・サンプル期間で検証されていません。", researchEvidence: "リサーチ証拠", executionAssumptions: "約定の前提", executionNextOpen: "シグナルは足の終値で判定し、成行エントリーは次の足の始値で約定します。", executionStopFirst: "同じ足でストップとターゲットに触れた場合はストップを優先します。買付余力不足のエントリーは拒否されます。", dataProvenance: "データの来歴", closedCandlesOnly: "確定足のみ", candleCloseUnknown: "足の確定状態は不明", dataGaps: "ローソク足の欠損", missingBars: "推定欠損本数", noUnexpectedGaps: "連続市場で予期しない欠損なし", robustness: "堅牢性", strategyReturn: "戦略リターン", benchmarkReturn: "バイ・アンド・ホールド", drawdownDuration: "ドローダウン期間", costStress: "約定コスト・ストレス", rejectedOrders: "拒否されたエントリー", rejectedOrdersHint: "買付余力チェックで拒否されたエントリーは、完了取引に含まれません。", attemptedNotional: "発注を試みた想定元本", buyingPower: "買付余力", insufficientBuyingPower: "買付余力不足", insufficientRiskSample: "信頼できる年率リスク比率を算出するにはリターン観測数が不足しています。", dataSnapshot: "データスナップショット", bars: "本", notAvailable: "利用不可", costDoubledFragile: "約定コストを2倍にすると利益が消えます。", continuousMarketGaps: "連続市場のローソク足に欠損が検出されました。",
  },
  ko: {
    strategyConfidence: "표본 내 백테스트 품질", qualityNotForecast: "예측이 아닌 설명적 증거", noLockedHoldout: "잠긴 홀드아웃 미검증", noLockedHoldoutBody: "이 점수는 표시된 표본만 설명합니다. 전략은 잠긴 표본 외 기간에서 검증되지 않았습니다.", researchEvidence: "연구 근거", executionAssumptions: "체결 가정", executionNextOpen: "신호는 봉 마감에 판정하며 시장가 진입은 다음 봉 시가에 체결됩니다.", executionStopFirst: "한 봉에서 손절과 목표가가 모두 닿으면 손절을 우선합니다. 매수력이 부족한 진입은 거부됩니다.", dataProvenance: "데이터 출처", closedCandlesOnly: "마감된 봉만 사용", candleCloseUnknown: "봉 마감 상태를 확인할 수 없음", dataGaps: "캔들 공백", missingBars: "추정 누락 봉", noUnexpectedGaps: "연속 시장에서 예기치 않은 공백 없음", robustness: "견고성", strategyReturn: "전략 수익률", benchmarkReturn: "매수 후 보유 수익률", drawdownDuration: "드로다운 기간", costStress: "체결 비용 스트레스", rejectedOrders: "거부된 진입", rejectedOrdersHint: "매수력 검사에서 거부된 진입은 완료 거래에 포함되지 않습니다.", attemptedNotional: "시도한 주문 명목값", buyingPower: "매수력", insufficientBuyingPower: "매수력 부족", insufficientRiskSample: "신뢰할 수 있는 연환산 위험 비율을 계산하기에는 수익률 관측치가 너무 적습니다.", dataSnapshot: "데이터 스냅샷", bars: "봉", notAvailable: "사용할 수 없음", costDoubledFragile: "체결 비용이 두 배가 되면 수익이 사라집니다.", continuousMarketGaps: "연속 시장 캔들 공백이 감지되었습니다.",
  },
  ru: {
    strategyConfidence: "Качество бэктеста внутри выборки", qualityNotForecast: "Описательные данные, а не прогноз", noLockedHoldout: "Нет зафиксированной контрольной выборки", noLockedHoldoutBody: "Оценка описывает только показанную выборку. Стратегия не проверялась на зафиксированном вневыборочном периоде.", researchEvidence: "Исследовательские данные", executionAssumptions: "Допущения исполнения", executionNextOpen: "Сигнал читается на закрытии свечи; рыночный вход исполняется по открытию следующей свечи.", executionStopFirst: "Если стоп и цель достигнуты в одной свече, приоритет у стопа. Вход без достаточной покупательной способности отклоняется.", dataProvenance: "Происхождение данных", closedCandlesOnly: "Только закрытые свечи", candleCloseUnknown: "Статус закрытия свечей недоступен", dataGaps: "Пропуски свечей", missingBars: "предполагаемых пропущенных свечей", noUnexpectedGaps: "Нет неожиданных пропусков на непрерывном рынке", robustness: "Устойчивость", strategyReturn: "Доходность стратегии", benchmarkReturn: "Доходность покупки и удержания", drawdownDuration: "Длительность просадки", costStress: "Стресс-тест издержек", rejectedOrders: "Отклонённые входы", rejectedOrdersHint: "Попытки входа, отклонённые проверкой покупательной способности, не считаются завершёнными сделками.", attemptedNotional: "Запрошенный номинал", buyingPower: "Покупательная способность", insufficientBuyingPower: "Недостаточная покупательная способность", insufficientRiskSample: "Наблюдений доходности недостаточно для надёжных годовых коэффициентов риска.", dataSnapshot: "Снимок данных", bars: "свечей", notAvailable: "Недоступно", costDoubledFragile: "При удвоении издержек исполнения прибыль исчезает.", continuousMarketGaps: "Обнаружены пропуски свечей на непрерывном рынке.",
  },
  tr: {
    strategyConfidence: "Örneklem içi backtest kalitesi", qualityNotForecast: "Tahmin değil, betimleyici kanıt", noLockedHoldout: "Kilitli holdout yok", noLockedHoldoutBody: "Bu puan yalnızca gösterilen örneklemi açıklar. Strateji kilitli bir örneklem dışı dönemde doğrulanmadı.", researchEvidence: "Araştırma kanıtı", executionAssumptions: "Gerçekleşme varsayımları", executionNextOpen: "Sinyaller mum kapanışında okunur; piyasa girişleri sonraki mumun açılışında gerçekleşir.", executionStopFirst: "Stop ve hedef aynı mumda tetiklenirse stop önceliklidir. Yetersiz alım gücü olan girişler reddedilir.", dataProvenance: "Veri kaynağı", closedCandlesOnly: "Yalnızca kapanmış mumlar", candleCloseUnknown: "Mum kapanış durumu bilinmiyor", dataGaps: "Mum boşlukları", missingBars: "tahmini eksik mum", noUnexpectedGaps: "Kesintisiz piyasada beklenmedik boşluk yok", robustness: "Sağlamlık", strategyReturn: "Strateji getirisi", benchmarkReturn: "Al ve tut getirisi", drawdownDuration: "Düşüş süresi", costStress: "Gerçekleşme maliyeti stresi", rejectedOrders: "Reddedilen girişler", rejectedOrdersHint: "Alım gücü kontrolünde reddedilen girişler tamamlanan işlemlere dahil edilmez.", attemptedNotional: "Denenen nominal tutar", buyingPower: "Alım gücü", insufficientBuyingPower: "Yetersiz alım gücü", insufficientRiskSample: "Güvenilir yıllık risk oranları için getiri gözlemi çok az.", dataSnapshot: "Veri anlık görüntüsü", bars: "mum", notAvailable: "Mevcut değil", costDoubledFragile: "Gerçekleşme maliyetleri iki katına çıktığında kâr kayboluyor.", continuousMarketGaps: "Kesintisiz piyasada mum boşlukları tespit edildi.",
  },
  uk: {
    strategyConfidence: "Якість бектесту всередині вибірки", qualityNotForecast: "Описові дані, а не прогноз", noLockedHoldout: "Немає зафіксованої контрольної вибірки", noLockedHoldoutBody: "Оцінка описує лише показану вибірку. Стратегію не перевірено на зафіксованому позавибірковому періоді.", researchEvidence: "Дослідницькі дані", executionAssumptions: "Припущення виконання", executionNextOpen: "Сигнал читається на закритті свічки; ринковий вхід виконується за відкриттям наступної свічки.", executionStopFirst: "Якщо стоп і ціль досягнуті в одній свічці, пріоритет має стоп. Вхід без достатньої купівельної спроможності відхиляється.", dataProvenance: "Походження даних", closedCandlesOnly: "Лише закриті свічки", candleCloseUnknown: "Статус закриття свічок недоступний", dataGaps: "Пропуски свічок", missingBars: "імовірно пропущених свічок", noUnexpectedGaps: "Немає неочікуваних пропусків на безперервному ринку", robustness: "Стійкість", strategyReturn: "Дохідність стратегії", benchmarkReturn: "Дохідність купівлі й утримання", drawdownDuration: "Тривалість просадки", costStress: "Стрес-тест витрат", rejectedOrders: "Відхилені входи", rejectedOrdersHint: "Спроби входу, відхилені перевіркою купівельної спроможності, не вважаються завершеними угодами.", attemptedNotional: "Запитана номінальна сума", buyingPower: "Купівельна спроможність", insufficientBuyingPower: "Недостатня купівельна спроможність", insufficientRiskSample: "Спостережень дохідності замало для надійних річних коефіцієнтів ризику.", dataSnapshot: "Знімок даних", bars: "свічок", notAvailable: "Недоступно", costDoubledFragile: "Після подвоєння витрат на виконання прибуток зникає.", continuousMarketGaps: "Виявлено пропуски свічок на безперервному ринку.",
  },
};

const releaseTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {
    strategyEngulfing: "Nến nhấn chìm",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Quét thanh khoản",
    strategyTrendPlus: "Xu hướng+",
    strategyVolBreak: "Phá vỡ volume",
    strategyMeanPlus: "Hồi quy+",
    strategyMTF: "Đa khung",
    setupLoadedFromHistory: "Đã tải thiết lập từ lịch sử.",
    viewHistory: "Xem lịch sử",
    realtimeBotCreated: "Đã tạo bot realtime.",
    createBotFailed: "Tạo bot thất bại.",
    botTickComplete: "Bot tick hoàn tất.",
    tickBotFailed: "Tick bot thất bại.",
    realtimeBotDeleted: "Đã xoá bot realtime.",
    deleteBotFailed: "Xoá bot thất bại.",
    accountCreated: "Đã tạo tài khoản.",
    emailAuthFailed: "Xác thực email thất bại.",
    emailAlreadyInUse: "Email này đã có tài khoản.",
    invalidCredentials: "Email hoặc mật khẩu chưa đúng.",
    passwordTooWeak: "Mật khẩu phải có ít nhất 8 ký tự.",
    signInUnavailable: "Đăng nhập hiện tạm thời chưa khả dụng. Vui lòng thử lại sau.",
    chartIndicators: "Chỉ báo trên chart",
    selectTrade: "Chọn lệnh",
    market: "Thị trường", symbol: "Cặp", signUp: "Đăng ký", createAccount: "Tạo tài khoản", improve: "Cải thiện", improveLoginHint: "Đăng nhập để phân tích thắng/thua và tạo chiến lược cải thiện.", improvedPrompt: "Prompt đã cải thiện", applyImprove: "Áp dụng cải thiện", aiAnalysisReady: "Phân tích AI đã sẵn sàng.", aiAnalysisFailed: "Phân tích AI thất bại.", improvedPromptApplied: "Đã áp dụng prompt cải thiện. Chạy backtest lại để so sánh.", runLockedHoldout: "Chạy kiểm tra holdout cuối", runningLockedHoldout: "Đang kiểm tra dữ liệu chưa từng dùng...", lockedHoldoutHint: "Chỉ có thể áp dụng ứng viên cải thiện sau khi vượt qua lần kiểm tra dữ liệu chưa từng dùng.", validationApplyBlocked: "Chưa thể áp dụng khi chưa xác thực", validationPending: "Ứng viên đang chờ lần kiểm tra holdout cuối.", validationPassed: "Ứng viên đã được xác thực. Bạn có thể áp dụng và chạy backtest mới.", validationRejected: "Ứng viên chưa đạt xác thực. Hãy giữ chiến lược ban đầu.", lockedHoldoutFailed: "Không thể hoàn tất kiểm tra holdout cuối. Vui lòng thử lại.", loadedResultFromHistory: "Đã tải kết quả từ lịch sử.", signInCreateBots: "Đăng nhập để tạo bot realtime.", loadedLatestBotResult: "Đã tải kết quả bot mới nhất.", entryToExit: "Từ vào lệnh đến thoát lệnh", chartEma20: "EMA 20", chartEma50: "EMA 50", chartBollinger: "Bollinger", chartVolume: "Khối lượng", chartTrades: "Lệnh",
  },
  hi: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Setup history से लोड हुआ।",
    realtimeBotCreated: "Realtime bot बन गया।",
    createBotFailed: "Bot बनाना विफल रहा।",
    botTickComplete: "Bot tick पूरा हुआ।",
    tickBotFailed: "Bot tick विफल रहा।",
    realtimeBotDeleted: "Realtime bot deleted.",
    deleteBotFailed: "Bot delete विफल रहा।",
    accountCreated: "Account बन गया।",
    emailAuthFailed: "Email authentication विफल रहा।",
    chartIndicators: "Chart indicators",
    selectTrade: "Trade चुनें",
    accountSyncHint: "quota, cloud history और AI credits लोड करने के लिए साइन इन करें।",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest पूरा हुआ",
    backtestFailed: "Backtest विफल रहा",
    basicBacktestHint: "Backtests चल सकते हैं; account sync अभी अस्थायी रूप से उपलब्ध नहीं है।",
    applyResult: "Result लागू करें",
    cloudHistory: "Cloud history",
    botLoginHint: "Realtime paper bots बनाने और monitor करने के लिए sign in करें।",
    bots: "Bots",
    createBot: "Bot बनाएं",
    cryptoLab: "Trading strategy platform",
    delete: "Delete",
    emptyBots: "अभी कोई realtime bot नहीं।",
    emptyHistory: "iOS app से saved setups sync करने के लिए login करें।",
    emptyResults: "Chart, trades, equity और risk details देखने के लिए backtest चलाएं।",
    endDate: "End date",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Account service unavailable",
    manualPrompt: "Manual prompt",
    maxRange: "Max range",
    mode: "Mode",
    netProfit: "Net profit",
    noBacktest: "अभी कोई backtest नहीं",
    noClosedTrades: "इस setup के लिए कोई closed trade नहीं मिला।",
    periodAdjusted: "Selected candle timeframe के अनुसार period adjust किया गया",
    promptPreview: "Rule parser prompt",
    quotaHistory: "History",
    ready: "Ready",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Settings",
    signedIn: "Signed in",
    startDate: "Start date",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Refresh",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "बाज़ार", symbol: "जोड़ी", name: "नाम", email: "ईमेल", password: "पासवर्ड", signUp: "साइन अप", createAccount: "खाता बनाएं", improve: "सुधारें", improveLoginHint: "जीत/हार का विश्लेषण और बेहतर रणनीति बनाने के लिए साइन इन करें।", improvedPrompt: "बेहतर प्रॉम्प्ट", applyImprove: "सुधार लागू करें", aiAnalysisReady: "AI विश्लेषण तैयार है।", aiAnalysisFailed: "AI विश्लेषण विफल रहा।", improvedPromptApplied: "बेहतर प्रॉम्प्ट लागू हुआ। तुलना के लिए backtest फिर चलाएं।", loadedResultFromHistory: "इतिहास से परिणाम लोड हुआ।", signInCreateBots: "Realtime bot बनाने के लिए साइन इन करें।", loadedLatestBotResult: "नवीनतम bot परिणाम लोड हुआ।", entryToExit: "Entry से exit", chartVolume: "वॉल्यूम", chartTrades: "ट्रेड",
    privacyDataTitle: "हम कौन सा डेटा संसाधित करते हैं", privacyDataBody: "हम account identifier, email, subscription status, quota usage, backtest setup, saved history, bot configuration, push notification device token और service logs संसाधित कर सकते हैं।", privacyUseTitle: "डेटा का उपयोग", privacyUseBody: "डेटा authentication, quota enforcement, history sync, backtests, requested signal notifications, reliability improvement और abuse prevention के लिए उपयोग होता है।", privacyThirdTitle: "तृतीय-पक्ष सेवाएं", privacyThirdBody: "Service Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance, और AI enabled होने पर OpenAI या Gemini का उपयोग कर सकती है।", privacyRetentionTitle: "संग्रहण और नियंत्रण", privacyRetentionBody: "Backtest setups और account usage account active रहने तक या service operations के लिए जरूरत तक रखे जाते हैं। Users account-linked data deletion के लिए support से संपर्क कर सकते हैं।", privacyRiskTitle: "ट्रेडिंग जोखिम", privacyRiskBody: "Backtests historical simulations हैं और future performance की guarantee नहीं देते। Backtest real trades place नहीं करता और investment advice नहीं देता।",
  },
  ur: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Setup history سے لوڈ ہوگیا۔",
    realtimeBotCreated: "Realtime bot بن گیا۔",
    createBotFailed: "Bot بنانا ناکام ہوا۔",
    botTickComplete: "Bot tick مکمل ہوا۔",
    tickBotFailed: "Bot tick ناکام ہوا۔",
    realtimeBotDeleted: "Realtime bot delete ہوگیا۔",
    deleteBotFailed: "Bot delete ناکام ہوا۔",
    accountCreated: "Account بن گیا۔",
    emailAuthFailed: "Email authentication ناکام ہوئی۔",
    chartIndicators: "Chart indicators",
    selectTrade: "Trade منتخب کریں",
    accountSyncHint: "quota، cloud history اور AI credits لوڈ کرنے کے لیے سائن ان کریں۔",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest مکمل ہوگیا",
    backtestFailed: "Backtest ناکام ہوا",
    basicBacktestHint: "Backtests چل سکتے ہیں؛ account sync عارضی طور پر دستیاب نہیں ہے۔",
    applyResult: "Result لاگو کریں",
    cloudHistory: "Cloud history",
    botLoginHint: "Realtime paper bots بنانے اور monitor کرنے کے لیے sign in کریں۔",
    bots: "Bots",
    createBot: "Bot بنائیں",
    cryptoLab: "Trading strategy platform",
    delete: "Delete",
    emptyBots: "ابھی کوئی realtime bot نہیں۔",
    emptyHistory: "iOS app سے saved setups sync کرنے کے لیے login کریں۔",
    emptyResults: "Chart، trades، equity اور risk details دیکھنے کے لیے backtest چلائیں۔",
    endDate: "End date",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Account service unavailable",
    manualPrompt: "Manual prompt",
    maxRange: "Max range",
    mode: "Mode",
    netProfit: "Net profit",
    noBacktest: "ابھی کوئی backtest نہیں",
    noClosedTrades: "اس setup کے لیے کوئی closed trade نہیں ملا۔",
    periodAdjusted: "Selected candle timeframe کے مطابق period adjust ہوگیا",
    promptPreview: "Rule parser prompt",
    quotaHistory: "History",
    ready: "Ready",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Settings",
    signedIn: "Signed in",
    startDate: "Start date",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Refresh",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "مارکیٹ", symbol: "جوڑا", name: "نام", email: "ای میل", password: "پاس ورڈ", signUp: "سائن اپ", createAccount: "اکاؤنٹ بنائیں", improve: "بہتر بنائیں", improveLoginHint: "جیت/ہار کا تجزیہ اور بہتر strategy بنانے کے لیے سائن ان کریں۔", improvedPrompt: "بہتر prompt", applyImprove: "بہتری لاگو کریں", aiAnalysisReady: "AI تجزیہ تیار ہے۔", aiAnalysisFailed: "AI تجزیہ ناکام ہوا۔", improvedPromptApplied: "بہتر prompt لاگو ہوگیا۔ موازنہ کے لیے backtest دوبارہ چلائیں۔", loadedResultFromHistory: "نتیجہ history سے لوڈ ہوگیا۔", signInCreateBots: "Realtime bots بنانے کے لیے سائن ان کریں۔", loadedLatestBotResult: "تازہ ترین bot نتیجہ لوڈ ہوگیا۔", entryToExit: "Entry سے exit", chartVolume: "والیوم", chartTrades: "ٹریڈز",
    privacyDataTitle: "ہم کون سا ڈیٹا پراسیس کرتے ہیں", privacyDataBody: "ہم account identifiers، email، subscription status، quota usage، backtest setup، saved history، bot configuration، push notification device tokens اور service logs پراسیس کر سکتے ہیں۔", privacyUseTitle: "ڈیٹا کا استعمال", privacyUseBody: "یہ ڈیٹا authentication، quotas، history sync، backtests، requested signal notifications، reliability improvement اور abuse prevention کے لیے استعمال ہوتا ہے۔", privacyThirdTitle: "فریقِ ثالث کی سروسز", privacyThirdBody: "Service Google Cloud، Firebase، Cloudflare، Apple services، Binance public market data، yfinance، اور AI features enabled ہوں تو OpenAI یا Gemini استعمال کر سکتی ہے۔", privacyRetentionTitle: "محفوظ رکھنا اور کنٹرول", privacyRetentionBody: "Backtest setups اور account usage account active رہنے تک یا service operations کے لیے ضرورت کے مطابق رکھے جاتے ہیں۔ Users account-linked data deletion کے لیے support سے رابطہ کر سکتے ہیں۔", privacyRiskTitle: "ٹریڈنگ کا خطرہ", privacyRiskBody: "Backtests historical simulations ہیں اور future performance کی ضمانت نہیں دیتے۔ Backtest real trades place نہیں کرتا اور investment advice نہیں دیتا۔",
  },
  th: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "หลายกรอบเวลา",
    setupLoadedFromHistory: "โหลด setup จากประวัติแล้ว",
    realtimeBotCreated: "สร้าง bot realtime แล้ว",
    createBotFailed: "สร้าง bot ไม่สำเร็จ",
    botTickComplete: "Bot tick เสร็จแล้ว",
    tickBotFailed: "Bot tick ไม่สำเร็จ",
    realtimeBotDeleted: "ลบ bot realtime แล้ว",
    deleteBotFailed: "ลบ bot ไม่สำเร็จ",
    accountCreated: "สร้างบัญชีแล้ว",
    emailAuthFailed: "ยืนยัน email ไม่สำเร็จ",
    chartIndicators: "ตัวชี้วัดบน chart",
    selectTrade: "เลือกคำสั่ง",
    accountSyncHint: "เข้าสู่ระบบเพื่อโหลด quota, cloud history และเครดิต AI",
    api: "API",
    appSubtitle: "เครื่องมือทดสอบกลยุทธ์",
    backtestComplete: "Backtest เสร็จสมบูรณ์",
    backtestFailed: "Backtest ล้มเหลว",
    basicBacktestHint: "Backtest ยังรันได้; การ sync บัญชีไม่พร้อมใช้งานชั่วคราว",
    applyResult: "ใช้ผลลัพธ์",
    cloudHistory: "ประวัติบนคลาวด์",
    botLoginHint: "เข้าสู่ระบบเพื่อสร้างและติดตาม bot paper realtime",
    bots: "บอท",
    createBot: "สร้างบอท",
    cryptoLab: "แล็บกลยุทธ์คริปโต",
    delete: "ลบ",
    emptyBots: "ยังไม่มี bot realtime",
    emptyHistory: "เข้าสู่ระบบเพื่อ sync setup ที่บันทึกจากแอป iOS",
    emptyResults: "รัน backtest เพื่อดู chart, trades, equity และรายละเอียดความเสี่ยง",
    endDate: "วันที่สิ้นสุด",
    entry: "เข้า",
    exit: "ออก",
    firebaseNotSet: "บริการบัญชีไม่พร้อมใช้งานชั่วคราว",
    manualPrompt: "Prompt แบบกำหนดเอง",
    maxRange: "ช่วงสูงสุด",
    mode: "โหมด",
    netProfit: "กำไรสุทธิ",
    noBacktest: "ยังไม่มี backtest",
    noClosedTrades: "setup นี้ยังไม่มีคำสั่งที่ปิดแล้ว",
    periodAdjusted: "ปรับช่วงเวลาตามกรอบแท่งเทียนที่เลือกแล้ว",
    promptPreview: "Prompt ตัวอ่านกฎ",
    quotaHistory: "ประวัติ",
    ready: "พร้อม",
    returnMetric: "ผลตอบแทน",
    riskDetails: "SL / TP",
    savedSetups: "setup ที่บันทึก",
    settings: "ตั้งค่า",
    signedIn: "เข้าสู่ระบบแล้ว",
    startDate: "วันที่เริ่มต้น",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "รีเฟรช",
    tradeTablePnL: "กำไร/ขาดทุน",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "ตลาด", symbol: "คู่เหรียญ", name: "ชื่อ", email: "อีเมล", password: "รหัสผ่าน", signUp: "สมัครสมาชิก", createAccount: "สร้างบัญชี", improve: "ปรับปรุง", improveLoginHint: "เข้าสู่ระบบเพื่อวิเคราะห์แพ้/ชนะและสร้างกลยุทธ์ที่ดีขึ้น", improvedPrompt: "Prompt ที่ปรับปรุงแล้ว", applyImprove: "ใช้การปรับปรุง", aiAnalysisReady: "AI วิเคราะห์เสร็จแล้ว", aiAnalysisFailed: "AI วิเคราะห์ไม่สำเร็จ", improvedPromptApplied: "ใช้ prompt ที่ปรับปรุงแล้ว รัน backtest อีกครั้งเพื่อเปรียบเทียบ", loadedResultFromHistory: "โหลดผลลัพธ์จากประวัติแล้ว", signInCreateBots: "เข้าสู่ระบบเพื่อสร้าง bot realtime", loadedLatestBotResult: "โหลดผล bot ล่าสุดแล้ว", entryToExit: "จาก entry ถึง exit", chartVolume: "Volume", chartTrades: "คำสั่ง",
    privacyDataTitle: "ข้อมูลที่เราประมวลผล", privacyDataBody: "เราอาจประมวลผลตัวระบุบัญชี อีเมล สถานะ subscription การใช้ quota setup backtest ประวัติที่บันทึกไว้ การตั้งค่า bot token อุปกรณ์สำหรับ push notification และ log เทคนิคที่จำเป็น", privacyUseTitle: "เราใช้ข้อมูลอย่างไร", privacyUseBody: "เราใช้ข้อมูลเพื่อยืนยันตัวตน จำกัด quota sync ประวัติ รัน backtest ส่ง notification สัญญาณที่ผู้ใช้ขอ ปรับปรุงความเสถียร และป้องกันการใช้งานผิดปกติ", privacyThirdTitle: "บริการภายนอก", privacyThirdBody: "บริการอาจใช้ Google Cloud, Firebase, Cloudflare, Apple services, ข้อมูลตลาดสาธารณะจาก Binance, yfinance และ OpenAI หรือ Gemini เมื่อเปิดใช้ AI", privacyRetentionTitle: "การเก็บรักษาและการควบคุม", privacyRetentionBody: "Setup backtest และข้อมูลการใช้งานบัญชีจะถูกเก็บขณะบัญชียัง active หรือเท่าที่จำเป็นต่อการให้บริการ ผู้ใช้ติดต่อ support เพื่อลบข้อมูลที่ผูกกับบัญชีได้", privacyRiskTitle: "ความเสี่ยงในการเทรด", privacyRiskBody: "Backtest เป็นการจำลองจากข้อมูลย้อนหลังและไม่รับประกันผลในอนาคต Backtest ไม่ส่งคำสั่งจริงและไม่ใช่คำแนะนำการลงทุน",
  },
  bn: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Setup history থেকে লোড হয়েছে।",
    realtimeBotCreated: "Realtime bot তৈরি হয়েছে।",
    createBotFailed: "Bot তৈরি ব্যর্থ।",
    botTickComplete: "Bot tick সম্পন্ন।",
    tickBotFailed: "Bot tick ব্যর্থ।",
    realtimeBotDeleted: "Realtime bot delete হয়েছে।",
    deleteBotFailed: "Bot delete ব্যর্থ।",
    accountCreated: "Account তৈরি হয়েছে।",
    emailAuthFailed: "Email authentication ব্যর্থ।",
    chartIndicators: "Chart indicators",
    selectTrade: "Trade নির্বাচন করুন",
    accountSyncHint: "quota, cloud history এবং AI credits লোড করতে সাইন ইন করুন।",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest সম্পন্ন",
    backtestFailed: "Backtest ব্যর্থ",
    basicBacktestHint: "Backtests চলবে; account sync আপাতত উপলভ্য নয়।",
    applyResult: "Result প্রয়োগ করুন",
    cloudHistory: "Cloud history",
    botLoginHint: "Realtime paper bots তৈরি ও monitor করতে sign in করুন।",
    bots: "Bots",
    createBot: "Bot তৈরি করুন",
    cryptoLab: "Trading strategy platform",
    delete: "Delete",
    emptyBots: "এখনও realtime bot নেই।",
    emptyHistory: "iOS app থেকে saved setups sync করতে login করুন।",
    emptyResults: "Chart, trades, equity এবং risk details দেখতে backtest চালান।",
    endDate: "End date",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Account service unavailable",
    manualPrompt: "Manual prompt",
    maxRange: "Max range",
    mode: "Mode",
    netProfit: "Net profit",
    noBacktest: "এখনও backtest নেই",
    noClosedTrades: "এই setup-এর জন্য closed trade নেই।",
    periodAdjusted: "Selected candle timeframe অনুযায়ী period adjust করা হয়েছে",
    promptPreview: "Rule parser prompt",
    quotaHistory: "History",
    ready: "Ready",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Settings",
    signedIn: "Signed in",
    startDate: "Start date",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Refresh",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "মার্কেট", symbol: "পেয়ার", name: "নাম", email: "ইমেইল", password: "পাসওয়ার্ড", signUp: "সাইন আপ", createAccount: "অ্যাকাউন্ট তৈরি করুন", improve: "উন্নত করুন", improveLoginHint: "জয়/পরাজয় বিশ্লেষণ ও উন্নত strategy পেতে সাইন ইন করুন।", improvedPrompt: "উন্নত prompt", applyImprove: "উন্নতি প্রয়োগ করুন", aiAnalysisReady: "AI বিশ্লেষণ প্রস্তুত।", aiAnalysisFailed: "AI বিশ্লেষণ ব্যর্থ হয়েছে।", improvedPromptApplied: "উন্নত prompt প্রয়োগ হয়েছে। তুলনার জন্য backtest আবার চালান।", loadedResultFromHistory: "History থেকে ফলাফল লোড হয়েছে।", signInCreateBots: "Realtime bot তৈরি করতে সাইন ইন করুন।", loadedLatestBotResult: "সর্বশেষ bot ফলাফল লোড হয়েছে।", entryToExit: "Entry থেকে exit", chartVolume: "Volume", chartTrades: "ট্রেড",
    privacyDataTitle: "আমরা যে ডেটা প্রক্রিয়া করি", privacyDataBody: "আমরা account identifiers, email, subscription status, quota usage, backtest setup, saved history, bot configuration, push notification device tokens এবং service logs প্রক্রিয়া করতে পারি।", privacyUseTitle: "ডেটা ব্যবহারের ধরন", privacyUseBody: "ডেটা authentication, quota enforcement, history sync, backtests, requested signal notifications, reliability improvement এবং abuse prevention-এ ব্যবহৃত হয়।", privacyThirdTitle: "তৃতীয় পক্ষের সেবা", privacyThirdBody: "Service Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance, এবং AI enabled থাকলে OpenAI বা Gemini ব্যবহার করতে পারে।", privacyRetentionTitle: "সংরক্ষণ ও নিয়ন্ত্রণ", privacyRetentionBody: "Backtest setups এবং account usage account active থাকা পর্যন্ত বা service operations-এর প্রয়োজনে রাখা হয়। Users account-linked data deletion-এর জন্য support-এ যোগাযোগ করতে পারেন।", privacyRiskTitle: "ট্রেডিং ঝুঁকি", privacyRiskBody: "Backtests historical simulations এবং future performance guarantee করে না। Backtest real trades place করে না এবং investment advice দেয় না।",
  },
  id: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Setup dimuat dari riwayat.",
    realtimeBotCreated: "Bot realtime dibuat.",
    createBotFailed: "Gagal membuat bot.",
    botTickComplete: "Bot tick selesai.",
    tickBotFailed: "Bot tick gagal.",
    realtimeBotDeleted: "Bot realtime dihapus.",
    deleteBotFailed: "Gagal menghapus bot.",
    accountCreated: "Akun dibuat.",
    emailAuthFailed: "Autentikasi email gagal.",
    chartIndicators: "Indikator chart",
    selectTrade: "Pilih trade",
    accountSyncHint: "Masuk untuk memuat quota, riwayat cloud, dan kredit AI.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest selesai",
    backtestFailed: "Backtest gagal",
    basicBacktestHint: "Backtest tetap bisa berjalan; sinkronisasi akun belum tersedia sementara.",
    applyResult: "Terapkan hasil",
    cloudHistory: "Riwayat cloud",
    botLoginHint: "Masuk untuk membuat dan memantau bot paper realtime.",
    bots: "Bot",
    createBot: "Buat bot",
    cryptoLab: "Lab strategi crypto",
    delete: "Hapus",
    emptyBots: "Belum ada bot realtime.",
    emptyHistory: "Login untuk sinkronisasi setup tersimpan dari app iOS.",
    emptyResults: "Jalankan backtest untuk melihat chart, trade, equity, dan detail risiko.",
    endDate: "Tanggal akhir",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Layanan akun sementara tidak tersedia",
    manualPrompt: "Prompt manual",
    maxRange: "Rentang maks",
    mode: "Mode",
    netProfit: "Profit bersih",
    noBacktest: "Belum ada backtest",
    noClosedTrades: "Tidak ada trade tertutup untuk setup ini.",
    periodAdjusted: "Periode disesuaikan dengan timeframe candle yang dipilih",
    promptPreview: "Prompt parser aturan",
    quotaHistory: "Riwayat",
    ready: "Siap",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "setup tersimpan",
    settings: "Pengaturan",
    signedIn: "Sudah masuk",
    startDate: "Tanggal mulai",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Refresh",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Pasar", symbol: "Pair", name: "Nama", email: "Email", password: "Kata sandi", signUp: "Daftar", createAccount: "Buat akun", improve: "Improve", improveLoginHint: "Masuk untuk menganalisis menang/kalah dan membuat strategi yang lebih baik.", improvedPrompt: "Prompt yang ditingkatkan", applyImprove: "Terapkan improve", aiAnalysisReady: "Analisis AI siap.", aiAnalysisFailed: "Analisis AI gagal.", improvedPromptApplied: "Prompt improve diterapkan. Jalankan backtest lagi untuk membandingkan.", loadedResultFromHistory: "Hasil dimuat dari riwayat.", signInCreateBots: "Masuk untuk membuat bot realtime.", loadedLatestBotResult: "Hasil bot terbaru dimuat.", entryToExit: "Entry ke exit", chartVolume: "Volume", chartTrades: "Trade",
    privacyDataTitle: "Data yang kami proses", privacyDataBody: "Kami dapat memproses identifier akun, email, status subscription, penggunaan quota, setup backtest, riwayat tersimpan, konfigurasi bot, token perangkat untuk push notification, dan log teknis.", privacyUseTitle: "Cara kami menggunakan data", privacyUseBody: "Data digunakan untuk autentikasi, quota, sinkronisasi riwayat, menjalankan backtest, mengirim notifikasi sinyal yang diminta, meningkatkan reliability, dan mencegah abuse.", privacyThirdTitle: "Layanan pihak ketiga", privacyThirdBody: "Service dapat memakai Google Cloud, Firebase, Cloudflare, Apple services, data market publik Binance, yfinance, serta OpenAI atau Gemini saat fitur AI aktif.", privacyRetentionTitle: "Retensi dan kontrol", privacyRetentionBody: "Setup backtest dan penggunaan akun disimpan selama akun aktif atau diperlukan untuk operasi layanan. User dapat menghubungi support untuk menghapus data terkait akun.", privacyRiskTitle: "Risiko trading", privacyRiskBody: "Backtest adalah simulasi historis dan tidak menjamin performa masa depan. Backtest tidak menempatkan trade sungguhan dan bukan nasihat investasi.",
  },
  fil: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Liquidity Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Na-load ang setup mula sa history.",
    realtimeBotCreated: "Nagawa ang realtime bot.",
    createBotFailed: "Nabigo ang paggawa ng bot.",
    botTickComplete: "Tapos ang bot tick.",
    tickBotFailed: "Nabigo ang bot tick.",
    realtimeBotDeleted: "Nabura ang realtime bot.",
    deleteBotFailed: "Nabigo ang pagbura ng bot.",
    accountCreated: "Nagawa ang account.",
    emailAuthFailed: "Nabigo ang email authentication.",
    chartIndicators: "Chart indicators",
    selectTrade: "Piliin ang trade",
    accountSyncHint: "Mag-sign in para i-load ang quota, cloud history, at AI credits.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Tapos na ang backtest",
    backtestFailed: "Nabigo ang backtest",
    basicBacktestHint: "Gagana pa rin ang backtests; pansamantalang hindi available ang account sync.",
    applyResult: "I-apply ang resulta",
    cloudHistory: "Cloud history",
    botLoginHint: "Mag-sign in para gumawa at mag-monitor ng realtime paper bots.",
    bots: "Bots",
    createBot: "Gumawa ng bot",
    cryptoLab: "Trading strategy platform",
    delete: "Burahin",
    emptyBots: "Wala pang realtime bots.",
    emptyHistory: "Mag-login para i-sync ang saved setups mula sa iOS app.",
    emptyResults: "Mag-run ng backtest para makita ang chart, trades, equity, at risk details.",
    endDate: "Petsa ng pagtatapos",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Account service unavailable",
    manualPrompt: "Manual prompt",
    maxRange: "Max range",
    mode: "Mode",
    netProfit: "Net profit",
    noBacktest: "Wala pang backtest",
    noClosedTrades: "Walang closed trades para sa setup na ito.",
    periodAdjusted: "Na-adjust ang period ayon sa napiling candle timeframe",
    promptPreview: "Rule parser prompt",
    quotaHistory: "History",
    ready: "Ready",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Settings",
    signedIn: "Signed in",
    startDate: "Petsa ng simula",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Refresh",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Market", symbol: "Pair", name: "Pangalan", email: "Email", password: "Password", signUp: "Mag-sign up", createAccount: "Gumawa ng account", improve: "I-improve", improveLoginHint: "Mag-sign in para suriin ang wins/losses at gumawa ng mas magandang strategy.", improvedPrompt: "Improved prompt", applyImprove: "I-apply ang improve", aiAnalysisReady: "Handa na ang AI analysis.", aiAnalysisFailed: "Nabigo ang AI analysis.", improvedPromptApplied: "Na-apply ang improved prompt. Patakbuhin muli ang backtest para ikumpara.", loadedResultFromHistory: "Na-load ang result mula sa history.", signInCreateBots: "Mag-sign in para gumawa ng realtime bots.", loadedLatestBotResult: "Na-load ang latest bot result.", entryToExit: "Entry papuntang exit", chartVolume: "Volume", chartTrades: "Trades",
    privacyDataTitle: "Data na pinoproseso namin", privacyDataBody: "Maaari naming iproseso ang account identifiers, email, subscription status, quota usage, backtest setup, saved history, bot configuration, push notification device tokens, at technical logs.", privacyUseTitle: "Paano ginagamit ang data", privacyUseBody: "Ginagamit ang data para sa authentication, quotas, history sync, backtests, requested signal notifications, reliability improvement, at abuse prevention.", privacyThirdTitle: "Third-party services", privacyThirdBody: "Maaaring gumamit ang service ng Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance, at OpenAI o Gemini kapag naka-enable ang AI.", privacyRetentionTitle: "Retention at control", privacyRetentionBody: "Iniingatan ang backtest setups at account usage habang active ang account o kailangan para sa service operations. Puwedeng makipag-ugnayan sa support para mag-request ng deletion.", privacyRiskTitle: "Trading risk", privacyRiskBody: "Historical simulations ang backtests at hindi guarantee ng future performance. Hindi naglalagay ng real trades ang Backtest at hindi ito investment advice.",
  },
  "es-419": {
    strategyEngulfing: "Envolvente",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Barrida de liquidez",
    strategyTrendPlus: "Tendencia+",
    strategyVolBreak: "Ruptura con volumen",
    strategyMeanPlus: "Reversión+",
    strategyMTF: "Multi-temporalidad",
    setupLoadedFromHistory: "Setup cargado desde historial.",
    realtimeBotCreated: "Bot realtime creado.",
    createBotFailed: "No se pudo crear el bot.",
    botTickComplete: "Tick del bot completado.",
    tickBotFailed: "Falló el tick del bot.",
    realtimeBotDeleted: "Bot realtime eliminado.",
    deleteBotFailed: "No se pudo eliminar el bot.",
    accountCreated: "Cuenta creada.",
    emailAuthFailed: "Falló la autenticación por email.",
    chartIndicators: "Indicadores del chart",
    selectTrade: "Seleccionar operación",
    accountSyncHint: "Inicia sesión para cargar cuota, historial cloud y créditos AI.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest completado",
    backtestFailed: "Backtest fallido",
    basicBacktestHint: "Los backtests aún funcionan; la sincronización de cuenta no está disponible temporalmente.",
    applyResult: "Aplicar resultado",
    cloudHistory: "Historial cloud",
    botLoginHint: "Inicia sesión para crear y monitorear bots paper realtime.",
    bots: "Bots",
    createBot: "Crear bot",
    cryptoLab: "Laboratorio de estrategias crypto",
    delete: "Eliminar",
    emptyBots: "Aún no hay bots realtime.",
    emptyHistory: "Inicia sesión para sincronizar setups guardados desde la app iOS.",
    emptyResults: "Ejecuta un backtest para ver chart, operaciones, equity y detalle de riesgo.",
    endDate: "Fecha final",
    entry: "Entrada",
    exit: "Salida",
    firebaseNotSet: "Servicio de cuenta no disponible",
    manualPrompt: "Prompt manual",
    maxRange: "Rango máximo",
    mode: "Modo",
    netProfit: "Ganancia neta",
    noBacktest: "Sin backtest",
    noClosedTrades: "No hay operaciones cerradas para este setup.",
    periodAdjusted: "Periodo ajustado según la temporalidad seleccionada",
    promptPreview: "Prompt del parser de reglas",
    quotaHistory: "Historial",
    ready: "Listo",
    returnMetric: "Retorno",
    riskDetails: "SL / TP",
    savedSetups: "setups guardados",
    settings: "Ajustes",
    signedIn: "Sesión iniciada",
    startDate: "Fecha inicial",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "Cruce MA",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Actualizar",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Mercado", symbol: "Par", name: "Nombre", email: "Email", password: "Contraseña", signUp: "Registrarse", createAccount: "Crear cuenta", improve: "Mejorar", improveLoginHint: "Inicia sesión para analizar ganancias/pérdidas y generar una estrategia mejorada.", improvedPrompt: "Prompt mejorado", applyImprove: "Aplicar mejora", aiAnalysisReady: "Análisis de AI listo.", aiAnalysisFailed: "Falló el análisis de AI.", improvedPromptApplied: "Prompt mejorado aplicado. Ejecuta otro backtest para comparar.", loadedResultFromHistory: "Resultado cargado desde historial.", signInCreateBots: "Inicia sesión para crear bots realtime.", loadedLatestBotResult: "Último resultado del bot cargado.", entryToExit: "Entrada a salida", chartVolume: "Volumen", chartTrades: "Operaciones",
    privacyDataTitle: "Datos que procesamos", privacyDataBody: "Podemos procesar identificadores de cuenta, email, estado de suscripción, uso de cuotas, configuración de backtest, historial guardado, configuración de bots, tokens de dispositivo para push notifications y logs técnicos.", privacyUseTitle: "Cómo usamos los datos", privacyUseBody: "Usamos estos datos para autenticar, aplicar cuotas, sincronizar historial, ejecutar backtests, enviar notificaciones solicitadas, mejorar confiabilidad y prevenir abuso.", privacyThirdTitle: "Servicios de terceros", privacyThirdBody: "El servicio puede usar Google Cloud, Firebase, Cloudflare, servicios de Apple, datos públicos de Binance, yfinance y OpenAI o Gemini cuando AI está activo.", privacyRetentionTitle: "Retención y control", privacyRetentionBody: "Las configuraciones y uso de cuenta se conservan mientras la cuenta esté activa o sea necesario para operar el servicio. El usuario puede contactar soporte para solicitar eliminación.", privacyRiskTitle: "Riesgo de trading", privacyRiskBody: "Los backtests son simulaciones históricas y no garantizan resultados futuros. Backtest no ejecuta operaciones reales ni brinda asesoría financiera.",
  },
  "pt-BR": {
    strategyEngulfing: "Engolfo",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Varredura de liquidez",
    strategyTrendPlus: "Tendência+",
    strategyVolBreak: "Rompimento com volume",
    strategyMeanPlus: "Reversão+",
    strategyMTF: "Multi-timeframe",
    setupLoadedFromHistory: "Setup carregado do histórico.",
    realtimeBotCreated: "Bot realtime criado.",
    createBotFailed: "Falha ao criar bot.",
    botTickComplete: "Tick do bot concluído.",
    tickBotFailed: "Falha no tick do bot.",
    realtimeBotDeleted: "Bot realtime excluído.",
    deleteBotFailed: "Falha ao excluir bot.",
    accountCreated: "Conta criada.",
    emailAuthFailed: "Falha na autenticação por email.",
    chartIndicators: "Indicadores do gráfico",
    selectTrade: "Selecionar trade",
    accountSyncHint: "Entre para carregar cotas, histórico na nuvem e créditos de IA.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest concluído",
    backtestFailed: "Backtest falhou",
    basicBacktestHint: "Backtests continuam funcionando; a sincronização da conta está temporariamente indisponível.",
    applyResult: "Aplicar resultado",
    cloudHistory: "Histórico na nuvem",
    botLoginHint: "Entre para criar e monitorar bots paper realtime.",
    bots: "Bots",
    createBot: "Criar bot",
    cryptoLab: "Laboratório de estratégias crypto",
    delete: "Excluir",
    emptyBots: "Ainda não há bots realtime.",
    emptyHistory: "Faça login para sincronizar setups salvos do app iOS.",
    emptyResults: "Rode um backtest para ver gráfico, trades, equity e detalhes de risco.",
    endDate: "Data final",
    entry: "Entrada",
    exit: "Saída",
    firebaseNotSet: "Serviço de conta indisponível",
    manualPrompt: "Prompt manual",
    maxRange: "Intervalo máximo",
    mode: "Modo",
    netProfit: "Lucro líquido",
    noBacktest: "Nenhum backtest",
    noClosedTrades: "Nenhum trade fechado para este setup.",
    periodAdjusted: "Período ajustado ao timeframe selecionado",
    promptPreview: "Prompt do parser de regras",
    quotaHistory: "Histórico",
    ready: "Pronto",
    returnMetric: "Retorno",
    riskDetails: "SL / TP",
    savedSetups: "setups salvos",
    settings: "Configurações",
    signedIn: "Conectado",
    startDate: "Data inicial",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "Cruzamento MA",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Atualizar",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Mercado", symbol: "Par", name: "Nome", email: "Email", password: "Senha", signUp: "Cadastrar", createAccount: "Criar conta", improve: "Melhorar", improveLoginHint: "Entre para analisar ganhos/perdas e gerar uma estratégia melhorada.", improvedPrompt: "Prompt melhorado", applyImprove: "Aplicar melhoria", aiAnalysisReady: "Análise de IA pronta.", aiAnalysisFailed: "Falha na análise de IA.", improvedPromptApplied: "Prompt melhorado aplicado. Rode o backtest novamente para comparar.", loadedResultFromHistory: "Resultado carregado do histórico.", signInCreateBots: "Entre para criar bots realtime.", loadedLatestBotResult: "Resultado mais recente do bot carregado.", entryToExit: "Entrada até saída", chartVolume: "Volume", chartTrades: "Trades",
    privacyDataTitle: "Dados que processamos", privacyDataBody: "Podemos processar identificadores de conta, email, status de assinatura, uso de cotas, setup de backtest, histórico salvo, configuração de bots, tokens de dispositivo para push notifications e logs técnicos.", privacyUseTitle: "Como usamos os dados", privacyUseBody: "Usamos os dados para autenticação, cotas, sincronização de histórico, backtests, notificações solicitadas, melhoria de confiabilidade e prevenção de abuso.", privacyThirdTitle: "Serviços de terceiros", privacyThirdBody: "O serviço pode usar Google Cloud, Firebase, Cloudflare, serviços Apple, dados públicos da Binance, yfinance e OpenAI ou Gemini quando recursos de IA estão ativos.", privacyRetentionTitle: "Retenção e controle", privacyRetentionBody: "Setups e uso da conta são mantidos enquanto a conta estiver ativa ou conforme necessário para operar o serviço. Usuários podem contatar suporte para solicitar exclusão.", privacyRiskTitle: "Risco de trading", privacyRiskBody: "Backtests são simulações históricas e não garantem desempenho futuro. Backtest não executa trades reais nem fornece aconselhamento financeiro.",
  },
  ja: {
    strategyEngulfing: "包み足",
    strategyPinBar: "ピンバー",
    strategyInsideBar: "インサイドバー",
    strategySweep: "流動性スイープ",
    strategyTrendPlus: "トレンド+",
    strategyVolBreak: "出来高ブレイク",
    strategyMeanPlus: "平均回帰+",
    strategyMTF: "複数時間軸",
    setupLoadedFromHistory: "履歴から設定を読み込みました。",
    realtimeBotCreated: "Realtime botを作成しました。",
    createBotFailed: "Botの作成に失敗しました。",
    botTickComplete: "Bot tickが完了しました。",
    tickBotFailed: "Bot tickに失敗しました。",
    realtimeBotDeleted: "Realtime botを削除しました。",
    deleteBotFailed: "Botの削除に失敗しました。",
    accountCreated: "アカウントを作成しました。",
    emailAuthFailed: "メール認証に失敗しました。",
    chartIndicators: "チャート指標",
    selectTrade: "取引を選択",
    accountSyncHint: "クォータ、クラウド履歴、AIクレジットを読み込むにはサインインしてください。",
    api: "API",
    appSubtitle: "戦略テスター",
    backtestComplete: "バックテスト完了",
    backtestFailed: "バックテスト失敗",
    basicBacktestHint: "バックテストは利用できます。アカウント同期は一時的に利用できません。",
    applyResult: "結果を適用",
    cloudHistory: "クラウド履歴",
    botLoginHint: "Realtime paper botを作成・監視するにはサインインしてください。",
    bots: "Bot",
    createBot: "Botを作成",
    cryptoLab: "暗号資産戦略ラボ",
    delete: "削除",
    emptyBots: "Realtime botはまだありません。",
    emptyHistory: "iOSアプリの保存済み設定を同期するにはログインしてください。",
    emptyResults: "バックテストを実行するとチャート、取引、エクイティ、リスク詳細を表示します。",
    endDate: "終了日",
    entry: "エントリー",
    exit: "決済",
    firebaseNotSet: "アカウントサービスを利用できません",
    manualPrompt: "手動プロンプト",
    maxRange: "最大範囲",
    mode: "モード",
    netProfit: "純利益",
    noBacktest: "バックテストなし",
    noClosedTrades: "この設定のクローズ済み取引はありません。",
    periodAdjusted: "選択した時間足に合わせて期間を調整しました",
    promptPreview: "ルール解析プロンプト",
    quotaHistory: "履歴",
    ready: "準備完了",
    returnMetric: "リターン",
    riskDetails: "SL / TP",
    savedSetups: "保存済み設定",
    settings: "設定",
    signedIn: "サインイン済み",
    startDate: "開始日",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MAクロス",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "更新",
    tradeTablePnL: "損益",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "市場", symbol: "ペア", name: "名前", email: "メール", password: "パスワード", signUp: "登録", createAccount: "アカウント作成", improve: "改善", improveLoginHint: "サインインすると勝敗を分析し、改善戦略を生成できます。", improvedPrompt: "改善済みプロンプト", applyImprove: "改善を適用", aiAnalysisReady: "AI分析が完了しました。", aiAnalysisFailed: "AI分析に失敗しました。", improvedPromptApplied: "改善済みプロンプトを適用しました。比較のため再度バックテストしてください。", loadedResultFromHistory: "履歴から結果を読み込みました。", signInCreateBots: "Realtime botを作成するにはサインインしてください。", loadedLatestBotResult: "最新のbot結果を読み込みました。", entryToExit: "エントリーから決済", chartVolume: "出来高", chartTrades: "取引",
    privacyDataTitle: "処理するデータ", privacyDataBody: "アカウント識別子、メール、サブスクリプション状態、クォータ使用量、バックテスト設定、保存履歴、bot設定、プッシュ通知用デバイストークン、技術ログを処理する場合があります。", privacyUseTitle: "データの利用目的", privacyUseBody: "認証、クォータ管理、履歴同期、バックテスト、要求された通知、信頼性向上、不正利用防止に使用します。", privacyThirdTitle: "第三者サービス", privacyThirdBody: "Google Cloud、Firebase、Cloudflare、Appleサービス、Binance公開市場データ、yfinance、AI機能有効時のOpenAIまたはGeminiを利用する場合があります。", privacyRetentionTitle: "保持と管理", privacyRetentionBody: "バックテスト設定とアカウント利用情報は、アカウントが有効な間またはサービス運営に必要な期間保持されます。削除希望はサポートへ連絡できます。", privacyRiskTitle: "取引リスク", privacyRiskBody: "バックテストは過去データのシミュレーションであり将来の成果を保証しません。Backtestは実取引を行わず、投資助言を提供しません。",
  },
  ko: {
    strategyEngulfing: "장악형",
    strategyPinBar: "핀바",
    strategyInsideBar: "인사이드바",
    strategySweep: "유동성 스윕",
    strategyTrendPlus: "추세+",
    strategyVolBreak: "거래량 돌파",
    strategyMeanPlus: "평균회귀+",
    strategyMTF: "멀티 타임프레임",
    setupLoadedFromHistory: "기록에서 설정을 불러왔습니다.",
    realtimeBotCreated: "Realtime bot이 생성되었습니다.",
    createBotFailed: "Bot 생성 실패.",
    botTickComplete: "Bot tick 완료.",
    tickBotFailed: "Bot tick 실패.",
    realtimeBotDeleted: "Realtime bot이 삭제되었습니다.",
    deleteBotFailed: "Bot 삭제 실패.",
    accountCreated: "계정이 생성되었습니다.",
    emailAuthFailed: "이메일 인증 실패.",
    chartIndicators: "차트 지표",
    selectTrade: "거래 선택",
    accountSyncHint: "쿼터, 클라우드 기록, AI 크레딧을 불러오려면 로그인하세요.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "백테스트 완료",
    backtestFailed: "백테스트 실패",
    basicBacktestHint: "백테스트는 계속 실행할 수 있으며 계정 동기화는 일시적으로 사용할 수 없습니다.",
    applyResult: "결과 적용",
    cloudHistory: "클라우드 기록",
    botLoginHint: "Realtime paper bot을 만들고 모니터링하려면 로그인하세요.",
    bots: "Bot",
    createBot: "Bot 만들기",
    cryptoLab: "크립토 전략 랩",
    delete: "삭제",
    emptyBots: "아직 realtime bot이 없습니다.",
    emptyHistory: "iOS 앱의 저장된 설정을 동기화하려면 로그인하세요.",
    emptyResults: "백테스트를 실행하면 차트, 거래, 에쿼티, 리스크 세부 정보를 볼 수 있습니다.",
    endDate: "종료일",
    entry: "진입",
    exit: "청산",
    firebaseNotSet: "계정 서비스를 사용할 수 없습니다",
    manualPrompt: "수동 프롬프트",
    maxRange: "최대 범위",
    mode: "모드",
    netProfit: "순이익",
    noBacktest: "백테스트 없음",
    noClosedTrades: "이 설정에 대한 종료 거래가 없습니다.",
    periodAdjusted: "선택한 캔들 타임프레임에 맞게 기간을 조정했습니다",
    promptPreview: "규칙 파서 프롬프트",
    quotaHistory: "기록",
    ready: "준비됨",
    returnMetric: "수익률",
    riskDetails: "SL / TP",
    savedSetups: "저장된 설정",
    settings: "설정",
    signedIn: "로그인됨",
    startDate: "시작일",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA 크로스",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "새로고침",
    tradeTablePnL: "손익",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "시장", symbol: "페어", name: "이름", email: "이메일", password: "비밀번호", signUp: "가입", createAccount: "계정 만들기", improve: "개선", improveLoginHint: "로그인하면 승패를 분석하고 개선된 전략을 생성할 수 있습니다.", improvedPrompt: "개선된 프롬프트", applyImprove: "개선 적용", aiAnalysisReady: "AI 분석이 준비되었습니다.", aiAnalysisFailed: "AI 분석에 실패했습니다.", improvedPromptApplied: "개선된 프롬프트가 적용되었습니다. 비교를 위해 백테스트를 다시 실행하세요.", loadedResultFromHistory: "기록에서 결과를 불러왔습니다.", signInCreateBots: "Realtime bot을 만들려면 로그인하세요.", loadedLatestBotResult: "최신 bot 결과를 불러왔습니다.", entryToExit: "진입에서 청산", chartVolume: "거래량", chartTrades: "거래",
    privacyDataTitle: "처리하는 데이터", privacyDataBody: "계정 식별자, 이메일, 구독 상태, quota 사용량, 백테스트 설정, 저장 기록, bot 설정, push notification용 device token, 기술 로그를 처리할 수 있습니다.", privacyUseTitle: "데이터 사용 방식", privacyUseBody: "인증, quota 관리, 기록 동기화, 백테스트 실행, 요청한 신호 알림, 안정성 개선, 남용 방지에 사용합니다.", privacyThirdTitle: "제3자 서비스", privacyThirdBody: "Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance, AI 기능 활성화 시 OpenAI 또는 Gemini를 사용할 수 있습니다.", privacyRetentionTitle: "보관 및 제어", privacyRetentionBody: "백테스트 설정과 계정 사용 정보는 계정 활성 기간 또는 서비스 운영에 필요한 기간 보관됩니다. 사용자는 support에 계정 연결 데이터 삭제를 요청할 수 있습니다.", privacyRiskTitle: "거래 위험", privacyRiskBody: "백테스트는 과거 시뮬레이션이며 미래 성과를 보장하지 않습니다. Backtest는 실제 거래를 실행하지 않으며 투자 조언을 제공하지 않습니다.",
  },
  ru: {
    strategyEngulfing: "Поглощение",
    strategyPinBar: "Пин-бар",
    strategyInsideBar: "Внутренний бар",
    strategySweep: "Сбор ликвидности",
    strategyTrendPlus: "Тренд+",
    strategyVolBreak: "Пробой объёма",
    strategyMeanPlus: "Возврат+",
    strategyMTF: "Мульти-таймфрейм",
    setupLoadedFromHistory: "Настройки загружены из истории.",
    realtimeBotCreated: "Realtime-бот создан.",
    createBotFailed: "Не удалось создать бот.",
    botTickComplete: "Tick бота завершён.",
    tickBotFailed: "Tick бота не выполнен.",
    realtimeBotDeleted: "Realtime-бот удалён.",
    deleteBotFailed: "Не удалось удалить бот.",
    accountCreated: "Аккаунт создан.",
    emailAuthFailed: "Ошибка email-аутентификации.",
    chartIndicators: "Индикаторы графика",
    selectTrade: "Выбрать сделку",
    accountSyncHint: "Войдите, чтобы загрузить квоты, облачную историю и AI-кредиты.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Бэктест завершён",
    backtestFailed: "Бэктест не выполнен",
    basicBacktestHint: "Бэктесты доступны; синхронизация аккаунта временно недоступна.",
    applyResult: "Применить результат",
    cloudHistory: "Облачная история",
    botLoginHint: "Войдите, чтобы создавать и отслеживать realtime paper bots.",
    bots: "Боты",
    createBot: "Создать бот",
    cryptoLab: "Лаборатория crypto-стратегий",
    delete: "Удалить",
    emptyBots: "Realtime-ботов пока нет.",
    emptyHistory: "Войдите, чтобы синхронизировать сохранённые настройки из iOS app.",
    emptyResults: "Запустите бэктест, чтобы увидеть chart, сделки, equity и риск.",
    endDate: "Дата окончания",
    entry: "Вход",
    exit: "Выход",
    firebaseNotSet: "Сервис аккаунта недоступен",
    manualPrompt: "Ручной prompt",
    maxRange: "Макс. диапазон",
    mode: "Режим",
    netProfit: "Чистая прибыль",
    noBacktest: "Бэктеста пока нет",
    noClosedTrades: "Для этих настроек нет закрытых сделок.",
    periodAdjusted: "Период скорректирован под выбранный таймфрейм свечей",
    promptPreview: "Prompt парсера правил",
    quotaHistory: "История",
    ready: "Готово",
    returnMetric: "Доходность",
    riskDetails: "SL / TP",
    savedSetups: "сохранённых настроек",
    settings: "Настройки",
    signedIn: "Вход выполнен",
    startDate: "Дата начала",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Обновить",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Рынок", symbol: "Пара", name: "Имя", email: "Email", password: "Пароль", signUp: "Регистрация", createAccount: "Создать аккаунт", improve: "Улучшить", improveLoginHint: "Войдите, чтобы проанализировать выигрыши/убытки и создать улучшенную стратегию.", improvedPrompt: "Улучшенный prompt", applyImprove: "Применить улучшение", aiAnalysisReady: "AI-анализ готов.", aiAnalysisFailed: "AI-анализ не выполнен.", improvedPromptApplied: "Улучшенный prompt применён. Запустите backtest снова для сравнения.", loadedResultFromHistory: "Результат загружен из истории.", signInCreateBots: "Войдите, чтобы создать realtime bots.", loadedLatestBotResult: "Последний результат bot загружен.", entryToExit: "От входа до выхода", chartVolume: "Объём", chartTrades: "Сделки",
    privacyDataTitle: "Какие данные мы обрабатываем", privacyDataBody: "Мы можем обрабатывать идентификаторы аккаунта, email, статус подписки, использование квот, настройки backtest, сохранённую историю, конфигурацию bot, device tokens для push notifications и технические logs.", privacyUseTitle: "Как мы используем данные", privacyUseBody: "Данные используются для аутентификации, квот, синхронизации истории, backtests, запрошенных уведомлений, повышения надёжности и предотвращения злоупотреблений.", privacyThirdTitle: "Сторонние сервисы", privacyThirdBody: "Сервис может использовать Google Cloud, Firebase, Cloudflare, Apple services, публичные данные Binance, yfinance, а также OpenAI или Gemini при включённом AI.", privacyRetentionTitle: "Хранение и контроль", privacyRetentionBody: "Настройки backtest и использование аккаунта хранятся, пока аккаунт активен или это нужно для работы сервиса. Пользователь может обратиться в support для удаления связанных данных.", privacyRiskTitle: "Торговый риск", privacyRiskBody: "Backtests являются историческими симуляциями и не гарантируют будущий результат. Backtest не совершает реальные сделки и не даёт инвестиционных советов.",
  },
  tr: {
    strategyEngulfing: "Engulfing",
    strategyPinBar: "Pin Bar",
    strategyInsideBar: "Inside Bar",
    strategySweep: "Likidite Sweep",
    strategyTrendPlus: "Trend+",
    strategyVolBreak: "Volume Breakout",
    strategyMeanPlus: "Mean+",
    strategyMTF: "Çoklu zaman aralığı",
    setupLoadedFromHistory: "Setup geçmişten yüklendi.",
    realtimeBotCreated: "Realtime bot oluşturuldu.",
    createBotFailed: "Bot oluşturulamadı.",
    botTickComplete: "Bot tick tamamlandı.",
    tickBotFailed: "Bot tick başarısız.",
    realtimeBotDeleted: "Realtime bot silindi.",
    deleteBotFailed: "Bot silinemedi.",
    accountCreated: "Hesap oluşturuldu.",
    emailAuthFailed: "Email authentication başarısız.",
    chartIndicators: "Chart indicators",
    selectTrade: "Trade seç",
    accountSyncHint: "Quota, cloud history ve AI kredilerini yüklemek için giriş yapın.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Backtest tamamlandı",
    backtestFailed: "Backtest başarısız",
    basicBacktestHint: "Backtests çalışır; account sync geçici olarak kullanılamıyor.",
    applyResult: "Sonucu uygula",
    cloudHistory: "Cloud history",
    botLoginHint: "Realtime paper bots oluşturmak ve izlemek için giriş yapın.",
    bots: "Botlar",
    createBot: "Bot oluştur",
    cryptoLab: "Trading strategy platform",
    delete: "Sil",
    emptyBots: "Henüz realtime bot yok.",
    emptyHistory: "iOS app’ten saved setups sync etmek için login olun.",
    emptyResults: "Chart, trades, equity ve risk detayları için backtest çalıştırın.",
    endDate: "Bitiş tarihi",
    entry: "Entry",
    exit: "Exit",
    firebaseNotSet: "Hesap servisi kullanılamıyor",
    manualPrompt: "Manual prompt",
    maxRange: "Maks. aralık",
    mode: "Mod",
    netProfit: "Net profit",
    noBacktest: "Henüz backtest yok",
    noClosedTrades: "Bu setup için closed trade yok.",
    periodAdjusted: "Period selected candle timeframe’e göre ayarlandı",
    promptPreview: "Rule parser prompt",
    quotaHistory: "Geçmiş",
    ready: "Hazır",
    returnMetric: "Return",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Ayarlar",
    signedIn: "Giriş yapıldı",
    startDate: "Başlangıç tarihi",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Yenile",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Piyasa", symbol: "Parite", name: "Ad", email: "Email", password: "Şifre", signUp: "Kaydol", createAccount: "Hesap oluştur", improve: "İyileştir", improveLoginHint: "Kazanç/kayıpları analiz etmek ve daha iyi strateji üretmek için giriş yapın.", improvedPrompt: "İyileştirilmiş prompt", applyImprove: "İyileştirmeyi uygula", aiAnalysisReady: "AI analizi hazır.", aiAnalysisFailed: "AI analizi başarısız.", improvedPromptApplied: "İyileştirilmiş prompt uygulandı. Karşılaştırmak için backtest'i tekrar çalıştırın.", loadedResultFromHistory: "Sonuç geçmişten yüklendi.", signInCreateBots: "Realtime bot oluşturmak için giriş yapın.", loadedLatestBotResult: "En güncel bot sonucu yüklendi.", entryToExit: "Entry'den exit'e", chartVolume: "Hacim", chartTrades: "İşlemler",
    privacyDataTitle: "İşlediğimiz veriler", privacyDataBody: "Account identifiers, email, subscription status, quota usage, backtest setup, saved history, bot configuration, push notification device tokens ve teknik logs işleyebiliriz.", privacyUseTitle: "Verileri nasıl kullanırız", privacyUseBody: "Veriler authentication, quota enforcement, history sync, backtests, requested signal notifications, reliability improvement ve abuse prevention için kullanılır.", privacyThirdTitle: "Üçüncü taraf servisler", privacyThirdBody: "Service Google Cloud, Firebase, Cloudflare, Apple services, Binance public market data, yfinance ve AI aktifken OpenAI veya Gemini kullanabilir.", privacyRetentionTitle: "Saklama ve kontrol", privacyRetentionBody: "Backtest setups ve account usage, account active olduğu sürece veya service operations için gerekli oldukça saklanır. Users account-linked data deletion için support ile iletişime geçebilir.", privacyRiskTitle: "Trading riski", privacyRiskBody: "Backtests historical simulations'dır ve future performance garantilemez. Backtest real trades place etmez ve investment advice sunmaz.",
  },
  uk: {
    strategyEngulfing: "Поглинання",
    strategyPinBar: "Пін-бар",
    strategyInsideBar: "Внутрішній бар",
    strategySweep: "Збір ліквідності",
    strategyTrendPlus: "Тренд+",
    strategyVolBreak: "Пробій обсягу",
    strategyMeanPlus: "Повернення+",
    strategyMTF: "Мульти-таймфрейм",
    setupLoadedFromHistory: "Налаштування завантажено з історії.",
    realtimeBotCreated: "Realtime bot створено.",
    createBotFailed: "Не вдалося створити bot.",
    botTickComplete: "Bot tick завершено.",
    tickBotFailed: "Bot tick не вдався.",
    realtimeBotDeleted: "Realtime bot видалено.",
    deleteBotFailed: "Не вдалося видалити bot.",
    accountCreated: "Акаунт створено.",
    emailAuthFailed: "Email-аутентифікація не вдалася.",
    chartIndicators: "Індикатори chart",
    selectTrade: "Вибрати угоду",
    accountSyncHint: "Увійдіть, щоб завантажити квоти, cloud history та AI-кредити.",
    api: "API",
    appSubtitle: "Strategy tester",
    backtestComplete: "Бектест завершено",
    backtestFailed: "Бектест не вдався",
    basicBacktestHint: "Бектести доступні; синхронізація акаунта тимчасово недоступна.",
    applyResult: "Застосувати результат",
    cloudHistory: "Cloud history",
    botLoginHint: "Увійдіть, щоб створювати й відстежувати realtime paper bots.",
    bots: "Боти",
    createBot: "Створити бот",
    cryptoLab: "Лабораторія crypto-стратегій",
    delete: "Видалити",
    emptyBots: "Realtime bot ще немає.",
    emptyHistory: "Увійдіть, щоб sync saved setups з iOS app.",
    emptyResults: "Запустіть backtest, щоб побачити chart, угоди, equity та risk details.",
    endDate: "Дата завершення",
    entry: "Вхід",
    exit: "Вихід",
    firebaseNotSet: "Сервіс акаунта недоступний",
    manualPrompt: "Manual prompt",
    maxRange: "Макс. діапазон",
    mode: "Режим",
    netProfit: "Чистий прибуток",
    noBacktest: "Backtest ще немає",
    noClosedTrades: "Для цього setup немає закритих угод.",
    periodAdjusted: "Період скориговано під вибраний candle timeframe",
    promptPreview: "Rule parser prompt",
    quotaHistory: "Історія",
    ready: "Готово",
    returnMetric: "Дохідність",
    riskDetails: "SL / TP",
    savedSetups: "saved setups",
    settings: "Налаштування",
    signedIn: "Вхід виконано",
    startDate: "Дата початку",
    strategyBollinger: "Bollinger",
    strategyBreakout: "Breakout",
    strategyMACD: "MACD",
    strategyMACross: "MA Cross",
    strategyPullback: "Pullback",
    strategyRSI: "RSI",
    tick: "Tick",
    refresh: "Оновити",
    tradeTablePnL: "P&L",
    chartEma20: "EMA 20",
    chartEma50: "EMA 50",
    chartBollinger: "Bollinger",
    market: "Ринок", symbol: "Пара", name: "Ім'я", email: "Email", password: "Пароль", signUp: "Зареєструватися", createAccount: "Створити акаунт", improve: "Покращити", improveLoginHint: "Увійдіть, щоб проаналізувати виграші/збитки та створити кращу стратегію.", improvedPrompt: "Покращений prompt", applyImprove: "Застосувати покращення", aiAnalysisReady: "AI-аналіз готовий.", aiAnalysisFailed: "AI-аналіз не вдався.", improvedPromptApplied: "Покращений prompt застосовано. Запустіть backtest знову для порівняння.", loadedResultFromHistory: "Результат завантажено з історії.", signInCreateBots: "Увійдіть, щоб створити realtime bots.", loadedLatestBotResult: "Останній результат bot завантажено.", entryToExit: "Від входу до виходу", chartVolume: "Обсяг", chartTrades: "Угоди",
    privacyDataTitle: "Дані, які ми обробляємо", privacyDataBody: "Ми можемо обробляти ідентифікатори акаунта, email, статус підписки, використання квот, налаштування backtest, збережену історію, конфігурацію bot, device tokens для push notifications і технічні logs.", privacyUseTitle: "Як ми використовуємо дані", privacyUseBody: "Дані використовуються для автентифікації, квот, синхронізації історії, backtests, запитаних сповіщень, покращення надійності та запобігання зловживанням.", privacyThirdTitle: "Сторонні сервіси", privacyThirdBody: "Service може використовувати Google Cloud, Firebase, Cloudflare, Apple services, публічні дані Binance, yfinance, а також OpenAI або Gemini, коли AI увімкнено.", privacyRetentionTitle: "Зберігання та контроль", privacyRetentionBody: "Налаштування backtest і використання акаунта зберігаються, поки акаунт активний або це потрібно для роботи сервісу. Користувач може звернутися до support для видалення даних.", privacyRiskTitle: "Ризик торгівлі", privacyRiskBody: "Backtests є історичними симуляціями та не гарантують майбутній результат. Backtest не виконує реальні угоди й не надає інвестиційних порад.",
  },
};

const strategyNameTranslations: Record<Locale, LocaleStrings> = {
  en: { strategyVisualBuilder: "Visual Strategy Builder", strategyCustomDSL: "Custom DSL Strategy" },
  vi: { strategyVisualBuilder: "Trình dựng chiến lược trực quan", strategyCustomDSL: "Chiến lược DSL tùy chỉnh" },
  hi: { strategyVisualBuilder: "विज़ुअल रणनीति बिल्डर", strategyCustomDSL: "कस्टम DSL रणनीति" },
  ur: { strategyVisualBuilder: "بصری حکمت عملی ساز", strategyCustomDSL: "حسبِ ضرورت DSL حکمت عملی" },
  th: { strategyVisualBuilder: "ตัวสร้างกลยุทธ์แบบภาพ", strategyCustomDSL: "กลยุทธ์ DSL แบบกำหนดเอง" },
  bn: { strategyVisualBuilder: "ভিজ্যুয়াল কৌশল নির্মাতা", strategyCustomDSL: "কাস্টম DSL কৌশল" },
  id: { strategyVisualBuilder: "Penyusun strategi visual", strategyCustomDSL: "Strategi DSL khusus" },
  "pt-BR": { strategyVisualBuilder: "Construtor visual de estratégias", strategyCustomDSL: "Estratégia DSL personalizada" },
  "es-419": { strategyVisualBuilder: "Constructor visual de estrategias", strategyCustomDSL: "Estrategia DSL personalizada" },
  fil: { strategyVisualBuilder: "Visual na strategy builder", strategyCustomDSL: "Custom na DSL strategy" },
  ja: { strategyVisualBuilder: "ビジュアル戦略ビルダー", strategyCustomDSL: "カスタムDSL戦略" },
  ko: { strategyVisualBuilder: "시각적 전략 빌더", strategyCustomDSL: "사용자 지정 DSL 전략" },
  ru: { strategyVisualBuilder: "Визуальный конструктор стратегий", strategyCustomDSL: "Пользовательская DSL-стратегия" },
  tr: { strategyVisualBuilder: "Görsel strateji oluşturucu", strategyCustomDSL: "Özel DSL stratejisi" },
  uk: { strategyVisualBuilder: "Візуальний конструктор стратегій", strategyCustomDSL: "Власна DSL-стратегія" },
};

const tradeDetailTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {},
  hi: {
    selectedTrade: "चुना हुआ ट्रेड",
    side: "दिशा",
    buy: "खरीदें",
    sell: "बेचें",
    long: "लॉन्ग",
    short: "शॉर्ट",
    outcome: "परिणाम",
    winner: "जीत",
    loser: "हार",
    duration: "अवधि",
    fees: "फीस",
  },
  ur: {
    selectedTrade: "منتخب ٹریڈ",
    side: "سمت",
    buy: "خرید",
    sell: "فروخت",
    long: "لانگ",
    short: "شارٹ",
    outcome: "نتیجہ",
    winner: "جیت",
    loser: "نقصان",
    duration: "دورانیہ",
    fees: "فیس",
  },
  th: {
    selectedTrade: "คำสั่งที่เลือก",
    side: "ฝั่ง",
    buy: "ซื้อ",
    sell: "ขาย",
    long: "Long",
    short: "Short",
    outcome: "ผลลัพธ์",
    winner: "ชนะ",
    loser: "แพ้",
    duration: "ระยะเวลา",
    fees: "ค่าธรรมเนียม",
  },
  bn: {
    selectedTrade: "নির্বাচিত ট্রেড",
    side: "দিক",
    buy: "কিনুন",
    sell: "বিক্রি",
    long: "লং",
    short: "শর্ট",
    outcome: "ফলাফল",
    winner: "জয়",
    loser: "হার",
    duration: "সময়কাল",
    fees: "ফি",
  },
  id: {
    selectedTrade: "Trade dipilih",
    side: "Sisi",
    buy: "Beli",
    sell: "Jual",
    long: "Long",
    short: "Short",
    outcome: "Hasil",
    winner: "Menang",
    loser: "Kalah",
    duration: "Durasi",
    fees: "Biaya",
  },
  fil: {
    selectedTrade: "Napiling trade",
    side: "Side",
    buy: "Buy",
    sell: "Sell",
    long: "Long",
    short: "Short",
    outcome: "Resulta",
    winner: "Panalo",
    loser: "Talo",
    duration: "Tagal",
    fees: "Fees",
  },
  "es-419": {
    selectedTrade: "Operación seleccionada",
    side: "Lado",
    buy: "Comprar",
    sell: "Vender",
    long: "Long",
    short: "Short",
    outcome: "Resultado",
    winner: "Ganancia",
    loser: "Pérdida",
    duration: "Duración",
    fees: "Comisiones",
  },
  "pt-BR": {
    selectedTrade: "Trade selecionado",
    side: "Lado",
    buy: "Comprar",
    sell: "Vender",
    long: "Long",
    short: "Short",
    outcome: "Resultado",
    winner: "Ganho",
    loser: "Perda",
    duration: "Duração",
    fees: "Taxas",
  },
  ja: {
    selectedTrade: "選択中の取引",
    side: "方向",
    buy: "買い",
    sell: "売り",
    long: "ロング",
    short: "ショート",
    outcome: "結果",
    winner: "勝ち",
    loser: "負け",
    duration: "保有時間",
    fees: "手数料",
  },
  ko: {
    selectedTrade: "선택한 거래",
    side: "방향",
    buy: "매수",
    sell: "매도",
    long: "롱",
    short: "숏",
    outcome: "결과",
    winner: "승",
    loser: "패",
    duration: "기간",
    fees: "수수료",
  },
  ru: {
    selectedTrade: "Выбранная сделка",
    side: "Сторона",
    buy: "Покупка",
    sell: "Продажа",
    long: "Лонг",
    short: "Шорт",
    outcome: "Итог",
    winner: "Плюс",
    loser: "Минус",
    duration: "Длительность",
    fees: "Комиссии",
  },
  tr: {
    selectedTrade: "Seçili işlem",
    side: "Yön",
    buy: "Al",
    sell: "Sat",
    long: "Long",
    short: "Short",
    outcome: "Sonuç",
    winner: "Kazanç",
    loser: "Kayıp",
    duration: "Süre",
    fees: "Ücretler",
  },
  uk: {
    selectedTrade: "Вибрана угода",
    side: "Сторона",
    buy: "Купівля",
    sell: "Продаж",
    long: "Лонг",
    short: "Шорт",
    outcome: "Результат",
    winner: "Прибуток",
    loser: "Збиток",
    duration: "Тривалість",
    fees: "Комісії",
  },
};

const backtestSettingTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {},
  hi: {
    direction: "ट्रेड दिशा",
    both: "दोनों",
    exitMode: "Exit mode",
    tpSlOnly: "सिर्फ TP/SL",
    signalAndRisk: "Signal + risk",
    signalOnly: "सिर्फ signal",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode और login होने पर AI credits उपयोग होंगे।",
  },
  ur: {
    direction: "ٹریڈ سمت",
    both: "دونوں",
    exitMode: "Exit mode",
    tpSlOnly: "صرف TP/SL",
    signalAndRisk: "Signal + risk",
    signalOnly: "صرف signal",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode اور login پر AI credits استعمال ہوں گے۔",
  },
  th: {
    direction: "ทิศทางคำสั่ง",
    both: "ทั้งสอง",
    exitMode: "โหมดออก",
    tpSlOnly: "TP/SL เท่านั้น",
    signalAndRisk: "Signal + risk",
    signalOnly: "Signal เท่านั้น",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode และ login จะใช้ AI credits",
  },
  bn: {
    direction: "ট্রেড দিক",
    both: "দুই দিক",
    exitMode: "Exit mode",
    tpSlOnly: "শুধু TP/SL",
    signalAndRisk: "Signal + risk",
    signalOnly: "শুধু signal",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode ও login থাকলে AI credit ব্যবহার হবে।",
  },
  id: {
    direction: "Arah trade",
    both: "Keduanya",
    exitMode: "Mode exit",
    tpSlOnly: "TP/SL saja",
    signalAndRisk: "Signal + risk",
    signalOnly: "Signal saja",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode dengan login memakai AI credits.",
  },
  fil: {
    direction: "Direksyon",
    both: "Pareho",
    exitMode: "Exit mode",
    tpSlOnly: "TP/SL only",
    signalAndRisk: "Signal + risk",
    signalOnly: "Signal only",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode with login uses AI credits.",
  },
  "es-419": {
    direction: "Dirección",
    both: "Ambos",
    exitMode: "Modo de salida",
    tpSlOnly: "Solo TP/SL",
    signalAndRisk: "Señal + riesgo",
    signalOnly: "Solo señal",
    aiParser: "AI Parser",
    aiParserHint: "Manual con sesión iniciada usa créditos AI.",
  },
  "pt-BR": {
    direction: "Direção",
    both: "Ambos",
    exitMode: "Modo de saída",
    tpSlOnly: "Só TP/SL",
    signalAndRisk: "Sinal + risco",
    signalOnly: "Só sinal",
    aiParser: "AI Parser",
    aiParserHint: "Manual com login usa créditos de IA.",
  },
  ja: {
    direction: "取引方向",
    both: "両方",
    exitMode: "決済モード",
    tpSlOnly: "TP/SLのみ",
    signalAndRisk: "シグナル + リスク",
    signalOnly: "シグナルのみ",
    aiParser: "AI Parser",
    aiParserHint: "Manualかつログイン時にAIクレジットを使います。",
  },
  ko: {
    direction: "거래 방향",
    both: "둘 다",
    exitMode: "청산 모드",
    tpSlOnly: "TP/SL만",
    signalAndRisk: "신호 + 리스크",
    signalOnly: "신호만",
    aiParser: "AI Parser",
    aiParserHint: "Manual 모드에서 로그인하면 AI credits를 사용합니다.",
  },
  ru: {
    direction: "Направление",
    both: "Оба",
    exitMode: "Режим выхода",
    tpSlOnly: "Только TP/SL",
    signalAndRisk: "Сигнал + риск",
    signalOnly: "Только сигнал",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode с входом использует AI кредиты.",
  },
  tr: {
    direction: "İşlem yönü",
    both: "İkisi de",
    exitMode: "Çıkış modu",
    tpSlOnly: "Sadece TP/SL",
    signalAndRisk: "Sinyal + risk",
    signalOnly: "Sadece sinyal",
    aiParser: "AI Parser",
    aiParserHint: "Manual modda giriş yapınca AI kredisi kullanılır.",
  },
  uk: {
    direction: "Напрямок",
    both: "Обидва",
    exitMode: "Режим виходу",
    tpSlOnly: "Лише TP/SL",
    signalAndRisk: "Сигнал + ризик",
    signalOnly: "Лише сигнал",
    aiParser: "AI Parser",
    aiParserHint: "Manual mode з входом використовує AI credits.",
  },
};

const riskNoticeTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {
    riskNoticeTitle: "Cảnh báo rủi ro",
    riskNoticeSubtitle: "Backtest là công cụ nghiên cứu chiến lược, không phải lời khuyên đầu tư.",
    riskNoticeCapitalTitle: "Có thể mất vốn",
    riskNoticeCapitalBody: "Giao dịch crypto và tài sản tài chính có rủi ro cao. Bạn có thể mất một phần hoặc toàn bộ số vốn.",
    riskNoticeBacktestTitle: "Backtest không đảm bảo tương lai",
    riskNoticeBacktestBody: "Kết quả trong quá khứ, kể cả khi có lợi nhuận, không đảm bảo hiệu suất trong điều kiện thị trường thật.",
    riskNoticeAiTitle: "AI có thể sai",
    riskNoticeAiBody: "AI có thể chuyển mô tả thành quy tắc chiến lược. Kiểm tra quy tắc trước khi chạy backtest vì AI có thể hiểu sai.",
    riskNoticeBotTitle: "Bot hiện là paper/signal",
    riskNoticeBotBody: "Bot realtime hiện dùng để theo dõi tín hiệu và lịch sử mô phỏng, không đảm bảo điểm vào/thoát chính xác.",
    riskNoticeConfirmation: "Tôi hiểu các rủi ro trên và đồng ý chỉ sử dụng Backtest như công cụ nghiên cứu.",
    riskNoticeContinue: "Tôi hiểu và tiếp tục",
  },
  hi: {
    riskNoticeTitle: "जोखिम चेतावनी",
    riskNoticeSubtitle: "Backtest रणनीति शोध का उपकरण है, निवेश सलाह नहीं।",
    riskNoticeCapitalTitle: "पूंजी का नुकसान हो सकता है",
    riskNoticeCapitalBody: "क्रिप्टो और वित्तीय संपत्तियों में ट्रेडिंग उच्च जोखिम वाली है। आप अपनी कुछ या पूरी पूंजी खो सकते हैं।",
    riskNoticeBacktestTitle: "बैकटेस्ट भविष्य की गारंटी नहीं है",
    riskNoticeBacktestBody: "पिछला प्रदर्शन, लाभदायक परिणाम सहित, वास्तविक बाजार में भविष्य के परिणामों की गारंटी नहीं देता।",
    riskNoticeAiTitle: "AI गलत हो सकता है",
    riskNoticeAiBody: "AI लिखे विवरण को रणनीति के नियमों में बदल सकता है। बैकटेस्ट से पहले नियम जाँचें; उनमें गलती हो सकती है।",
    riskNoticeBotTitle: "बॉट अभी पेपर/सिग्नल मोड में हैं",
    riskNoticeBotBody: "रीयलटाइम बॉट संकेत और simulated history ट्रैक करते हैं; वे सही entry या exit की गारंटी नहीं देते।",
    riskNoticeConfirmation: "मैं इन जोखिमों को समझता/समझती हूं और Backtest को केवल शोध उपकरण के रूप में उपयोग करूंगा/करूंगी।",
    riskNoticeContinue: "मैं समझता/समझती हूं",
  },
  ur: {
    riskNoticeTitle: "خطرے کی تنبیہ",
    riskNoticeSubtitle: "Backtest حکمتِ عملی کی تحقیق کا ٹول ہے، سرمایہ کاری کا مشورہ نہیں۔",
    riskNoticeCapitalTitle: "سرمایہ ضائع ہو سکتا ہے",
    riskNoticeCapitalBody: "کرپٹو اور مالی اثاثوں کی ٹریڈنگ میں زیادہ خطرہ ہے۔ آپ اپنا کچھ یا پورا سرمایہ کھو سکتے ہیں۔",
    riskNoticeBacktestTitle: "بیک ٹیسٹ مستقبل کی ضمانت نہیں",
    riskNoticeBacktestBody: "ماضی کے نتائج، چاہے منافع بخش ہوں، حقیقی مارکیٹ میں مستقبل کی کارکردگی کی ضمانت نہیں دیتے۔",
    riskNoticeAiTitle: "AI غلط ہو سکتا ہے",
    riskNoticeAiBody: "AI تحریری وضاحت کو حکمت عملی کے قواعد میں بدل سکتا ہے۔ بیک ٹیسٹ سے پہلے قواعد دیکھیں؛ وہ غلط ہو سکتے ہیں۔",
    riskNoticeBotTitle: "بوٹس ابھی پیپر/سگنل موڈ میں ہیں",
    riskNoticeBotBody: "ریئل ٹائم بوٹس سگنلز اور simulated history ٹریک کرتے ہیں؛ درست entry یا exit کی ضمانت نہیں دیتے۔",
    riskNoticeConfirmation: "میں ان خطرات کو سمجھتا/سمجھتی ہوں اور Backtest کو صرف تحقیق کے ٹول کے طور پر استعمال کروں گا/گی۔",
    riskNoticeContinue: "میں سمجھ گیا/گئی",
  },
  th: {
    riskNoticeTitle: "คำเตือนความเสี่ยง",
    riskNoticeSubtitle: "Backtest เป็นเครื่องมือวิจัยกลยุทธ์ ไม่ใช่คำแนะนำการลงทุน",
    riskNoticeCapitalTitle: "อาจสูญเสียเงินทุน",
    riskNoticeCapitalBody: "การเทรดคริปโตและสินทรัพย์ทางการเงินมีความเสี่ยงสูง คุณอาจสูญเสียเงินบางส่วนหรือทั้งหมด",
    riskNoticeBacktestTitle: "Backtest ไม่รับประกันอนาคต",
    riskNoticeBacktestBody: "ผลลัพธ์ในอดีต แม้จะทำกำไร ไม่ได้รับประกันผลลัพธ์ในตลาดจริง",
    riskNoticeAiTitle: "AI อาจผิดพลาด",
    riskNoticeAiBody: "AI ช่วยแปลงคำอธิบายเป็นกฎกลยุทธ์ ตรวจสอบกฎก่อนทดสอบย้อนหลัง เพราะอาจแปลความผิดได้",
    riskNoticeBotTitle: "บอทยังเป็น paper/signal",
    riskNoticeBotBody: "บอท realtime ใช้ติดตามสัญญาณและประวัติจำลอง ไม่รับประกันจุดเข้าออกที่ถูกต้อง",
    riskNoticeConfirmation: "ฉันเข้าใจความเสี่ยงเหล่านี้ และจะใช้ Backtest เป็นเครื่องมือวิจัยเท่านั้น",
    riskNoticeContinue: "เข้าใจและดำเนินการต่อ",
  },
  bn: {
    riskNoticeTitle: "ঝুঁকি সতর্কতা",
    riskNoticeSubtitle: "Backtest কৌশল গবেষণার একটি টুল, বিনিয়োগ পরামর্শ নয়।",
    riskNoticeCapitalTitle: "মূলধন হারাতে পারেন",
    riskNoticeCapitalBody: "ক্রিপ্টো ও আর্থিক সম্পদ ট্রেডিং উচ্চ ঝুঁকিপূর্ণ। আপনি আংশিক বা সম্পূর্ণ মূলধন হারাতে পারেন।",
    riskNoticeBacktestTitle: "ব্যাকটেস্ট ভবিষ্যৎ নিশ্চিত করে না",
    riskNoticeBacktestBody: "অতীত ফলাফল, লাভজনক হলেও, বাস্তব বাজারে ভবিষ্যৎ পারফরম্যান্সের নিশ্চয়তা দেয় না।",
    riskNoticeAiTitle: "AI ভুল হতে পারে",
    riskNoticeAiBody: "AI লিখিত বিবরণকে কৌশলের নিয়মে রূপ দিতে পারে। ব্যাকটেস্টের আগে নিয়ম যাচাই করুন; ভুল হতে পারে।",
    riskNoticeBotTitle: "বট এখন paper/signal মোডে",
    riskNoticeBotBody: "Realtime bot সংকেত ও simulated history ট্র্যাক করে; সঠিক entry বা exit নিশ্চিত করে না।",
    riskNoticeConfirmation: "আমি এই ঝুঁকিগুলো বুঝেছি এবং Backtest শুধু গবেষণার টুল হিসেবে ব্যবহার করব।",
    riskNoticeContinue: "বুঝেছি, চালিয়ে যান",
  },
  id: {
    riskNoticeTitle: "Peringatan Risiko",
    riskNoticeSubtitle: "Backtest adalah alat riset strategi, bukan nasihat investasi.",
    riskNoticeCapitalTitle: "Modal bisa hilang",
    riskNoticeCapitalBody: "Trading crypto dan aset finansial berisiko tinggi. Anda bisa kehilangan sebagian atau seluruh modal.",
    riskNoticeBacktestTitle: "Backtest tidak menjamin masa depan",
    riskNoticeBacktestBody: "Hasil historis, termasuk yang profit, tidak menjamin performa di pasar nyata.",
    riskNoticeAiTitle: "AI bisa salah",
    riskNoticeAiBody: "AI dapat mengubah deskripsi teks menjadi aturan strategi. Periksa aturan sebelum backtest karena hasilnya bisa salah.",
    riskNoticeBotTitle: "Bot masih paper/signal",
    riskNoticeBotBody: "Bot realtime melacak sinyal dan riwayat simulasi; tidak menjamin entry atau exit yang tepat.",
    riskNoticeConfirmation: "Saya memahami risiko ini dan setuju menggunakan Backtest hanya sebagai alat riset.",
    riskNoticeContinue: "Saya mengerti",
  },
  fil: {
    riskNoticeTitle: "Babala sa Risk",
    riskNoticeSubtitle: "Ang Backtest ay tool para sa strategy research, hindi financial advice.",
    riskNoticeCapitalTitle: "Puwedeng mawala ang capital",
    riskNoticeCapitalBody: "Mataas ang risk ng crypto at financial asset trading. Puwede kang mawalan ng bahagi o buong capital.",
    riskNoticeBacktestTitle: "Hindi guarantee ang backtest",
    riskNoticeBacktestBody: "Hindi ginagarantiya ng historical results, kahit profitable, ang future performance sa real market.",
    riskNoticeAiTitle: "Puwedeng magkamali ang AI",
    riskNoticeAiBody: "Maaaring gawing mga tuntunin ng diskarte ng AI ang nakasulat na paglalarawan. Suriin ang mga tuntunin bago mag-backtest dahil maaaring mali ang mga ito.",
    riskNoticeBotTitle: "Paper/signal tools ang bots",
    riskNoticeBotBody: "Realtime bots track signals at simulated history; hindi nito ginagarantiya ang tamang entry o exit.",
    riskNoticeConfirmation: "Nauunawaan ko ang risks at gagamitin ko ang Backtest bilang research tool lamang.",
    riskNoticeContinue: "Naiintindihan ko",
  },
  "es-419": {
    riskNoticeTitle: "Aviso de Riesgo",
    riskNoticeSubtitle: "Backtest es una herramienta de investigación de estrategias, no asesoramiento financiero.",
    riskNoticeCapitalTitle: "Puedes perder capital",
    riskNoticeCapitalBody: "Operar crypto y activos financieros tiene alto riesgo. Puedes perder parte o todo tu capital.",
    riskNoticeBacktestTitle: "El backtest no garantiza el futuro",
    riskNoticeBacktestBody: "Los resultados históricos, incluso rentables, no garantizan rendimiento en el mercado real.",
    riskNoticeAiTitle: "La IA puede equivocarse",
    riskNoticeAiBody: "La IA puede convertir una descripción en reglas de estrategia. Revisa las reglas antes de ejecutar el backtest: pueden contener errores.",
    riskNoticeBotTitle: "Los bots están en modo paper/señal",
    riskNoticeBotBody: "Los bots realtime siguen señales e historial simulado; no garantizan entradas o salidas correctas.",
    riskNoticeConfirmation: "Entiendo estos riesgos y acepto usar Backtest solo como herramienta de investigación.",
    riskNoticeContinue: "Entiendo y continuar",
  },
  "pt-BR": {
    riskNoticeTitle: "Aviso de Risco",
    riskNoticeSubtitle: "Backtest é uma ferramenta de pesquisa de estratégias, não aconselhamento financeiro.",
    riskNoticeCapitalTitle: "Você pode perder capital",
    riskNoticeCapitalBody: "Trading de cripto e ativos financeiros tem alto risco. Você pode perder parte ou todo o capital.",
    riskNoticeBacktestTitle: "Backtest não garante o futuro",
    riskNoticeBacktestBody: "Resultados passados, mesmo lucrativos, não garantem desempenho em mercado real.",
    riskNoticeAiTitle: "A IA pode errar",
    riskNoticeAiBody: "A IA pode transformar uma descrição em regras de estratégia. Confira as regras antes do backtest, pois podem conter erros.",
    riskNoticeBotTitle: "Bots estão em modo paper/sinal",
    riskNoticeBotBody: "Bots em tempo real acompanham sinais e histórico simulado; não garantem entradas ou saídas corretas.",
    riskNoticeConfirmation: "Entendo estes riscos e concordo em usar Backtest apenas como ferramenta de pesquisa.",
    riskNoticeContinue: "Entendo e continuar",
  },
  ja: {
    riskNoticeTitle: "リスク通知",
    riskNoticeSubtitle: "Backtestは戦略研究ツールであり、投資助言ではありません。",
    riskNoticeCapitalTitle: "資金を失う可能性があります",
    riskNoticeCapitalBody: "暗号資産や金融商品の取引には高いリスクがあり、資金の一部または全部を失う可能性があります。",
    riskNoticeBacktestTitle: "バックテストは将来を保証しません",
    riskNoticeBacktestBody: "利益が出た過去結果であっても、実際の市場での将来成績を保証しません。",
    riskNoticeAiTitle: "AIは誤る可能性があります",
    riskNoticeAiBody: "AIは文章を戦略ルールに変換できます。誤りがある場合があるため、バックテスト前にルールを確認してください。",
    riskNoticeBotTitle: "Botはpaper/signalツールです",
    riskNoticeBotBody: "Realtime botはシグナルとシミュレーション履歴を追跡しますが、正確なentryやexitを保証しません。",
    riskNoticeConfirmation: "これらのリスクを理解し、Backtestを研究ツールとしてのみ使用することに同意します。",
    riskNoticeContinue: "理解して続行",
  },
  ko: {
    riskNoticeTitle: "위험 고지",
    riskNoticeSubtitle: "Backtest는 전략 연구 도구이며 투자 조언이 아닙니다.",
    riskNoticeCapitalTitle: "자본 손실 가능성",
    riskNoticeCapitalBody: "암호화폐와 금융자산 거래는 고위험이며 자본의 일부 또는 전부를 잃을 수 있습니다.",
    riskNoticeBacktestTitle: "백테스트는 미래를 보장하지 않습니다",
    riskNoticeBacktestBody: "수익이 난 과거 결과도 실제 시장의 미래 성과를 보장하지 않습니다.",
    riskNoticeAiTitle: "AI는 틀릴 수 있습니다",
    riskNoticeAiBody: "AI는 설명문을 전략 규칙으로 변환할 수 있습니다. 오류가 있을 수 있으므로 백테스트 전에 규칙을 확인하세요.",
    riskNoticeBotTitle: "Bot은 paper/signal 도구입니다",
    riskNoticeBotBody: "Realtime bot은 신호와 시뮬레이션 기록을 추적하지만 정확한 entry 또는 exit를 보장하지 않습니다.",
    riskNoticeConfirmation: "이 위험을 이해하며 Backtest를 연구 도구로만 사용하는 데 동의합니다.",
    riskNoticeContinue: "이해하고 계속",
  },
  ru: {
    riskNoticeTitle: "Предупреждение о риске",
    riskNoticeSubtitle: "Backtest — инструмент исследования стратегий, а не инвестиционный совет.",
    riskNoticeCapitalTitle: "Вы можете потерять капитал",
    riskNoticeCapitalBody: "Торговля крипто и финансовыми активами связана с высоким риском. Вы можете потерять часть или весь капитал.",
    riskNoticeBacktestTitle: "Бэктест не гарантирует будущее",
    riskNoticeBacktestBody: "Исторические результаты, даже прибыльные, не гарантируют результат в реальном рынке.",
    riskNoticeAiTitle: "AI может ошибаться",
    riskNoticeAiBody: "ИИ может преобразовать текстовое описание в правила стратегии. Проверьте правила перед бэктестом: в них возможны ошибки.",
    riskNoticeBotTitle: "Боты работают как paper/signal",
    riskNoticeBotBody: "Realtime bots отслеживают сигналы и симулированную историю; они не гарантируют точные entry или exit.",
    riskNoticeConfirmation: "Я понимаю эти риски и согласен использовать Backtest только как исследовательский инструмент.",
    riskNoticeContinue: "Понимаю и продолжить",
  },
  tr: {
    riskNoticeTitle: "Risk Uyarısı",
    riskNoticeSubtitle: "Backtest bir strateji araştırma aracıdır, yatırım tavsiyesi değildir.",
    riskNoticeCapitalTitle: "Sermaye kaybedebilirsiniz",
    riskNoticeCapitalBody: "Crypto ve finansal varlık trading'i yüksek risklidir. Sermayenizin bir kısmını veya tamamını kaybedebilirsiniz.",
    riskNoticeBacktestTitle: "Backtest geleceği garanti etmez",
    riskNoticeBacktestBody: "Karlı geçmiş sonuçlar bile gerçek piyasada gelecekteki performansı garanti etmez.",
    riskNoticeAiTitle: "AI hata yapabilir",
    riskNoticeAiBody: "AI metin açıklamasını strateji kurallarına dönüştürebilir. Hata içerebileceğinden backtest öncesinde kuralları kontrol edin.",
    riskNoticeBotTitle: "Botlar paper/signal aracıdır",
    riskNoticeBotBody: "Realtime botlar sinyalleri ve simüle geçmişi izler; doğru entry veya exit garantisi vermez.",
    riskNoticeConfirmation: "Bu riskleri anlıyorum ve Backtest'i yalnızca araştırma aracı olarak kullanmayı kabul ediyorum.",
    riskNoticeContinue: "Anladım ve devam et",
  },
  uk: {
    riskNoticeTitle: "Попередження про ризик",
    riskNoticeSubtitle: "Backtest — інструмент дослідження стратегій, а не інвестиційна порада.",
    riskNoticeCapitalTitle: "Ви можете втратити капітал",
    riskNoticeCapitalBody: "Торгівля crypto та фінансовими активами має високий ризик. Ви можете втратити частину або весь капітал.",
    riskNoticeBacktestTitle: "Бектест не гарантує майбутнє",
    riskNoticeBacktestBody: "Історичні результати, навіть прибуткові, не гарантують результат у реальному ринку.",
    riskNoticeAiTitle: "AI може помилятися",
    riskNoticeAiBody: "ШІ може перетворити текстовий опис на правила стратегії. Перевірте правила перед бектестом: у них можливі помилки.",
    riskNoticeBotTitle: "Боти є paper/signal інструментами",
    riskNoticeBotBody: "Realtime bots відстежують сигнали та симульовану історію; вони не гарантують точні entry або exit.",
    riskNoticeConfirmation: "Я розумію ці ризики й погоджуюся використовувати Backtest лише як дослідницький інструмент.",
    riskNoticeContinue: "Розумію і продовжити",
  },
};

const growthTranslations: Record<Locale, LocaleStrings> = {
  en: {},
  vi: {},
  hi: {
    loadedPresetFromGuide: "Guide से setup लोड हुआ",
    runFreeBacktest: "मुफ्त backtest चलाएं",
    resultEmptyStep1: "Market, timeframe और strategy चुनें।",
    resultEmptyStep2: "Fees, slippage, stop loss और take profit के साथ backtest चलाएं।",
    resultEmptyStep3: "चार्ट, लागत, गिरावट और ट्रेड देखें। अगले टेस्ट से तुलना के लिए परिणाम सहेजें।",
    aiImproveCta: "इस strategy का विश्लेषण और सुधार करें",
    aiImproveHint: "AI win rate, stop-loss behavior, exits और trade clusters पढ़कर retest के लिए सुरक्षित prompt सुझाता है।",
    trialPromptBody: "सशुल्क प्लान लंबी टेस्ट अवधि, अधिक सहेजा हुआ इतिहास और अधिक पेपर सिग्नल बॉट देते हैं।",
    currentPlanCta: "आपका current plan range, AI credits, saved history और paper bot limits तय करता है।",
  },
  ur: {
    loadedPresetFromGuide: "Guide سے setup لوڈ ہوگیا",
    runFreeBacktest: "مفت backtest چلائیں",
    resultEmptyStep1: "Market، timeframe اور strategy منتخب کریں۔",
    resultEmptyStep2: "Fees، slippage، stop loss اور take profit کے ساتھ backtest چلائیں۔",
    resultEmptyStep3: "چارٹ، اخراجات، سرمایہ میں کمی اور سودے دیکھیں۔ اگلے ٹیسٹ سے موازنے کے لیے نتیجہ محفوظ کریں۔",
    aiImproveCta: "اس strategy کا تجزیہ اور بہتری کریں",
    aiImproveHint: "AI win rate، stop-loss behavior، exits اور trade clusters پڑھ کر retest کے لیے محفوظ prompt تجویز کرتا ہے۔",
    trialPromptBody: "بامعاوضہ پلان زیادہ طویل ٹیسٹ، زیادہ محفوظ ہسٹری اور زیادہ پیپر سگنل بوٹس دیتے ہیں۔",
    currentPlanCta: "آپ کا current plan range، AI credits، saved history اور paper bot limits طے کرتا ہے۔",
  },
  th: {
    loadedPresetFromGuide: "โหลด setup จากคู่มือแล้ว",
    runFreeBacktest: "รัน backtest ฟรี",
    resultEmptyStep1: "เลือกตลาด กรอบเวลา และกลยุทธ์",
    resultEmptyStep2: "รัน backtest พร้อมค่าธรรมเนียม slippage stop loss และ take profit",
    resultEmptyStep3: "ตรวจกราฟ ต้นทุน การลดลงของเงินทุน และแต่ละเทรด บันทึกผลเพื่อเปรียบเทียบกับการทดสอบครั้งถัดไป",
    aiImproveCta: "วิเคราะห์และปรับปรุงกลยุทธ์นี้",
    aiImproveHint: "AI ตรวจ win rate พฤติกรรม stop-loss จุดออก และกลุ่มคำสั่ง แล้วเสนอ prompt ที่ปลอดภัยขึ้นเพื่อทดสอบใหม่",
    trialPromptBody: "แพ็กเกจแบบชำระเงินให้ช่วงทดสอบยาวขึ้น ประวัติที่บันทึกมากขึ้น และบอตสัญญาณจำลองมากขึ้น",
    currentPlanCta: "แพ็กเกจปัจจุบันกำหนดช่วงทดสอบ เครดิต AI ประวัติที่บันทึก และขีดจำกัด paper bot",
  },
  bn: {
    loadedPresetFromGuide: "গাইড থেকে setup লোড হয়েছে",
    runFreeBacktest: "ফ্রি backtest চালান",
    resultEmptyStep1: "Market, timeframe ও strategy নির্বাচন করুন।",
    resultEmptyStep2: "Fees, slippage, stop loss ও take profit সহ backtest চালান।",
    resultEmptyStep3: "চার্ট, খরচ, মূলধনের পতন ও ট্রেড দেখুন। পরের পরীক্ষার সঙ্গে তুলনার জন্য ফল সংরক্ষণ করুন।",
    aiImproveCta: "এই strategy বিশ্লেষণ ও উন্নত করুন",
    aiImproveHint: "AI win rate, stop-loss behavior, exits ও trade clusters পড়ে retest করার জন্য নিরাপদ prompt সাজেস্ট করে।",
    trialPromptBody: "পেইড প্ল্যানে দীর্ঘ সময়ের পরীক্ষা, বেশি সংরক্ষিত ইতিহাস ও বেশি পেপার সিগন্যাল বট পাওয়া যায়।",
    currentPlanCta: "আপনার current plan range, AI credits, saved history ও paper bot limits নিয়ন্ত্রণ করে।",
  },
  id: {
    loadedPresetFromGuide: "Setup dimuat dari panduan",
    runFreeBacktest: "Jalankan backtest gratis",
    resultEmptyStep1: "Pilih market, timeframe, dan strategi.",
    resultEmptyStep2: "Jalankan backtest dengan biaya, slippage, stop loss, dan take profit.",
    resultEmptyStep3: "Periksa grafik, biaya, penurunan modal, dan transaksi. Simpan hasil untuk membandingkan pengujian berikutnya.",
    aiImproveCta: "Analisis dan perbaiki strategi ini",
    aiImproveHint: "AI meninjau win rate, perilaku stop-loss, exit, dan cluster trade, lalu menyarankan prompt yang lebih aman untuk dites ulang.",
    trialPromptBody: "Paket berbayar menyediakan periode uji lebih panjang, lebih banyak riwayat tersimpan, dan bot sinyal simulasi.",
    currentPlanCta: "Plan saat ini mengatur range, kredit AI, riwayat tersimpan, dan batas paper bot.",
  },
  fil: {
    loadedPresetFromGuide: "Na-load ang setup mula sa guide",
    runFreeBacktest: "Mag-run ng libreng backtest",
    resultEmptyStep1: "Pumili ng market, timeframe, at strategy.",
    resultEmptyStep2: "Patakbuhin ang backtest kasama ang fees, slippage, stop loss, at take profit.",
    resultEmptyStep3: "Suriin ang chart, gastos, pagbaba ng puhunan, at mga trade. I-save ang resulta para ihambing sa susunod na pagsusuri.",
    aiImproveCta: "Suriin at pagandahin ang strategy na ito",
    aiImproveHint: "Binabasa ng AI ang win rate, stop-loss behavior, exits, at trade clusters, tapos nagmumungkahi ng mas ligtas na prompt para i-retest.",
    trialPromptBody: "May mas mahabang panahon ng pagsusuri, mas maraming naka-save na kasaysayan, at paper signal bot ang mga bayad na plan.",
    currentPlanCta: "Ang current plan ang nagtatakda ng range, AI credits, saved history, at paper bot limits.",
  },
  "es-419": {
    loadedPresetFromGuide: "Configuración cargada desde la guía",
    runFreeBacktest: "Ejecutar backtest gratis",
    resultEmptyStep1: "Elige mercado, temporalidad y estrategia.",
    resultEmptyStep2: "Ejecuta el backtest con comisiones, slippage, stop loss y take profit.",
    resultEmptyStep3: "Revisa el gráfico, los costos, la caída del capital y las operaciones. Guarda el resultado para comparar la siguiente prueba.",
    aiImproveCta: "Analizar y mejorar esta estrategia",
    aiImproveHint: "La IA revisa win rate, stop-loss, salidas y grupos de operaciones, y sugiere un prompt más seguro para volver a probar.",
    trialPromptBody: "Los planes de pago ofrecen periodos de prueba más largos, más historial guardado y más bots de señales simuladas.",
    currentPlanCta: "Tu plan actual controla rango, créditos de IA, historial guardado y límites de bots paper.",
  },
  "pt-BR": {
    loadedPresetFromGuide: "Setup carregado do guia",
    runFreeBacktest: "Rodar backtest grátis",
    resultEmptyStep1: "Escolha mercado, tempo gráfico e estratégia.",
    resultEmptyStep2: "Rode o backtest com taxas, slippage, stop loss e take profit.",
    resultEmptyStep3: "Confira o gráfico, os custos, a queda do capital e as operações. Salve o resultado para comparar o próximo teste.",
    aiImproveCta: "Analisar e melhorar esta estratégia",
    aiImproveHint: "A IA analisa win rate, comportamento do stop-loss, saídas e clusters de trades, e sugere um prompt mais seguro para retestar.",
    trialPromptBody: "Os planos pagos oferecem períodos de teste mais longos, mais histórico salvo e mais bots de sinais simulados.",
    currentPlanCta: "Seu plano atual controla período, créditos de IA, histórico salvo e limites de bots paper.",
  },
  ja: {
    loadedPresetFromGuide: "ガイドから設定を読み込みました",
    runFreeBacktest: "無料バックテストを実行",
    resultEmptyStep1: "市場、時間足、戦略を選択します。",
    resultEmptyStep2: "手数料、スリッページ、損切り、利確を含めてバックテストします。",
    resultEmptyStep3: "チャート、コスト、ドローダウン、取引を確認し、次の検証と比較できるよう結果を保存しましょう。",
    aiImproveCta: "この戦略を分析して改善",
    aiImproveHint: "AIが勝率、損切り傾向、決済、取引クラスターを確認し、再テスト用のより安全なプロンプトを提案します。",
    trialPromptBody: "有料プランでは検証期間、保存履歴、ペーパーシグナルボットの上限が増えます。",
    currentPlanCta: "現在のプランが期間、AIクレジット、保存履歴、paper bot上限を決めます。",
  },
  ko: {
    loadedPresetFromGuide: "가이드에서 설정을 불러왔습니다",
    runFreeBacktest: "무료 백테스트 실행",
    resultEmptyStep1: "시장, 타임프레임, 전략을 선택하세요.",
    resultEmptyStep2: "수수료, 슬리피지, 손절, 익절을 포함해 백테스트하세요.",
    resultEmptyStep3: "차트, 비용, 자본 감소폭, 거래를 확인하세요. 다음 테스트와 비교할 수 있도록 결과를 저장하세요.",
    aiImproveCta: "이 전략 분석 및 개선",
    aiImproveHint: "AI가 승률, 손절 행동, 청산, 거래 클러스터를 검토하고 재테스트용으로 더 안전한 프롬프트를 제안합니다.",
    trialPromptBody: "유료 플랜은 더 긴 테스트 기간, 더 많은 저장 기록과 모의 신호 봇을 제공합니다.",
    currentPlanCta: "현재 플랜이 범위, AI 크레딧, 저장 기록, paper bot 한도를 결정합니다.",
  },
  ru: {
    loadedPresetFromGuide: "Настройки загружены из гайда",
    runFreeBacktest: "Запустить бесплатный бэктест",
    resultEmptyStep1: "Выберите рынок, таймфрейм и стратегию.",
    resultEmptyStep2: "Запустите бэктест с комиссиями, проскальзыванием, стоп-лоссом и тейк-профитом.",
    resultEmptyStep3: "Изучите график, издержки, просадку и сделки. Сохраните результат для сравнения со следующим тестом.",
    aiImproveCta: "Проанализировать и улучшить стратегию",
    aiImproveHint: "AI оценивает win rate, стоп-лоссы, выходы и кластеры сделок, затем предлагает более безопасный prompt для ретеста.",
    trialPromptBody: "Платные планы дают более длинные периоды тестирования, больше сохранённой истории и ботов учебных сигналов.",
    currentPlanCta: "Текущий план определяет период, AI credits, сохраненную историю и лимиты paper bots.",
  },
  tr: {
    loadedPresetFromGuide: "Kurulum rehberden yüklendi",
    runFreeBacktest: "Ücretsiz backtest çalıştır",
    resultEmptyStep1: "Market, zaman aralığı ve strateji seçin.",
    resultEmptyStep2: "Ücret, slipaj, stop loss ve take profit ile backtest çalıştırın.",
    resultEmptyStep3: "Grafiği, maliyetleri, sermaye düşüşünü ve işlemleri inceleyin. Sonraki testle karşılaştırmak için sonucu kaydedin.",
    aiImproveCta: "Bu stratejiyi analiz et ve iyileştir",
    aiImproveHint: "AI win rate, stop-loss davranışı, çıkışlar ve işlem kümelerini inceler, retest için daha güvenli prompt önerir.",
    trialPromptBody: "Ücretli planlar daha uzun test aralıkları, daha fazla kayıtlı geçmiş ve simülasyon sinyal botu sunar.",
    currentPlanCta: "Mevcut planınız aralık, AI kredileri, kayıtlı geçmiş ve paper bot limitlerini belirler.",
  },
  uk: {
    loadedPresetFromGuide: "Налаштування завантажено з гайда",
    runFreeBacktest: "Запустити безкоштовний бектест",
    resultEmptyStep1: "Оберіть ринок, таймфрейм і стратегію.",
    resultEmptyStep2: "Запустіть бектест із комісіями, прослизанням, стоп-лосом і тейк-профітом.",
    resultEmptyStep3: "Перегляньте графік, витрати, просідання та угоди. Збережіть результат для порівняння з наступним тестом.",
    aiImproveCta: "Проаналізувати й покращити стратегію",
    aiImproveHint: "AI оцінює win rate, stop-loss, виходи й кластери угод, потім пропонує безпечніший prompt для ретесту.",
    trialPromptBody: "Платні плани дають довші періоди тестування, більше збереженої історії та ботів навчальних сигналів.",
    currentPlanCta: "Поточний план визначає період, AI credits, збережену історію та ліміти paper bots.",
  },
};

export function resolveLocale(language: string): Locale {
  const normalized = language.toLowerCase();
  if (normalized.startsWith("pt")) return "pt-BR";
  if (normalized.startsWith("es")) return "es-419";
  const exact = locales.find((locale) => locale.toLowerCase() === normalized);
  if (exact) return exact;
  const base = locales.find((locale) => normalized.startsWith(locale.toLowerCase().split("-")[0]));
  return base || "en";
}

function localizedTranslation(locale: Locale, key: TranslationKey): string | undefined {
  return (
    (marketDataErrorTranslations[locale] as Partial<Record<TranslationKey, string>>)[key] ||
    (experienceModeTranslations[locale] as Partial<Record<TranslationKey, string>>)[key] ||
    (handoffTranslations[locale] as Partial<Record<TranslationKey, string>>)[key] ||
    (privacyOverlayTranslations[locale] as Partial<Record<TranslationKey, string>>)[key] ||
    derivativesTranslations[locale][key] ||
    naturalnessTranslations[locale]?.[key] ||
    completionTranslations[locale]?.[key] ||
    researchQualityTranslations[locale][key] ||
    validationTranslations[locale]?.[key] ||
    translations[locale][key] ||
    strategyNameTranslations[locale]?.[key] ||
    releaseTranslations[locale]?.[key] ||
    tradeDetailTranslations[locale]?.[key] ||
    backtestSettingTranslations[locale]?.[key] ||
    riskNoticeTranslations[locale]?.[key] ||
    growthTranslations[locale]?.[key] ||
    pricingTranslations[locale]?.[key]
  );
}

export function createTranslator(locale: Locale) {
  return (key: TranslationKey): string => localizedTranslation(locale, key) || en[key] || key;
}

export const translationKeys = Object.freeze(Object.keys(en) as TranslationKey[]);

export function missingTranslationKeys(locale: Locale): TranslationKey[] {
  if (locale === "en") return [];
  return translationKeys.filter((key) => !localizedTranslation(locale, key));
}

export function isRTL(locale: Locale): boolean {
  return locale === "ur";
}
